import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Bookmark,
  Brain,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Flame,
  Focus,
  Keyboard,
  Layers,
  LayoutGrid,
  Gauge,
  Lightbulb,
  Link as LinkIcon,
  Mail,
  Info,
  KeyRound,
  Maximize2,
  Minimize2,
  Bell,
  EyeOff,
  Heart,
  Home,
  Newspaper,
  Menu as MenuIcon,
  ImageDown,
  Download,
  MessageCircle,
  Moon,
  Pause,
  PieChart,
  Coffee,
  Play,
  Quote,
  RotateCw,
  Scale,
  Search,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Snowflake,
  Sparkles,
  Star,
  Target,
  Sun,
  ThumbsDown,
  ThumbsUp,
  Type,
  User,
  Volume2,
  X,
  Zap,
  Compass,
  ArrowRight,
  FileText,
  UserRound
} from "lucide-react";
import { Pillars, Sections } from "./Masthead";
import { Favorites } from "./Favorites";
import { PhoneDock } from "./PhoneDock";
import { LensMenu } from "./LensMenu";
import { readFavs } from "./more/favs";
// The phone menu (the home's drawer, every tool and page), loaded only when opened.
const ReaderDrawer = lazy(() => import("./ReaderDrawer"));
import cardsData from "./data/cards.json";
import {
  SESSION_SIZE,
  nextRoundStartIndex,
  personalizeFeed,
  readerWeights,
  resumeIndex,
  roundEndForStart,
  roundSizeForStart,
  roundStartForIndex,
  seedViewedForResume
} from "./session";
import type { GameResult, GameToday, InSnipCard, TrustLayer } from "./types";
import { type EngagementType, type PerksState, type StanceState, accountUid, anonId, fetchServerWeights, fetchStance, fetchTrending, gameToday as fetchGameToday, getPerks, isSignedIn, pingStreak, referralId, sendEvent, sendFeedback, sendStance, subscribeEmail, withReferral } from "./engagement";
import { httpHref } from "./safeHref";
import { EmailAuthError, completeRedirectSignIn, deleteAccount, onAuthChange, requestEmailCode, signInWithApple, signInWithGoogle, signOutUser, verifyEmailCode } from "./auth";
import { emailSyntaxError } from "./emailValidation";
import { canPromptInstall, isIOS, isStandalone, onInstallChange, promptInstall } from "./pwaInstall";
import { PLAY_URL, APP_STORE_URL, GET_PATH, storeHref, webPlatform, appPromoDismissed, dismissAppPromo, shouldShowAppWall } from "./appPromo";
import AppWall from "./AppWall";
import { GET_URL, QR_GET_DATAURI } from "./qrGet";
import { type DayWeather, getBriefWeather } from "./weather";
import { enableWebPush, onForegroundPush, pushEnabledLocally, pushSupported, refreshWebPushToken } from "./push";
import FeatureTour from "./FeatureTour";
import { TOUR_VERSION, shouldShowTour } from "./tourSteps";
import PhoneDoor, { AppNudge, RoundPrompt } from "./PhoneDoor";
import { arrivedOnAStory, isPhoneWeb, roundPromptReady, setAppChoice, shouldNudgeForApp, shouldShowPhoneDoor } from "./phone";
import DeepDive from "./DeepDivePanel";
import { deepDiveFor, hasDeepDiveFor, needsDiveHydration } from "./deepDive";
import { displayFacts, platePointers } from "./plateFacts";
import { bodyParagraphs, frontPoints } from "./frontPoints";
import DisplaySheet from "./DisplaySheet";
import { applyDisplay, type Display, DISPLAY_DEFAULTS, FACES, loadDisplay, roundTimeLeft, saveDisplay } from "./display";
import BottomNav, { type NavTab } from "./BottomNav";
import BirthdayPanel from "./BirthdayPanel";
import AudioBriefing from "./AudioBriefing";
import WeekRecap from "./WeekRecap";
import { recordWeeklyRead, topTopics, weeklyReadCount } from "./weekStats";
import { isBirthdayToday, readDob, type Dob } from "./birthday";
import { GOLD, loadDominantColor, type RGB } from "./cardColor";
// The one slug-to-masthead map, shared with the Pages Functions (see _mastheads.js).
import { prettySourceName, sourceDisplayName } from "../functions/_mastheads.js";
import { minutesWord, readMinutes } from "../functions/_minutes.js";
import { bionicNodes } from "./bionic";
import { keepCompounds, markFigures } from "./typeset";
import { AppIcon } from "./AppIcon";
import { displayCaption } from "./caption";
import { keyPoints } from "./keyPoints";
import { isGoodNews, isFlaggedGoodNews, shelfLabel } from "./goodNews";
import { type SourceTier, tierForDomain, tierHasChip, tierLabel } from "./sourceTier";
import { isSharedLink, parseCardLink, parseNextChain, parseReferrer, parseSeenCard, parseVia } from "./cardLink";
import { bumpTopic, topicState, tuningWeights } from "./feedTuning";
import { landingCard, lensOrder } from "./lens";
import { imageLqip, imageTintHue, resolveImageUrl } from "./assets";
import { computeRecap } from "./recap";
import { computeSignalScore, signalBand, explainSignalScore } from "./signal";
import { claimStatus, lastCheckedLabel, mustKnowOf, receiptOf } from "./receipt";
import { RecallModal } from "./Recall";
import { LibraryModal } from "./Library";
import { ProModal } from "./Pro";
import { type AccountSummary, type FeedMode, type Insights, type TopicPrefs, askCard, fetchAccount, fetchBriefs, fetchCard, fetchFreshCount, fetchInsights, fetchGoodNews, fetchQuiz, fetchReferrals, fetchRelated, linkAccount, markSeen, nextSession, refreshSession, saveProfile, searchCards, sessionApiEnabled, unlinkAccount, updatePosition, fetchLatestTeasers, fetchStoryChain, mintDayShare, type LatestTeaser, type StoryChainStep } from "./sessionApi";
import { EXIT_BOUNCE, exitDurationSec, planGlide, springFor, supportsLinearEasing } from "./spring";
import { type DailyInsight, ROUND_DONE_KEY, hasFinishedARound, pickInsightOfDay } from "./insights";
import { QuizModal } from "./Quiz";
import { SignalOrNoiseCard, SignalOrNoiseGame } from "./SignalOrNoise";
import { shareCardImage, shareDeepDiveImage, shareFrontPageImage, shareQuoteImage, shareRecapImage } from "./shareImage";
import { fetchEdition, type EditionPaper } from "./sessionApi";
import { paperStats } from "./v3/editionTime";
import ShareSheet from "./ShareSheet";
import { vt } from "./viewTransition";
import { useAnimatedNumber } from "./useAnimatedNumber";
import { hapticTap, isNativeApp, syncStatusBar } from "./native";
import { INVITE_AFTER_CARDS, canSkipFirstDoor, isMember, showsFirstDoor } from "./memberGate";
import { tipForHour } from "./tips";
import { hookForCard } from "./readingHooks";
import { computeReferralLadder } from "./referralTier";
// v3 "Proof and Play": receipts, follow-ups, diet, calm catch-up, archetypes, game challenge.
import { Onboarding, type OnboardResult } from "./v3/Onboarding";
import { FollowUpChips } from "./v3/FollowUpChips";
import { ProofShareButton, ReceiptStrip } from "./v3/ReceiptStrip";
import { SignalDietPanel } from "./v3/SignalDietPanel";
import { CatchMeUp } from "./v3/CatchMeUp";
import { pickCalmCards } from "./v3/calmPick";
import { categoryReads, divesOpened, readTotals, trackCardRead, trackDiveOpened } from "./v3/track";
import { computeArchetype } from "./v3/archetype";
import { shareArchetypeImage } from "./v3/shareV3";
import { wantsGameFromHash } from "./v3/gameShare";
import { buildLocalGame } from "./v3/localGame";
import { buildFrontPageQuiz } from "./v3/frontPageQuiz";
import { quizShareText, wantsQuizFromHash } from "./v3/quizShare";
import { editionShareText } from "./v3/editionShare";
import { EditionDesk } from "./v3/EditionDesk";

// Newsletter editions (the "pick your inbox" signup). Default-to-yes: all pre-selected, one
// Subscribe. The chosen editions are captured on subscribe so the pipeline can route later.
const EDITIONS = [
  { id: "daily", label: "Daily Signal" },
  { id: "good-news", label: "Good News" },
  { id: "money", label: "Money" },
  { id: "sport", label: "Sport" },
  { id: "tech", label: "Tech" },
] as const;
const READ_TARGET = SESSION_SIZE;
const DAILY_GOAL_CARDS = 20; // ~2 rounds, the daily reading goal (a habit anchor)
const DEPLOY_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
// Six reactions: five expressive emojis (centered on phones) and a plain Like pinned to the
// right end as the primary positive signal (no separate dislike). One tap, not a verdict.
const REACTIONS = [
  { id: "fire",      Icon: Flame,     emoji: "\uD83D\uDD25", label: "Hot",        tint: "#ff7849" },
  { id: "insight",   Icon: Lightbulb, emoji: "\uD83D\uDCA1", label: "Insightful", tint: "#f0c66a" },
  { id: "wow",       Icon: Zap,       emoji: "\u26A1",        label: "Wow",        tint: "#82b5ec" },
  { id: "pondering", Icon: Brain,     emoji: "\uD83E\uDDE0", label: "Pondering",  tint: "#c39ce8" },
  { id: "spark",     Icon: Sparkles,  emoji: "\u2728",        label: "Curious",    tint: "#82dceb" },
  { id: "like",      Icon: ThumbsUp,  emoji: "\uD83D\uDC4D", label: "Like",       tint: "#5fd08a" }
] as const;
const WORDS_PER_MINUTE = 220;
const SWIPE_THRESHOLD = 60;
const HINT_DURATION_MS = 7000;

// Death and serious harm change a card's REGISTER, whatever category the pipeline filed it
// under. The audit twice caught a fatal crash leading the day under "Culture & Curiosity"
// with a flame-reaction row beneath it, the known fatal-accident-is-not-recreation defect
// class surfacing in the most visible slot. The pipeline gate is the real fix; this is the
// deterministic client-side guard that keeps the playful furniture (fun reactions, the
// Good News chip) off such stories no matter what the data says.
const GRAVE_RE = /\b(dies?|died|death|dead|killed?|killing|fatal(?:ly)?|homicide|murder(?:ed|s)?|suicide|drowns?|drowned)\b/i;
function isGraveStory(card: { headline?: string; essence?: string } | null | undefined): boolean {
  if (!card) return false;
  return GRAVE_RE.test(card.headline ?? "") || GRAVE_RE.test(card.essence ?? "");
}

type ReactionMap = Record<string, string[]>;
type Theme = "dark" | "light" | "sepia" | "oled" | "retro" | "auto";
/** "auto" follows the device: dark rooms get Cinema, bright rooms get Daylight. */
function resolveTheme(theme: Theme): Exclude<Theme, "auto"> {
  if (theme !== "auto") return theme;
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}
// Honest names: the default theme is not flat "dark", the story's own image
// paints the room, so it is Cinema. Midnight is the true-black quiet one.
// Retro is the vintage press edition: aged newsprint, halftone photographs.
// FOUR offered, not six. "Auto" resolves to Cinema in any dark room and Daylight in a bright
// one, so as a menu entry it was a third name for two themes the reader can already pick, and
// Midnight sat a few points of black away from Cinema. Six choices where four are distinct is
// not generosity, it is a reader deciding between things that look the same.
// The Theme type keeps "oled" and "auto" so a reader who stored one still renders correctly;
// migrateHiddenTheme moves them onto Cinema, since a theme you cannot re-select from the menu
// is a trap once you switch away from it.
// THREE, since the Kindle layer (2026-09-13): Cinema, Paper and Retro. Daylight became Paper,
// the light theme as a Kindle page (kindle.css section 5), and Sepia folded into Retro, which
// is the same warm paper with a soul. A stored "sepia" still renders and migrates to Retro.
const THEME_OPTIONS: { id: Theme; label: string }[] = [
  { id: "light", label: "Paper" },
  { id: "retro", label: "Retro" },
  { id: "dark", label: "Cinema" },
];
// A reader who chose to read without an account. Remembered so the first door is asked once.
const WALL_SKIP_KEY = "insnip.wallSkipped";
const HIDDEN_THEMES = new Set<Theme>(["oled", "auto"]);
export function migrateHiddenTheme(stored: Theme): Theme {
  if (stored === "sepia") return "retro";
  return HIDDEN_THEMES.has(stored) ? "dark" : stored;
}
type SubscribeStatus = "idle" | "saving" | "saved" | "invalid" | "error";
type SpeechState = "idle" | "speaking";
// Bump to re-show the welcome/onboarding popup to the ENTIRE user base on their
// next visit (any reader with a lower stored version sees it once more). Used for
// a coordinated "fresh start" after a big content/image refresh.
// v4 bumped this to 3 to force-migrate readers onto the rebuilt onboarding; keep that.
const ONBOARD_VERSION = 3;
// Feedback categories: distinct sets for a specific card vs. the product as a whole. Kept
// identical to the app's so one admin view can read both surfaces without translating.
const FEEDBACK_CATEGORIES_CARD = [
  "Inaccurate or misleading",
  "Confusing or unclear",
  "Great card",
  "Wrong or bad image",
  "Biased framing",
  "Something else",
] as const;
const FEEDBACK_CATEGORIES_GENERAL = [
  "Bug or something broke",
  "Feature request",
  "Design or usability",
  "Content quality",
  "Love the site",
  "Something else",
] as const;
const FEEDBACK_WORD_LIMIT = 100;
// Teaching starts on the FIRST card, not the third. Waiting for three stories was meant to earn
// the interruption, but it moved the welcome to a moment the reader had already built their own
// habit in, and most readers never reached it: the walkthrough went unseen by the people who
// needed it. It now runs on card one, after a short dwell (below) so the first headline is read
// first and the welcome lands on a reader who is oriented rather than on a blank arrival.
const TOUR_AFTER_CARDS = 1;
// The dwell: long enough to take in the first headline and its photograph, short enough that the
// welcome still feels like part of arriving rather than an interruption of reading.
const TOUR_DWELL_MS = 3500;

// THERE IS NO STAGING UNLOCK ANY MORE, and this note is here so nobody adds one back.
// A hostname-keyed constant used to drop every member gate on insnip-staging.pages.dev so the
// app could be reviewed without an account. It cost more than it gave: staging stopped being a
// rehearsal of production, so the one build anybody actually looked at was the one build where
// the gates did not exist. Search opened for a signed-out reviewer on staging and stayed shut in
// production, and neither of those was ever seen next to the other.
// To review a member feature now, sign in. That is what a reader has to do.

type Streak = { count: number; lastDate: string };

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

function isoDay(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function relativeDate(value: string): string {
  const then = new Date(value).getTime();
  const now = Date.now();
  const diffDays = Math.round((now - then) / 86_400_000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(value);
}

/** A catchy, NON-temporal freshness label for cards. We deliberately never show "Yesterday" or
 *  "N days ago", a stale-looking timestamp makes a genuinely useful insight feel skippable. Every
 *  card reads as current/worth-it; the gradation is subtle (newest gets the most excitement). */
function recencyLabel(value: string): string {
  const then = new Date(value).getTime();
  if (!then) return "Latest";
  const hours = (Date.now() - then) / 3_600_000;
  if (hours < 6) return "Just dropped";
  if (hours < 30) return "Fresh";
  if (hours < 24 * 6) return "Latest";
  return "Worth knowing";
}

/** Evergreen knowledge cards are not daily news, suppress any time cue entirely. */
function cardTimeLabel(card: InSnipCard): string {
  return card.contentType === "knowledge" ? "" : recencyLabel(card.publishedAt || "");
}
/** Card meta line: "<when> · <read time>" for news, just "<read time>" for evergreen. */
function cardMeta(card: InSnipCard): string {
  const when = cardTimeLabel(card);
  const read = readingTime(card);
  return when ? `${when} · ${read}` : read;
}
/** A news card published in the last ~90 minutes, surfaced as a "Just in" liveness cue. */
function isJustIn(card: InSnipCard): boolean {
  if (card.contentType === "knowledge" || card.contentType === "brief" || card.contentType === "explainer") return false;
  const t = Date.parse(card.publishedAt || "");
  return !!t && Date.now() - t < 90 * 60 * 1000;
}
/** Tidy stray source typography on a single-line string: a space before terminal
 *  punctuation ("responsibility ?" → "responsibility?") and doubled spaces. */
function tidyText(s: string): string {
  return (s || "").replace(/ +([?!,.;:])/g, "$1").replace(/ {2,}/g, " ");
}

/** The brief's calendar day, from its id (brief-YYYY-MM-DD), stable regardless of the
 *  reader's timezone, so two briefs generated either side of local midnight don't both
 *  collapse onto the same displayed date. */
function briefDayKey(card: InSnipCard): string {
  const m = /(\d{4})-(\d{2})-(\d{2})/.exec(card.id || "");
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return (card.publishedAt || "").slice(0, 10);
}
function briefDayLabel(card: InSnipCard): string {
  const d = new Date(`${briefDayKey(card)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
}

/** "49s" / "2 min": the read time as it rides the folio line (2026-09-13, the simpler card). */
function readSeconds(card: InSnipCard): number {
  const text = `${card.headline} ${card.essence} ${card.body} ${card.whyItMatters}`;
  const words = text.trim().split(/\s+/).length;
  return Math.max(20, Math.round((words / WORDS_PER_MINUTE) * 60));
}
function shortRead(card: InSnipCard): string {
  const seconds = readSeconds(card);
  return seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)} min`;
}

function readingTime(card: InSnipCard): string {
  const text = `${card.headline} ${card.essence} ${card.body} ${card.whyItMatters}`;
  const words = text.trim().split(/\s+/).length;
  const seconds = Math.max(20, Math.round((words / WORDS_PER_MINUTE) * 60));
  if (seconds < 60) return `${seconds}s read`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min read`;
}

/** The card's exact publish moment, spelled out ("16 July 2026, 2:30 pm"). The card face keeps
 *  the calm non-temporal freshness label; this concrete date lives inside the Sources drawer so a
 *  reader who wants to know precisely when a story dropped can, and it lines up with that label. */
function fullPublishedLabel(card: InSnipCard): string {
  const t = Date.parse(card.publishedAt || "");
  if (!t) return "";
  return new Date(t).toLocaleString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** How many distinct sources back a card (primary + supporting), floored at 1. Drives the
 *  "Sources · N" chip so a reader can see at a glance the story is sourced, and where to look. */
function sourceCount(card: InSnipCard): number {
  const supporting = Array.isArray(card.supportingSources) ? card.supportingSources.length : 0;
  const primary = card.primarySourceName || card.primarySourceUrl ? 1 : 0;
  return Math.max(1, primary + supporting);
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function faviconFor(url: string): string {
  return `https://www.google.com/s2/favicons?sz=64&domain=${domainOf(url)}`;
}

function tagHue(tag: string | undefined): number {
  if (!tag) return 0;
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) >>> 0;
  return hash % 360;
}

/** Subtle haptic feedback for key taps (no-op where unsupported). Routes through the
 *  native bridge so the Capacitor app gets real impact haptics; the web path is the
 *  same navigator.vibrate(pattern) it always was. */
function haptic(pattern: number | number[] = 8) {
  hapticTap(Array.isArray(pattern) ? pattern : [pattern]);
}

// The solid background each theme paints (mirrors --bg in styles.css), used to keep
// the native status bar in step with the room the reader is sitting in.
const THEME_BG: Record<Exclude<Theme, "auto">, string> = {
  dark: "#0b0a0e",
  light: "#f5efe2",
  sepia: "#ece0c8",
  oled: "#000000",
  retro: "#e9e2cf",
};

/** Time-of-day warmth for the ambient glow: mornings (5-10h) run slightly cool, evenings
 *  (17-23h) slightly warm, everything else neutral. Multiplies the sampled RGB per channel
 *  and clamps each to the 0-255 byte range, so the room subtly follows the reader's hour. */
function todWarm(r: number, g: number, b: number): RGB {
  const h = new Date().getHours();
  let mr = 1;
  let mg = 1;
  let mb = 1;
  if (h >= 5 && h < 10) { mr = 0.96; mb = 1.05; }
  else if (h >= 17 && h < 23) { mr = 1.06; mg = 1.0; mb = 0.94; }
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return { r: clamp(r * mr), g: clamp(g * mg), b: clamp(b * mb) };
}

/** Coarse time-of-day bucket, drives a faint ambient tint so the app feels aware of the hour. */
function timeOfDay(): "dawn" | "day" | "dusk" | "night" {
  const h = new Date().getHours();
  if (h < 6) return "night";
  if (h < 10) return "dawn";
  if (h < 17) return "day";
  if (h < 20) return "dusk";
  return "night";
}

function readingPattern(reactions: ReactionMap, viewedIds: string[], cards: InSnipCard[]) {
  const viewedCards = viewedIds
    .map((id) => cards.find((card) => card.id === id))
    .filter((card): card is InSnipCard => Boolean(card));
  const tagCounts = viewedCards.reduce<Record<string, number>>((acc, card) => {
    const tag = card.category ?? "General";
    acc[tag] = (acc[tag] ?? 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
  const topTag = entries[0]?.[0] ?? "mixed signals";
  const top = topTag.toLowerCase();
  const distinct = entries.length;
  const readCount = viewedCards.length || 1;
  const dominance = (entries[0]?.[1] ?? 0) / readCount;
  const flat = Object.values(reactions).flat();
  const r = (id: string) => flat.filter((x) => x === id).length;

  // Reaction-led archetypes first (strongest signal), then reading-shape archetypes
  // (which work even with zero reactions, that's why it always read "Curious Skimmer"
  // before). A rotating default keeps it fresh session to session.
  if (r("pondering") >= 2)
    return { title: "The Deep Diver", body: `You lingered on ${top} and weighed the details. We'll bring sharper, source-heavy stories.`, topTag };
  if (r("fire") >= 3)
    return { title: "Heat Seeker", body: `You went straight for the boldest ${top} stories. Expect more high-voltage drops.`, topTag };
  if (r("spark") >= 2)
    return { title: "The Wanderer", body: "You chased the surprising and the odd. Your feed will keep a few delightful curveballs.", topTag };
  if (distinct >= 5)
    return { title: "The Polymath", body: `You roamed ${distinct} fields in one sitting. We'll keep your drop wide and worldly.`, topTag };
  if (dominance >= 0.6)
    return { title: `${topTag} Regular`, body: `${topTag} is clearly your beat; we'll lead with it and still slip in a wildcard.`, topTag };
  if (flat.length >= 5)
    return { title: "Expressive Scanner", body: "You reacted often and moved fast. Punchy, varied and easy to skim is your lane.", topTag };

  const defaults = [
    { title: "Curious Skimmer", body: `You sampled ${top} most; we'll mix useful signals with surprising finds.` },
    { title: "The Connector", body: `You moved across ${top} and beyond, linking the dots. We'll keep the threads coming.` },
    { title: "Signal Hunter", body: `You're after what actually matters in ${top}. Less noise, more signal, noted.` },
    { title: "The Early Bird", body: `You caught today's ${top} before most. We'll keep you first to the drop.` },
  ];
  let h = 0;
  for (const ch of topTag + String(readCount)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return { ...defaults[Math.abs(h) % defaults.length], topTag };
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * The Insight of the Day owed on THIS visit, or null. Pure read of storage so the reader's single
 * first-run interruption can be decided in one place, before anything async runs.
 */
function dueInsightOfDay(): DailyInsight | null {
  // Native (iOS/Android) never renders the insight, so it never claims the slot there either.
  if (isNativeApp) return null;
  // The prize gate: until a first round is FINISHED the insight never interrupts. A returning
  // reader (key stamped by the round-completion handler) keeps the daily ritual unchanged.
  if (!hasFinishedARound((k) => localStorage.getItem(k))) return null;
  const today = isoDay(new Date());
  if (readStored<string>("insnip.insightSeen", "") === today) return null;
  // On the reader's birthday the celebration takes priority over the daily insight.
  const bday = readDob(readStored<unknown>("insnip.dob", null));
  if (bday && isBirthdayToday(bday, new Date()) && readStored<number>("insnip.bdayYear", 0) !== new Date().getFullYear()) return null;
  const signals = readStored<Record<string, number>>("insnip.topicSignals", {});
  const interests = readStored<string[]>("insnip.interests", []);
  const top =
    Object.entries(signals).sort((a, b) => b[1] - a[1])[0]?.[0] ?? interests[0] ?? null;
  return pickInsightOfDay(today, top);
}

/** Stamp "this reader has finished a round", the key that unlocks the daily insight. */
function markRoundDone(): void {
  try { localStorage.setItem(ROUND_DONE_KEY, "1"); } catch { /* best-effort */ }
}

/**
 * The round explains itself: one quiet line per known whyShown kind. Unknown kinds return
 * null and render NOTHING, so a new worker vocabulary can never put raw ids on a card.
 */
function whyShownLabel(w: { kind: string; detail: string } | null | undefined): string | null {
  if (!w || typeof w.kind !== "string") return null;
  switch (w.kind) {
    // USE THE DETAIL THE WORKER SENT, which it builds from the floor ACTUALLY in force for that
    // round precisely so a retuned constant can never make this line lie. Hardcoding "five" here
    // threw that away: the opening rounds of a sitting now run an Australian floor of eight, and
    // this would have told the reader five while the round held eight. Capitalised for display
    // because the worker writes it as a sentence fragment.
    case "au": {
      const d = typeof w.detail === "string" ? w.detail.trim() : "";
      if (!d) return "An Australian pick";
      return d.charAt(0).toUpperCase() + d.slice(1);
    }
    // "buzz" is the CORROBORATION count, how many newsrooms independently ran the story, not an
    // engagement number. This said "Readers are on this now", which is a claim the product has no
    // data for: there is no community-engagement signal anywhere in it. Say the true thing.
    case "buzz": return "Several outlets are running this";
    case "searched": return "People are searching for this";
    case "affinity": return typeof w.detail === "string" && w.detail ? `Included because you read ${w.detail}` : null;
    case "taste": return "Like stories you read";
    case "insight": return "A high-signal pick";
    default: return null;
  }
}

/** Local-date key (per the reader's own timezone), e.g. "2026-06-15". */
function localDateKey(): string {
  return new Date().toLocaleDateString("en-CA");
}

/** How many rounds the reader has done today (their timezone). Resets each day. */
function readDayRound(): number {
  const stored = readStored<{ date: string; n: number }>("insnip.dayRound", { date: "", n: 0 });
  return stored.date === localDateKey() ? stored.n : 0;
}

function writeDayRound(n: number): void {
  try {
    localStorage.setItem("insnip.dayRound", JSON.stringify({ date: localDateKey(), n }));
  } catch {
    /* best-effort */
  }
}

function updateStreak(): Streak {
  const stored = readStored<Streak | null>("insnip.streak", null);
  const today = isoDay(new Date());
  if (!stored) {
    const next: Streak = { count: 1, lastDate: today };
    localStorage.setItem("insnip.streak", JSON.stringify(next));
    return next;
  }
  if (stored.lastDate === today) return stored;
  const yesterday = isoDay(new Date(Date.now() - 86_400_000));
  const next: Streak = {
    count: stored.lastDate === yesterday ? stored.count + 1 : 1,
    lastDate: today
  };
  localStorage.setItem("insnip.streak", JSON.stringify(next));
  return next;
}

function currentBuildScriptPath(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  if (!script?.src) return null;
  try {
    return new URL(script.src).pathname;
  } catch {
    return script.src;
  }
}

async function hasFreshDeploy(): Promise<boolean> {
  const currentScript = currentBuildScriptPath();
  if (!currentScript) return false;
  const response = await fetch(`/?insnip-check=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" }
  });
  if (!response.ok) return false;
  const html = await response.text();
  return !html.includes(currentScript);
}

const ONBOARD_OPTIONS = [
  { id: "Money & Work", emoji: "💰", blurb: "Markets, jobs, the economy" },
  { id: "Technology & Tools", emoji: "⚡", blurb: "AI, products, cybersecurity" },
  { id: "Science & Systems", emoji: "🔬", blurb: "Research, space, health" },
  { id: "Power & Policy", emoji: "🏛️", blurb: "Government, courts, rules" },
  { id: "Culture & Curiosity", emoji: "✨", blurb: "Media, arts, the surprising" },
  { id: "Sport & Play", emoji: "🏅", blurb: "Sport, games, competition" },
  { id: "Planet & Environment", emoji: "🌍", blurb: "Climate, nature, energy" },
];


// The 7 anchor categories that drive "Make It Yours" topic personalization.
const ANCHOR_CATEGORIES: { id: string; emoji: string }[] = [
  { id: "Money & Work", emoji: "💰" },
  { id: "Science & Systems", emoji: "🔬" },
  { id: "Technology & Tools", emoji: "⚡" },
  { id: "Culture & Curiosity", emoji: "✨" },
  { id: "Power & Policy", emoji: "🏛️" },
  { id: "Sport & Play", emoji: "🏅" },
  { id: "Planet & Environment", emoji: "🌍" },
];

type TopicPref = "follow" | "mute";
type TopicPrefMap = Record<string, TopicPref>;

/** Read the reader's saved topic prefs ({ "<category>": "follow" | "mute" }). */
function readTopicPrefs(): TopicPrefMap {
  const raw = readStored<Record<string, unknown>>("insnip.topics", {});
  const out: TopicPrefMap = {};
  for (const [cat, val] of Object.entries(raw)) {
    if (val === "follow" || val === "mute") out[cat] = val;
  }
  return out;
}

/** Persist the topic prefs map (absent category = neutral). */
function writeTopicPrefs(map: TopicPrefMap): void {
  try {
    localStorage.setItem("insnip.topics", JSON.stringify(map));
  } catch {
    /* best-effort */
  }
}

/** Split a prefs map into the followed (preferred) + muted category lists. */
function topicPrefsToLists(map: TopicPrefMap): TopicPrefs {
  const preferred: string[] = [];
  const muted: string[] = [];
  for (const [cat, val] of Object.entries(map)) {
    if (val === "follow") preferred.push(cat);
    else if (val === "mute") muted.push(cat);
  }
  return { preferred, muted };
}

export default function App() {
  const [cards, setCards] = useState<InSnipCard[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  // Explore by content type (#F5): All / News / Knowledge.
  const [contentFilter, setContentFilter] = useState<"all" | "news" | "knowledge">("all");
  // The Brief, a per-day digest, its own feature (not in the swipe feed).
  const [briefs, setBriefs] = useState<InSnipCard[]>([]);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefIndex, setBriefIndex] = useState(0);
  const [quizCard, setQuizCard] = useState<InSnipCard | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  // "Signal or Noise", the daily can-you-tell-what's-real game (its own overlay + rail card).
  const [gameOpen, setGameOpen] = useState(false);
  const [gameToday, setGameToday] = useState<GameToday | null>(null);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  // True when the worker had no round for today; the local fallback fills in.
  const [gameFetchMissed, setGameFetchMissed] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [relatedCards, setRelatedCards] = useState<InSnipCard[]>([]);
  const [briefWeather, setBriefWeather] = useState<DayWeather | null>(null);
  // Server-side semantic search (#2): the box queries /search (semantic + keyword,
  // never empty) instead of substring-filtering the loaded deck. null = not searching.
  const [searchResults, setSearchResults] = useState<InSnipCard[] | null>(null);
  const [searchWeak, setSearchWeak] = useState(false);
  const [searching, setSearching] = useState(false);
  // Search is its own modal (decoupled from the deck): type → live suggestions → tap a
  // result to read it → back to results → close. searchDetail = the card being previewed.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDetail, setSearchDetail] = useState<InSnipCard | null>(null);
  // Account / sign-in (#8): null until auth resolves; signedIn=false = anonymous.
  const [account, setAccount] = useState<AccountSummary | null>(null);
  // Which member-only feature a guest just reached for (opens the unlock prompt). null = none.
  const [gateFeature, setGateFeature] = useState<null | "search" | "brief" | "ask" | "deepdive">(null);
  const [signInOpen, setSignInOpen] = useState(false); // shared sign-in modal (no feature gate)
  // Firebase's verdict on this session: null until its first callback (which waits for any
  // persisted session to restore), then true/false. The mandatory-sign-in wall keys on this.
  const [firebaseAuthed, setFirebaseAuthed] = useState<boolean | null>(null);

  // THE ONE ANSWER to "may this reader have Deep dive, the Brief, Search and Ask?".
  //
  // EITHER signal is proof, and both are written === true, never truthy, because both are
  // null while auth is still resolving and a member surface must be shut during that window
  // rather than open and then snatched back.
  //  - firebaseAuthed: Firebase says this session belongs to a signed-in user. That IS the
  //    requirement (a valid Google, Apple or email-code sign-in), and it is known the moment
  //    the persisted session restores, offline included.
  //  - account.signedIn: the worker's profile row, which needs a network round trip.
  // The worker signal alone was not enough, and the APP is where it would have hurt: one
  // failed /account call (a tunnel, a flaky connection, a cold worker) and a reader who is
  // demonstrably signed in gets told to sign in. Reading works offline in the app; being
  // recognised has to as well.
  //
  // Every door into those four checks this, AND every one of the four refuses to RENDER
  // without it (see the overlays below). Two layers on purpose: the door check is what shows
  // the sign-in card, and the render check is what makes a missed door harmless. The gates
  // used to live only on the doors, and the app grew side entrances that walked straight past
  // them (a topic chip in the wire opened the search library; the tour opened the Brief).
  const member = isMember(firebaseAuthed, account?.signedIn);
  // How many friends this reader has brought in (the invite mechanic).
  const [referralCount, setReferralCount] = useState(0);
  // Reward balances (streak-freeze tokens + rewarded referrals). Best-effort, zeros until fetched.
  const [perks, setPerks] = useState<PerksState>({ freezeTokens: 0, referralsRewarded: 0 });
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [signInEmail, setSignInEmail] = useState("");
  // Email 5-digit code: "form" = enter email, "code" = enter the code we emailed.
  const [emailStep, setEmailStep] = useState<"form" | "code">("form");
  const [emailCode, setEmailCode] = useState("");
  // PWA install (retention): show our own "Add to Home Screen" prompt once the reader
  // has engaged, unless already installed or previously dismissed.
  const [installReady, setInstallReady] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => readStored("insnip.installDismissed", false));
  const [installing, setInstalling] = useState(false);
  // "Get the native app" prompt (live on the App Store and Google Play). Web-only.
  const [appPromoGone, setAppPromoGone] = useState(() => appPromoDismissed());
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [savedIds, setSavedIds] = useState<string[]>(() => readStored<string[]>("insnip.saved", []));
  // Saved cards resolved by id beyond today's deck (so older saves still show in the saved view).
  const [savedCardCache, setSavedCardCache] = useState<Record<string, InSnipCard>>({});
  // The feed card to return to when leaving the saved view via "Home".
  const feedReturnIdRef = useRef<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => readStored<string[]>("insnip.hidden", []));
  const [imageShareState, setImageShareState] = useState<"idle" | "busy" | "done">("idle");
  const [shareCard, setShareCard] = useState<InSnipCard | null>(null);
  const [viewedIds, setViewedIds] = useState<string[]>([]);
  const [reactions, setReactions] = useState<ReactionMap>({});
  // "Call it: Signal or Noise" per card: my call + the crowd split + the decided verdict.
  // Hydrated lazily for the active card; POST responses keep it fresh after a call.
  const [stances, setStances] = useState<Record<string, StanceState>>({});
  const [showInsight, setShowInsight] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // The five expressive reactions sit behind one control; Like stands on its own.
  const [reactOpen, setReactOpen] = useState(false);
  // "Sourced and gate-checked" trust popover (opened from the Sources stat + Intelligence chip).
  const [trustOpen, setTrustOpen] = useState(false);
  // Brief share: flips the button label to "Copied" briefly on the clipboard fallback path.
  const [briefCopied, setBriefCopied] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [editions, setEditions] = useState<string[]>(() => EDITIONS.map((e) => e.id));
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [goodNewsOpen, setGoodNewsOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [deepDiveOpen, setDeepDiveOpen] = useState(false);
  // A question handed off from the Deep Dive to the real Ask AI: sent once Ask actually opens
  // (which may be after a sign-in gate), so the question survives the unlock flow.
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<SubscribeStatus>("idle");
  const [emailDraft, setEmailDraft] = useState("");
  // v3 Atelier: new readers open on the daylight paper look; Cinema stays one tap away.
  // Cinema is v4's default voice (main.tsx migrates stored themes once, pre-paint).
  const [theme, setTheme] = useState<Theme>(() => migrateHiddenTheme(readStored<Theme>("insnip.theme", "dark")));
  // The reader's Aa (the Kindle layer): face, size, weight, spacing, alignment, warmth.
  const [display, setDisplay] = useState<Display>(() => loadDisplay());
  // The feed lens: Top (the curated order), For you (followed topics + your own
  // affinity first), Latest (newest drop first). A reader-owned view of the same deck.
  // The phone browser has no order menu, so it always reads the curated order (a lens left on
  // "Latest" from an earlier visit would otherwise be stuck with no way back).
  const [lens, setLens] = useState<"top" | "foryou" | "latest">(() => (isPhoneWeb() ? "top" : readStored("insnip.lens", "top")));
  // Reading measure: the comfortable 700px column, or a wide 820px one.
  const [measure, setMeasure] = useState<"comfy" | "wide">(() => readStored("insnip.measure", "comfy"));
  // The daily reading goal is the reader's to set (10 / 20 / 30 stories).
  const [dailyGoal, setDailyGoal] = useState<number>(() => readStored("insnip.goal", DAILY_GOAL_CARDS));
  const [aaOpen, setAaOpen] = useState(false);
  const [tickerIx, setTickerIx] = useState(0);
  // The digest: today's whole front page on one sheet, readable in minutes.
  const [digestOpen, setDigestOpen] = useState(false);
  // The referral ladder folds away behind one row until asked for.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [interests, setInterests] = useState<string[]>(() => readStored<string[]>("insnip.interests", []));
  const [affinity, setAffinity] = useState<Record<string, number>>(() => readStored<Record<string, number>>("insnip.affinity", {}));
  const [topicSignals, setTopicSignals] = useState<Record<string, number>>(() => readStored<Record<string, number>>("insnip.topicSignals", {}));
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // The mandatory first-use gesture tour ("Follow the Spark"). Runs once before onboarding.
  const [tourOpen, setTourOpen] = useState(false);
  // The tour drives REAL navigation as its live demo, but nothing it shows counts as reading:
  // visitCard checks this ref and skips every read record (view log, seen set, streak, ritual
  // logs) while the tour owns the deck. finishTour returns the deck to the card the tour
  // started on, so a brand-new reader still begins at story 1 with an honest zero.
  const tourOpenRef = useRef(false);
  const tourReturnRef = useRef<{ id: string; viewed: string[] } | null>(null);
  // Bottom bar, one at a time: a fresh card is clean (~2s), then the status bar; pull up -> menu.
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusReady, setStatusReady] = useState(false);
  // v3: the whole first-run flow lives in its own component (v3/Onboarding); the app keeps
  // only the entry mode ("profile" = the Birthday panel's jump straight to the profile beat).
  const [onboardEntry, setOnboardEntry] = useState<"first-run" | "profile">("first-run");
  const savedDob = readDob(readStored<unknown>("insnip.dob", null));
  const [dob, setDob] = useState<Dob | null>(savedDob);
  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [birthdayCelebration, setBirthdayCelebration] = useState(false);
  const [weekRecapOpen, setWeekRecapOpen] = useState(false);
  // v3 surfaces: the signal-diet wheel and the calm catch-up ritual.
  const [dietOpen, setDietOpen] = useState(false);
  const [calmOpen, setCalmOpen] = useState(false);
  const [calmCards, setCalmCards] = useState<InSnipCard[]>([]);
  // The Desk: today's edition as a designed front page. Auto-opens once per day
  // (deep links and #game skip it); always reachable from the top bar.
  const [deskOpen, setDeskOpen] = useState(false);
  // The front page's instruments: what the country is reading now, how many stories landed
  // since it opened, and whether this reader wants the front page first on every visit.
  // The front page: set from the worker when the page opens, set again on request.
  const [deskPaper, setDeskPaper] = useState<EditionPaper | null>(null);
  const [deskPaperState, setDeskPaperState] = useState<"loading" | "ready" | "failed" | "setting">("loading");
  const deskPaperRef = useRef<EditionPaper | null>(null);
  const deskBaselineRef = useRef<Set<string> | null>(null);
  const [deskFresh, setDeskFresh] = useState(0);
  const [frontPageFirst, setFrontPageFirst] = useState<boolean>(() => { try { return localStorage.getItem("insnip.frontPageFirst") === "1"; } catch { return false; } });
  // Feedback popup: null = closed; otherwise the target (a specific card, or the product).
  const [feedbackFor, setFeedbackFor] = useState<null | { kind: "general" } | { kind: "card"; cardId: string }>(null);
  const [fbCategory, setFbCategory] = useState("");
  const [fbMessage, setFbMessage] = useState("");
  const [fbStatus, setFbStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const deskAutoRef = useRef(false);
  // Living color: ambient glow derived from the active card's image (gold fallback).
  const [ambient, setAmbient] = useState<RGB>(GOLD);
  const [focusMode, setFocusMode] = useState(false);
  const [speechState, setSpeechState] = useState<SpeechState>("idle");
  const [briefingActive, setBriefingActive] = useState(false);
  // Audio briefing session: stays open (mini-player visible) while paused; briefingRate = speed.
  const [briefingSession, setBriefingSession] = useState(false);
  const [briefingRate, setBriefingRate] = useState(1);
  const [recapOpen, setRecapOpen] = useState(false);
  const [trending, setTrending] = useState<string[]>([]);
  const [streak, setStreak] = useState<Streak>({ count: 0, lastDate: "" });
  const [showHint, setShowHint] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Feed ordering for THIS session only, deliberately NOT persisted, so a returning reader
  // always starts back on "latest" (the deck/rounds reset as usual). The ref lets the
  // round-loader read the current mode without a stale closure.
  const [feedMode, setFeedMode] = useState<FeedMode>("latest");
  const feedModeRef = useRef<FeedMode>("latest");
  // A visit interrupts the reader at most once. The landing page at "/" does the explaining now,
  // so when an Insight of the Day is due it takes the slot and the gesture tour and the interests
  // picker wait for a later open (the tour stays replayable from the You menu). This is decided
  // once, on the first render, because the tour only decides after the round fetch resolves: two
  // independent checks would race, and dismissing the insight clears the state a late check reads.
  const [insightDue] = useState<DailyInsight | null>(dueInsightOfDay);
  // "Insight of the Day", shown once, on the first open of each day.
  const [insightOfDay, setInsightOfDay] = useState<DailyInsight | null>(insightDue);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  // THE PHONE DOOR. Read once, at mount, so the answer cannot change under the reader
  // mid-session; every later change goes through takeApp/declineApp below.
  //
  // Deliberately mounted INSIDE App rather than in place of it, which is the whole difference
  // between this and the wall it replaces: the feed loads, the round starts, and the door
  // simply sits over a reader that is already there. Declining costs no fetch and no reload.
  // Someone who tapped a shared headline reads that headline FIRST; the door waits for them
  // to move on to a second story (see arrivedOnAStory). Everyone else meets it on arrival.
  const [doorDeferred, setDoorDeferred] = useState(() => shouldShowPhoneDoor() && arrivedOnAStory());
  const [phoneDoorOpen, setPhoneDoorOpen] = useState(() => shouldShowPhoneDoor() && !arrivedOnAStory());
  // THE TOUR WAITS ITS TURN. Two sheets on one screen is the one layout this product forbids,
  // and the first-run tour used to land on top of the sign-in door. Every call that wants the
  // tour goes through here: it opens when no door, gate or wall is up, else tries again shortly.
  const tourBlockedRef = useRef(false); // set each render once authWall exists (below)
  const openTourWhenClear = useCallback(function tryTour() {
    if (!tourBlockedRef.current) { setTourOpen(true); return; }
    window.setTimeout(tryTour, 1500);
  }, []);
  const [appNudge, setAppNudge] = useState(shouldNudgeForApp);
  // THE SWIPE LESSON ON A PHONE BROWSER (USER 2026-10-30, from the phone: "users won't know to
  // swipe left"). The one-shot seven-second flash was the right lesson at the wrong length: it
  // ran once, on card 1, while the visitor was still reading the headline, and never again on
  // that device. Now it stays until it is LEARNED, across the first three cards, lighter each
  // time: card 1 nudges the card, peeks the next one and says it; card 2 peeks and says "again";
  // card 3 only peeks. Landing on card 4, by swipe or by the arrow, ends it for good and opens
  // the interlude once: the ask, after three stories, in the same deck, swiped past like a card.
  // -1 off, 0..2 the card it stands on, 3 done.
  const [lesson, setLesson] = useState(-1);
  const [interlude, setInterlude] = useState(false);
  const interludeAt = useRef<number | null>(null);
  const interludeShown = useRef(false);
  // A topic read to its last story: the card that offers the rest of the round (see move).
  const [topicEnd, setTopicEnd] = useState(false);
  // The end-of-round prompt. `due` is armed at the two round-completion paths below (beside
  // markRoundDone, which is module level and cannot set state) and spent when the prompt
  // opens, so it can never fire twice for one round.
  const [roundPromptDue, setRoundPromptDue] = useState(false);
  const [roundPromptOpen, setRoundPromptOpen] = useState(false);
  const [promptedRound, setPromptedRound] = useState(1);
  // A phone in a browser is its own surface (see phoneread.css). Nearly everything that
  // surface does differently is CSS, but one thing cannot be: with the bottom nav removed,
  // the account button on the masthead is the ONLY way into the profile, and it has to open
  // the mobile sheet rather than the desktop dropdown.
  const phoneWeb = useMemo(() => isPhoneWeb(), []);
  // The phone menu: the menu button on the bar and More on the dock open the same drawer as the home.
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const storePlatform = useMemo(() => webPlatform(), []);

  /** They tapped through to a store. We cannot see whether they installed, so this only ever
   *  means "do not ask again" for the things that ASK: the full-screen door and the
   *  end-of-round prompt.
   *
   *  It leaves the standing strip alone (USER, 2026-09-08). It used to retract it, and the
   *  bug that made obvious was this: tapping a store link opens the store, and then the reader
   *  comes back to this same browser, still without the deep dive, search or Ask, and the one
   *  line explaining where those live had removed itself on the strength of a tap whose
   *  outcome we never learn. The strip is the label on this surface, not a pitch to be won. */
  const takeApp = useCallback((surface: string) => {
    setAppChoice("taken");
    setPhoneDoorOpen(false);
    sendEvent({ type: "app_get_click", tag: surface });
  }, []);

  /** They chose the browser. Honoured for good: the door never returns on this device. What
   *  remains is the one-line strip inside the card, which is the honest version of "here is
   *  what you are missing". */
  const declineApp = useCallback(() => {
    setAppChoice("declined");
    setPhoneDoorOpen(false);
    setAppNudge(true);
    sendEvent({ type: "app_door_declined", tag: storePlatform });
  }, [storePlatform]);
  const [verdicts, setVerdicts] = useState<Record<string, "up" | "down">>(() => readStored("insnip.verdicts", {}));
  const [pullY, setPullY] = useState(0);
  const [dragX, setDragX] = useState(0); // live horizontal swipe offset (finger-following)
  const [swipeSettling, setSwipeSettling] = useState(false); // transition on for spring-back / fly-off
  // The release ride: a spring trajectory baked into a CSS linear() easing (see spring.ts,
  // ported from the app's compositor glide). Null = the legacy 260ms curve (old engines).
  const [glideRide, setGlideRide] = useState<{ ms: number; easing: string } | null>(null);
  // Finger velocity in px/ms, lightly smoothed, so the release inherits the throw.
  const swipeVel = useRef({ x: 0, t: 0, v: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [freshCount, setFreshCount] = useState(0); // newly-released cards since this feed loaded
  // When the reader dismisses the "new snips" pill, snooze it (persisted) for an hour.
  const [freshSnooze, setFreshSnooze] = useState<number>(() => {
    try { return Number(localStorage.getItem("insnip.freshSnooze")) || 0; } catch { return 0; }
  });
  const feedLoadedAtRef = useRef(Math.floor(Date.now() / 1000));
  const [reward, setReward] = useState<{ emoji: string; title: string; body: string; big: boolean } | null>(null);
  const [bionicMode, setBionicMode] = useState<boolean>(() => readStored<boolean>("insnip.bionic", false));
  // Key points is the default reading view; it resets to Key points on each new card (see effect).
  // Reading depth: Quick (the loved key-points scan), Standard (the full body), Deeper
  // (body + key facts + jargon notes). The three-way depth toggle replaces the old
  // Full/Key-points binary. Opens on Quick per card, matching the previous default.
  // Two reading depths only: Quick (the key-points scan) and Deeper (full body + key facts +
  // jargon notes). The old middle "Standard" was just a lesser Deeper, so it was removed.
  const [readLevel, setReadLevel] = useState<"quick" | "deeper">("quick");
  // THE ONE DISCLOSURE (2026-09-13, the simpler card). The Quick|Deeper toggle, the why-box
  // and the Australia box are gone from the card face; "Read the full story" opens them in
  // place, under the pointers. Closed on every new card.
  const [storyOpen, setStoryOpen] = useState(false);
  // The card copy scrolls internally on phones; when the body sits below the fold we show a
  // "scroll for the full story" cue so the reader knows there is more (they used to miss it).
  const copyRef = useRef<HTMLDivElement>(null);
  const [copyHasMore, setCopyHasMore] = useState(false);
  const [slideshowMode, setSlideshowMode] = useState<"off" | "essence" | "body">("off");
  const [refreshReady, setRefreshReady] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [topicPrefs, setTopicPrefs] = useState<TopicPrefMap>(() => readTopicPrefs());
  const [topicsToast, setTopicsToast] = useState<string | null>(null);
  const [graffiti, setGraffiti] = useState<{ id: string; emoji: string; startX: number; startY: number; tx: number; ty: number; r: number; s: number }[]>([]);
  // Editorial type cover: cards whose hero image proved too small for a cinematic hero
  // (naturalWidth < 640) render a typographic cover instead of a blurry stretch.
  const [lowResHero, setLowResHero] = useState<Record<string, boolean>>({});
  // Load overture: a once-per-session entrance choreography class on the app shell.
  const [overture, setOverture] = useState(false);
  // Catch-me-up: how long the reader was away (days) + today's dismissal of the banner.
  const [awayDays, setAwayDays] = useState(0);
  const [catchupDismissed, setCatchupDismissed] = useState(true);
  // Round flare: a brief celebratory class on the gold progressline when a round completes.
  const [roundFlare, setRoundFlare] = useState(false);
  // Quote share pill: floats above a text selection inside the card copy.
  const [quotePill, setQuotePill] = useState<{ x: number; y: number; text: string } | null>(null);
  const quoteTimerRef = useRef<number | null>(null);
  const dragXRef = useRef(0);
  // Throttle for ambient-driven native status-bar syncs (at most one per second).
  const barSyncAtRef = useRef(0);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const flingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ttsRef = useRef<SpeechSynthesisUtterance | null>(null);
  // Persistent "seen" set (ids the reader has actually viewed), distinct from
  // the round-scoped viewedIds. Powers resume-where-you-left-off + skip-seen.
  const seenRef = useRef<Set<string>>(new Set(readStored<string[]>("insnip.seen", [])));
  // The same set's size, as STATE, because the sign-in invitation is timed off it and a ref
  // does not re-render. Every place that grows seenRef must grow this too, or the invitation
  // silently never arrives. There are exactly two such places: the resume seed and visitCard.
  const [seenCount, setSeenCount] = useState<number>(() => seenRef.current.size);
  const serverDrivenRef = useRef(false);
  // Current topic prefs as {preferred,muted} lists, kept in a ref so the
  // round-loading paths always read the latest without re-binding callbacks.
  const topicListsRef = useRef<TopicPrefs>(topicPrefsToLists(readTopicPrefs()));
  const [hasMoreCards, setHasMoreCards] = useState(true);
  const [dayRound, setDayRound] = useState(1);
  const [caughtUp, setCaughtUp] = useState(false);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [milestone, setMilestone] = useState<string | null>(null);
  const [pushState, setPushState] = useState<"unknown" | "unsupported" | "off" | "on" | "busy">("unknown");
  const [sharedCardId, setSharedCardId] = useState<string | null>(null);
  // "favorites" when the lead card came from the reader's own Favorites panel: same landing,
  // but the ribbon says so instead of "Shared with you".
  const [sharedVia, setSharedVia] = useState<string | null>(null);
  const [referralWelcome, setReferralWelcome] = useState(false);
  const lastImgTapRef = useRef(0);
  const lastCopyTapRef = useRef(0);
  const copyTapTimerRef = useRef<number | null>(null);
  const [heartKey, setHeartKey] = useState(0);
  // "Ask about this card", a lightweight grounded Q/A panel over the active card.
  const [askOpen, setAskOpen] = useState(false);
  const [askInput, setAskInput] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [askThread, setAskThread] = useState<{ q: string; a: string; sources?: { name: string; url: string }[] }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Order the feed once per session for this reader: explicit interests +
        // affinity learned locally + server-side affinities from the engagement
        // backend (blended, short timeout). Stable for the session so indices/
        // rounds don't shift mid-read; new signals apply next visit.
        const storedInterests = readStored<string[]>("insnip.interests", []);
        const storedAffinity = readStored<Record<string, number>>("insnip.affinity", {});
        const storedSignals = readStored<Record<string, number>>("insnip.topicSignals", {});
        const weights = readerWeights(storedInterests, storedAffinity);
        // Fold in explicit "more/less like this" tuning (can be negative to bury).
        for (const [category, delta] of Object.entries(tuningWeights(storedSignals))) {
          weights[category] = (weights[category] ?? 0) + delta;
        }
        const server = await fetchServerWeights();
        if (cancelled) return;
        for (const [category, value] of Object.entries(server)) {
          weights[category] = (weights[category] ?? 0) + Math.min(Number(value) || 0, 6) * 0.5;
        }

        // Server-driven feed: when the session API is configured, serve rounds
        // from the live card store (per-user no-repeat, resume, freshness). Falls
        // through to the bundled cards.json if the API is unset or unreachable.
        if (sessionApiEnabled()) {
          // "Make It Yours" prefs: followed categories weight up + muted hide.
          const topicLists = topicPrefsToLists(readTopicPrefs());
          const preferred = [
            ...(topicLists.preferred ?? []),
            ...storedInterests,
            ...Object.entries(storedSignals).filter(([, v]) => v > 0).map(([k]) => k),
          ];
          // First open of a new LOCAL day: start the day fresh on round 1 with the
          // latest drop (don't resume yesterday's mid-round position or lead with
          // stale cards). The seen-log + server-side insights/streaks persist, so
          // never-repeat and personalisation still carry across days.
          const isNewDay = readStored<string>("insnip.lastDay", "") !== localDateKey();
          const prefs = { preferred, muted: topicLists.muted };
          // A page load / app open should ALWAYS lead with the freshest cards, never a
          // resumed, frozen round (which re-showed already-seen cards and ignored newer
          // drops → "refresh keeps showing yesterday's cards"). refreshSession builds a
          // new round from the newest UNSEEN cards, so the never-repeat seen-log makes
          // this continue right where the reader left off AND surfaces anything new.
          const round = await refreshSession(preferred, prefs);
          if (cancelled) return;
          if (round && round.cards.length) {
            serverDrivenRef.current = true;
            // Shared deep link: lead with the EXACT shared card so the recipient lands
            // on the story their friend sent (the viral loop's payoff), even if it
            // isn't in their own round. ref=s marks it as a share for the welcome banner.
            const linkSrc =
              typeof window !== "undefined" ? window.location.hash || window.location.search : "";
            const linkedId = parseCardLink(linkSrc);
            let feed = round.cards;
            let sharedLanding = false;
            if (linkedId) {
              const shared =
                round.cards.find((c) => c.id === linkedId) ?? (await fetchCard(linkedId));
              if (shared && !cancelled) {
                // A push deep link can tow a story chain (`next=`, the follow-on hot stories
                // the engine picked). Queue them right behind the pushed card so finishing one
                // story flows straight into the next instead of a dead end.
                const chainIds = parseNextChain(linkSrc).filter(
                  (cid) => cid !== shared.id && !seenRef.current.has(cid),
                );
                const chainCards = (
                  await Promise.all(
                    chainIds.map(
                      async (cid) =>
                        round.cards.find((c) => c.id === cid) ??
                        (await fetchCard(cid).catch(() => null)),
                    ),
                  )
                ).filter((c): c is InSnipCard => !!c);
                if (cancelled) return;
                const leadIds = new Set([shared.id, ...chainCards.map((c) => c.id)]);
                feed = [shared, ...chainCards, ...round.cards.filter((c) => !leadIds.has(c.id))];
                setSharedCardId(shared.id);
                setSharedVia(parseVia(linkSrc));
                sharedLanding = true;
                if (isSharedLink(linkSrc)) {
                  setReferralWelcome(true);
                  // Attribute the inbound click to the sharer (referral hook), unless this
                  // is the sharer opening their own link.
                  const referrer = parseReferrer(linkSrc);
                  if (referrer && referrer !== anonId()) {
                    sendEvent({ type: "referral", cardId: shared.id, tag: referrer });
                  }
                }
              }
            }
            // THE STORY THEY HAVE ALREADY READ. The share page prints the whole story and its
            // button says "Read the other nine", so the reader arriving with `seen=<id>` has
            // finished that one. If it is in today's round it is taken out of the deal and
            // logged as read, so the nine really are the other nine and the first card of the
            // reader is never the card they just closed. It is not the linked card (which
            // `card=` asks us to LEAD with) and it never touches a round it is not in.
            const seenId = parseSeenCard(window.location.search);
            if (seenId && seenId !== linkedId && feed.some((c) => c.id === seenId)) {
              feed = feed.filter((c) => c.id !== seenId);
              seenRef.current.add(seenId);
              try {
                localStorage.setItem("insnip.seen", JSON.stringify([...seenRef.current]));
              } catch {
                /* the deal is still right for this session */
              }
            }
            setCards(feed);
            setHasMoreCards(round.hasMore);
            // Mark today as opened only now that a fresh round is actually in hand -
            // if the new-day refresh had failed/returned empty we'd fall through and a
            // reload would correctly retry the new-day path instead of resuming stale.
            if (isNewDay) {
              try {
                localStorage.setItem("insnip.lastDay", JSON.stringify(localDateKey()));
              } catch {
                /* ignore */
              }
            }
            // Always open on the freshest card (a shared deep link puts that card at
            // feed[0]). New day resets the round counter; same-day reloads keep it.
            const rnd = isNewDay ? 1 : Math.max(readDayRound(), 1);
            setDayRound(rnd);
            writeDayRound(rnd);
            // A shared or pushed card leads no matter what the lens does to the rest; otherwise
            // the round opens on the first card the reader will SEE under their lens.
            const landing = sharedLanding ? feed[0] : (landOn(feed) ?? feed[0]);
            setActiveId(landing.id);
            setViewedIds([landing.id]);
            // Consume the landing card in the never-repeat log too (every card reached via
            // visitCard is marked seen; the landing card was the ONE exception, so on a reload
            // it stayed the newest-unseen and re-led as card 1 while the rest of the round moved
            // on, "the first card never changes but the others do"). Marking it seen means a
            // reload leads with genuinely fresh content.
            if (serverDrivenRef.current) void markSeen([landing.id]);
            setStatus("ready");
            // The round path returns here, which is why the old swipe hint never ran on the live
            // site: its gate sat below, on the fallback path only. The staged lesson starts on
            // both (USER 2026-10-30).
            if (isPhoneWeb() && readStored<number>("insnip.lessonV", 0) < 1) setLesson(0);
            { const nextStreak = updateStreak(); setStreak(nextStreak); celebrateStreak(nextStreak.count); }
            // First-run intro is DELAYED, deliberately, and does not open here. Both the v4
            // brand-first onboarding and the gesture tour used to open on first paint, so a brand
            // new reader met an overlay before a single sentence of journalism. The trigger now
            // lives in the effect below: it arms on the first card and opens TOUR_DWELL_MS later,
            // so the first headline is read first. It still runs onboarding before the tour, so
            // v4's brand moment is kept, just moved a few seconds past the arrival.
            return;
          }
        }

        const ordered = personalizeFeed([...(cardsData as InSnipCard[])], weights);
        setCards(ordered);

        // Resume where the reader left off: land on the first card they haven't
        // seen yet (not card 1), skipping cards already read across refreshes.
        // Prune the seen-set to the current feed so it can't grow unbounded or
        // hold stale ids from older feeds.
        const feedIds = ordered.map((card) => card.id);
        const feedIdSet = new Set(feedIds);
        const prunedSeen = new Set([...seenRef.current].filter((id) => feedIdSet.has(id)));
        seenRef.current = prunedSeen;
        // A shared deep link (#card=ID) opens that exact card; otherwise resume.
        const linkedId =
          typeof window !== "undefined"
            ? parseCardLink(window.location.hash || window.location.search)
            : null;
        const linkedCard = linkedId ? ordered.find((card) => card.id === linkedId) : null;
        const startIdx = linkedCard
          ? ordered.findIndex((card) => card.id === linkedCard.id)
          : resumeIndex(feedIds, prunedSeen);
        const startCard = ordered[startIdx] ?? ordered[0] ?? null;
        // Seed the round-scoped viewed list so the after-10th analysis still
        // triggers correctly when resuming partway through a round.
        const seededViewed = startCard
          ? seedViewedForResume(feedIds, startIdx, prunedSeen)
          : [];
        if (startCard) {
          prunedSeen.add(startCard.id);
          localStorage.setItem("insnip.seen", JSON.stringify([...prunedSeen]));
        }
        setSeenCount(prunedSeen.size);
        setActiveId(startCard?.id ?? null);
        setViewedIds(seededViewed);
        setStatus("ready");
        { const nextStreak = updateStreak(); setStreak(nextStreak); celebrateStreak(nextStreak.count); }
        // The tour and the v4 onboarding are DEFERRED to the effect below (they now wait until a
        // reader has read a few stories). What stays here is the swipe hint, which is the one
        // lesson that actually matters on card 1: that there IS a card 2. It is a brief,
        // non-blocking hint rather than an overlay, and it used to be suppressed precisely when a
        // first-time reader needed it most, because the tour outranked it in this chain.
        if (!insightDue && isPhoneWeb() && readStored<number>("insnip.lessonV", 0) < 1) {
          // A phone browser gets the staged lesson (see `lesson` above), keyed on its own flag
          // so a device that saw the old seven-second flash is taught properly once.
          setLesson(0);
        } else if (
          // Native app: the swipe lesson IS the whole onboarding, so key it on its own versioned
          // flag - existing installs that set the old web flag still get taught the gesture once.
          // Web keeps the original once-ever flag.
          !insightDue &&
          (isNativeApp
            ? readStored<number>("insnip.swipeHintV", 0) < 1
            : !readStored<boolean>("insnip.hintSeen", false))
        ) {
          setShowHint(true);
          window.setTimeout(() => {
            setShowHint(false);
            localStorage.setItem("insnip.hintSeen", JSON.stringify(true));
            localStorage.setItem("insnip.swipeHintV", "1");
          }, HINT_DURATION_MS);
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function checkDeploy() {
      try {
        if (!cancelled && (await hasFreshDeploy())) {
          setRefreshReady(true);
        }
      } catch {
        // Network checks are best-effort; reading should never be interrupted.
      }
    }
    const timer = window.setInterval(checkDeploy, DEPLOY_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  // Poll for newly-released cards so the "new snips available" pill can appear without a
  // manual refresh. Cheap COUNT; runs only when the tab is visible.
  useEffect(() => {
    if (!sessionApiEnabled()) return;
    let cancelled = false;
    const check = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const n = await fetchFreshCount(feedLoadedAtRef.current);
      if (!cancelled) setFreshCount(n);
    };
    void check();
    const timer = window.setInterval(check, 90_000);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("insnip.saved", JSON.stringify(savedIds));
  }, [savedIds]);

  useEffect(() => {
    localStorage.setItem("insnip.hidden", JSON.stringify(hiddenIds));
  }, [hiddenIds]);

  useEffect(() => {
    localStorage.setItem("insnip.theme", JSON.stringify(theme));
    document.documentElement.setAttribute("data-theme", resolveTheme(theme));
    // Auto follows the device live: flip with the OS, not on the next reload.
    if (theme !== "auto" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => document.documentElement.setAttribute("data-theme", resolveTheme("auto"));
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  // Load overture: once per browser session, the shell carries an "overture" class for
  // 1700ms so CSS can choreograph the first entrance. Subsequent mounts skip it.
  useEffect(() => {
    if (isNativeApp) return; // native app: no entrance choreography, open straight to the cards
    try {
      if (sessionStorage.getItem("insnip.overture")) return;
      sessionStorage.setItem("insnip.overture", "1");
    } catch {
      return; // storage unavailable: skip the choreography rather than replay it forever
    }
    setOverture(true);
    const t = window.setTimeout(() => setOverture(false), 1700);
    return () => window.clearTimeout(t);
  }, []);

  // THE EDITION OPEN. The overture above tunes the stagger, but the day still started
  // cold on a random story with every rail already talking (the audit's first WOW gap:
  // no threshold, no curtain-up). Once per LOCAL DAY the reader gets a 1.6s title card,
  // masthead, date, the finite promise, then the curtain parts onto the round. A daily
  // ritual, not a splash screen: deep links skip it (a shared story must open instantly),
  // reduced motion skips it, the native app skips it (it has its own arrival), and it is
  // pointer-events: none throughout so it can never trap a click.
  const [curtain, setCurtain] = useState(false);
  useEffect(() => {
    if (isNativeApp) return;
    // Only for a reader who is actually THROUGH the door. Screenshot-verified: for an
    // anonymous visitor the sign-in wall stacks above this layer, so playing now would
    // silently burn the day's one curtain behind a gate nobody can see through.
    if (firebaseAuthed !== true) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (/card=/.test(window.location.hash) || arrivedOnAStory()) return; // arriving AT a story: no ceremony in the way
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (localStorage.getItem("insnip.editionOpenDay") === today) return;
      localStorage.setItem("insnip.editionOpenDay", today);
    } catch {
      return;
    }
    setCurtain(true);
    const t = window.setTimeout(() => setCurtain(false), 1900);
    return () => window.clearTimeout(t);
  }, [firebaseAuthed]);

  // Native app reader geometry is pure CSS (styles.css "NATIVE APP layout fixes"): the
  // .reader-frame is a position:fixed box whose top/bottom are derived from the same
  // safe-area vars that place the header and the dock, so no JS measurement is needed (an
  // earlier JS refinement layer failed to apply on-device and left stale fallback gaps).

  // Catch-me-up: remember when the reader last visited, and if they were away more than
  // three days offer the Brief as the fastest way back in (dismissable once per day).
  useEffect(() => {
    try {
      const prev = Number(localStorage.getItem("insnip.lastVisit")) || 0;
      localStorage.setItem("insnip.lastVisit", String(Date.now()));
      if (prev > 0) setAwayDays(Math.floor((Date.now() - prev) / 86_400_000));
      setCatchupDismissed(localStorage.getItem("insnip.catchupDismiss") === localDateKey());
    } catch {
      /* best-effort, the banner is a nicety */
    }
  }, []);

  // Keep the native status bar (Capacitor) painted like the app: re-sync on theme change,
  // and at most once a second as the ambient color shifts. Pure no-op on the web.
  useEffect(() => {
    syncStatusBar(THEME_BG[resolveTheme(theme)]);
  }, [theme]);
  useEffect(() => {
    const now = Date.now();
    if (now - barSyncAtRef.current < 1000) return;
    barSyncAtRef.current = now;
    syncStatusBar(THEME_BG[resolveTheme(theme)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambient]);

  // Round flare: when the round-complete summary lands, the gold progressline celebrates
  // for 1600ms ("round-complete" class, CSS animates it).
  useEffect(() => {
    if (!showInsight) return;
    setRoundFlare(true);
    const t = window.setTimeout(() => setRoundFlare(false), 1600);
    return () => window.clearTimeout(t);
  }, [showInsight]);

  // Quote pill: selecting text inside the card body/essence floats a "Share quote" pill
  // above the selection (mouse + touch, 150ms settle). Scroll, collapse or drag hides it.
  useEffect(() => {
    dragXRef.current = dragX;
    if (dragX) setQuotePill(null); // never fight the swipe gesture
  }, [dragX]);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.getSelection !== "function") return;
    const evaluate = () => {
      try {
        if (dragXRef.current) { setQuotePill(null); return; }
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setQuotePill(null); return; }
        const text = sel.toString().replace(/\s+/g, " ").trim();
        if (text.length < 8 || text.length > 280) { setQuotePill(null); return; }
        const anchor = sel.anchorNode;
        const el = anchor && (anchor.nodeType === 1 ? (anchor as Element) : anchor.parentElement);
        if (!el || !el.closest(".essence, .full-read, .body-points")) { setQuotePill(null); return; }
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        if (!rect || (!rect.width && !rect.height)) { setQuotePill(null); return; }
        const x = Math.min(Math.max(rect.left + rect.width / 2, 72), window.innerWidth - 72);
        const y = Math.max(rect.top - 10, 54);
        setQuotePill({ x, y, text });
      } catch {
        setQuotePill(null);
      }
    };
    const schedule = () => {
      if (quoteTimerRef.current) window.clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = window.setTimeout(evaluate, 150);
    };
    const hide = () => setQuotePill(null);
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) hide();
    };
    document.addEventListener("mouseup", schedule);
    document.addEventListener("touchend", schedule);
    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", hide, true);
    return () => {
      if (quoteTimerRef.current) window.clearTimeout(quoteTimerRef.current);
      document.removeEventListener("mouseup", schedule);
      document.removeEventListener("touchend", schedule);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", hide, true);
    };
  }, []);

  // Birthday celebration: once a year, on the reader's birthday. Fires after the first-run tour
  // (so new readers learn the app first) and renders only when nothing else is open (see below).
  useEffect(() => {
    if (!dob) return;
    const now = new Date();
    if (!isBirthdayToday(dob, now)) return;
    if (readStored<number>("insnip.bdayYear", 0) === now.getFullYear()) return;
    if (shouldShowTour(readStored<number>("insnip.tourV", 0))) return;
    setBirthdayCelebration(true);
    setBirthdayOpen(true);
    localStorage.setItem("insnip.bdayYear", JSON.stringify(now.getFullYear()));
  }, [dob]);

  useEffect(() => {
    saveDisplay(display);
    applyDisplay(document.documentElement, display);
  }, [display]);

  useEffect(() => {
    localStorage.setItem("insnip.lens", JSON.stringify(lens));
  }, [lens]);
  useEffect(() => {
    localStorage.setItem("insnip.measure", JSON.stringify(measure));
    document.documentElement.setAttribute("data-measure", measure);
  }, [measure]);
  useEffect(() => {
    localStorage.setItem("insnip.goal", JSON.stringify(dailyGoal));
  }, [dailyGoal]);

  // Notification landing: a reader arriving from a push (ref=n) records an "open", this
  // powers adaptive frequency + open-rate. A quiz push (#quiz) also opens the quiz.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const src = (window.location.hash || window.location.search).replace(/^[#?]/, "&");
    if (/(?:^|&)ref=n(?:&|$)/.test(src)) {
      sendEvent({ type: "notif_open", cardId: parseCardLink(window.location.hash || window.location.search) ?? undefined });
    }
    if (/(?:^|&)quiz(?:&|$)/.test(src)) void openQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native push TAP (fired by native.ts routePushTap): open the exact card/surface the push points
  // at, even while the app is already running. Cards are fetched if not in the current deck, and a
  // push's `next=` story chain is queued right behind the tapped story, so a notification always
  // lands the reader on its story and flows them into the next hot one, never the default screen.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onPushNav(e: Event) {
      const detail = (e as CustomEvent).detail ?? {};
      const kind: string = detail.kind || "";
      // Log the open FIRST. It used to sit below the game/quiz/brief branches and below the
      // "no card id" return, so a tap on any push that was not card-shaped recorded nothing.
      // That made push look ignored (6 opens in 30 days) when it was mostly unmeasured, and the
      // adaptive step-down then muted readers for a silence they had not chosen.
      const srcHash = typeof detail.hash === "string" && detail.hash ? `#${detail.hash}` : String(detail.url || "");
      const tappedId: string = detail.cardId || parseCardLink(srcHash) || "";
      if (detail.from !== "home") sendEvent({ type: "notif_open", cardId: tappedId || undefined, tag: kind || "card" });
      if (kind === "game") { setGameOpen(true); return; }
      if (kind === "quiz") { void openQuiz(); return; }
      if (kind === "brief") { openBrief(); return; }
      const src = srcHash;
      const id: string = tappedId;
      if (!id) return;
      const chainIds = parseNextChain(src).filter((cid) => cid !== id);
      // The front page hands over the stories it already holds, so nothing is fetched twice.
      const seeded: InSnipCard[] = Array.isArray(detail.cards) ? (detail.cards as InSnipCard[]) : [];
      void (async () => {
        const fetched = await Promise.all(
          [id, ...chainIds].map(
            async (cid) => cards.find((c) => c.id === cid) ?? seeded.find((c) => c.id === cid) ?? (await fetchCard(cid).catch(() => null)),
          ),
        );
        const lead = fetched.filter((c): c is InSnipCard => !!c);
        // The tapped story must lead; if its fetch failed, never land the reader elsewhere.
        if (!lead.length || lead[0].id !== id) return;
        const leadIds = new Set(lead.map((c) => c.id));
        setCards((cur) => [...lead, ...cur.filter((c) => !leadIds.has(c.id))]);
        setActiveId(id);
        setViewedIds((v) => (v.includes(id) ? v : [...v, id]));
        visitCard(id);
        // "Listen to this edition": the briefing reads from this story on and turns the pages.
        if (detail.listen && !briefingSession) toggleBriefing();
      })();
    }
    window.addEventListener("insnip:pushnav", onPushNav);
    return () => window.removeEventListener("insnip:pushnav", onPushNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, briefingSession]);

  useEffect(() => {
    localStorage.setItem("insnip.interests", JSON.stringify(interests));
  }, [interests]);

  useEffect(() => {
    localStorage.setItem("insnip.affinity", JSON.stringify(affinity));
  }, [affinity]);

  useEffect(() => {
    localStorage.setItem("insnip.bionic", JSON.stringify(bionicMode));
  }, [bionicMode]);

  useEffect(() => {
    localStorage.setItem("insnip.topicSignals", JSON.stringify(topicSignals));
  }, [topicSignals]);

  // Persist "Make It Yours" topic prefs and keep the round-loading ref in sync.
  useEffect(() => {
    writeTopicPrefs(topicPrefs);
    topicListsRef.current = topicPrefsToLists(topicPrefs);
  }, [topicPrefs]);

  // Remember the exact card the reader is on (even after going back) so reopening
  // resumes there, not at the furthest-seen card, locally and (server-driven)
  // on the server for cross-device resume.
  useEffect(() => {
    if (!activeId) return;
    localStorage.setItem("insnip.activeId", activeId);
    if (serverDrivenRef.current) {
      const idx = cards.findIndex((c) => c.id === activeId);
      if (idx >= 0) void updatePosition(idx);
    }
  }, [activeId, cards]);

  // Collective "trending now" across readers (best-effort; the panel hides if empty).
  useEffect(() => {
    let cancelled = false;
    fetchTrending()
      .then((cats) => { if (!cancelled) setTrending(cats); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Daily streak as server truth (cross-device), web-push token refresh, and a
  // foreground toast if a push lands while the tab is focused. All best-effort.
  useEffect(() => {
    let cancelled = false;
    void pingStreak().then((s) => {
      if (cancelled || !s) return;
      setStreak({ count: s.current, lastDate: isoDay(new Date()) });
      if (typeof s.freezeTokens === "number") setPerks((prev) => ({ ...prev, freezeTokens: s.freezeTokens as number }));
      if (s.freezeUsed) {
        // A freeze quietly bridged a missed day: a calm, brief note (reuses the milestone toast).
        setMilestone(`A freeze saved your ${s.current}-day streak.`);
        window.setTimeout(() => setMilestone(null), 5200);
      } else if (s.counted && s.milestone) {
        celebrateStreak(s.milestone);
      }
    });
    // Reward balances (freezes + rewarded referrals), best-effort, drives the invite + freeze UI.
    void getPerks().then((p) => { if (!cancelled) setPerks((prev) => ({ ...prev, ...p })); });
    // "Signal or Noise": load today's set (best-effort) + prefill any stored result for today,
    // so the rail card can show the score without opening the game. Local day matches streak.
    const sonDay = new Date().toLocaleDateString("en-CA");
    void fetchGameToday(sonDay).then((g) => { if (!cancelled) { setGameToday(g); if (!g) setGameFetchMissed(true); } });
    try {
      const stored = localStorage.getItem(`insnip.son.${sonDay}`);
      if (stored) setGameResult(JSON.parse(stored) as GameResult);
    } catch { /* ignore, the rail card just starts empty */ }
    void pushSupported().then((ok) => {
      if (cancelled) return;
      if (!ok) { setPushState("unsupported"); return; }
      setPushState(pushEnabledLocally() ? "on" : "off");
      void refreshWebPushToken();
    });
    void onForegroundPush((title, body) => {
      setMilestone(`🔔 ${title}${body ? `, ${body}` : ""}`);
      window.setTimeout(() => setMilestone(null), 5200);
    });
    return () => { cancelled = true; };
  }, []);

  function jumpToCategory(category: string) {
    const target =
      cards.find((card) => card.category === category && !seenRef.current.has(card.id)) ??
      cards.find((card) => card.category === category);
    if (!target) return;
    setActiveTag("All");
    setShowSavedOnly(false);
    setSearchQuery("");
    visitCard(target.id);
    haptic(6);
  }

  function tuneTopic(category: string | undefined, delta: number, cardId?: string) {
    if (!category) return;
    setTopicSignals((current) => bumpTopic(current, category, delta));
    // Record the explicit topic feedback server-side (cross-device sync + admin insight).
    // The local signal map still steers this session's ordering immediately.
    sendEvent({ type: delta > 0 ? "more" : "less", cardId, category });
    haptic(8);
  }

  // "Not interested": strongest negative feedback. Record it, never serve this card
  // again (the cross-device seen log), and drop it from view this session.
  function hideCard(cardId: string) {
    const card = cards.find((c) => c.id === cardId);
    sendEvent({ type: "hide", cardId, category: card?.category });
    void markSeen([cardId], "hide");
    setHiddenIds((current) => (current.includes(cardId) ? current : [...current, cardId]));
    haptic(18);
  }

  // "Make It Yours": toggle a category between follow / mute / neutral. Follow
  // and mute are mutually exclusive per category; tapping the active state clears it.
  function toggleTopicPref(category: string, pref: TopicPref) {
    haptic(8);
    setTopicPrefs((current) => {
      const next = { ...current };
      if (next[category] === pref) delete next[category];
      else next[category] = pref;
      return next;
    });
  }

  // Save the panel: persist (handled by effect) and reload the feed with the new
  // prefs so the change takes effect immediately. A subtle toast confirms it.
  function applyTopicPrefs() {
    setTopicsOpen(false);
    // Sync the ref now so loadServerRound reads the latest before the effect runs.
    topicListsRef.current = topicPrefsToLists(topicPrefs);
    const lists = topicListsRef.current;
    const tunedCount = (lists.preferred?.length ?? 0) + (lists.muted?.length ?? 0);
    setTopicsToast(tunedCount ? "Feed tuned to your topics" : "Topic preferences cleared");
    window.setTimeout(() => setTopicsToast(null), 2600);
    if (serverDrivenRef.current) {
      void loadServerRound("refresh");
    } else if (typeof window !== "undefined") {
      // Bundled-fallback mode: re-order the local feed to favor followed topics
      // and drop muted ones (mirrors the server muted-exclusion).
      changeTag("All");
    }
  }

  // Explicit Like/Dislike verdict per card, a clean signal distinct from the 5
  // emojis. Tapping the active verdict again clears it.
  function setVerdict(cardId: string, v: "up" | "down") {
    setVerdicts((prev) => {
      const next = { ...prev };
      const cleared = next[cardId] === v;
      if (cleared) delete next[cardId];
      else next[cardId] = v;
      try { localStorage.setItem("insnip.verdicts", JSON.stringify(next)); } catch { /* ignore */ }
      if (!cleared) {
        const card = cards.find((c) => c.id === cardId);
        // A verdict is explicit preference: route up/down to more/less so a dislike
        // *buries* the category instead of (as a generic reaction) boosting it.
        sendEvent({ type: v === "up" ? "more" : "less", cardId, category: card?.category, tag: v === "up" ? "like" : "dislike" });
      }
      return next;
    });
    haptic(v === "up" ? [12, 30, 12] : 18);
  }

  // Double-tap a card -> Like, with a heart burst (Instagram/Inshorts feel).
  function likeWithHeart(cardId: string) {
    if (verdicts[cardId] !== "up") setVerdict(cardId, "up");
    const now = Date.now();
    setHeartKey(now);
    haptic([10, 30, 10]);
    window.setTimeout(() => setHeartKey((k) => (k === now ? 0 : k)), 900);
  }

  // Double-tap the hero image -> Like + heart burst.
  function onImageTap() {
    const now = Date.now();
    if (now - lastImgTapRef.current < 350) {
      lastImgTapRef.current = 0;
      if (activeCard) likeWithHeart(activeCard.id);
    } else {
      lastImgTapRef.current = now;
    }
  }

  // Card-copy tap: a SINGLE tap expands/collapses (after a short delay to let a
  // second tap register), a DOUBLE tap Likes with a heart, so double-tap no longer
  // just toggles the content open then shut.
  function onCopyTap(event: React.MouseEvent) {
    const t = event.target as HTMLElement;
    if (t.closest("button, a, input, textarea, .reaction-row, .verdict-row, .command-bar, .sources-btn, .share-btn, .meta-pills")) {
      return;
    }
    const now = Date.now();
    if (now - lastCopyTapRef.current < 300) {
      lastCopyTapRef.current = 0;
      if (copyTapTimerRef.current) {
        window.clearTimeout(copyTapTimerRef.current);
        copyTapTimerRef.current = null;
      }
      if (activeCard) likeWithHeart(activeCard.id);
      return;
    }
    lastCopyTapRef.current = now;
    if (copyTapTimerRef.current) window.clearTimeout(copyTapTimerRef.current);
    copyTapTimerRef.current = window.setTimeout(() => {
      copyTapTimerRef.current = null;
      // Single tap no longer toggles a reading view (the Full / Key points control does that now);
      // the timer is kept only so a double tap (like) can cancel it within the 300ms window.
    }, 300);
  }

  useEffect(() => {
    document.documentElement.toggleAttribute("data-focus", focusMode);
    return () => document.documentElement.removeAttribute("data-focus");
  }, [focusMode]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const categories = useMemo(() => {
    const unique = new Set(cards.map((card) => card.category));
    return ["All", ...Array.from(unique).sort()];
  }, [cards]);

  const dailyStats = useMemo(() => {
    const stories = cards.length;
    const totalSeconds = cards.reduce((sum, card) => {
      const words = `${card.headline} ${card.essence} ${card.body} ${card.whyItMatters}`.trim().split(/\s+/).length;
      return sum + Math.max(20, Math.round((words / WORDS_PER_MINUTE) * 60));
    }, 0);
    const minutes = Math.max(1, Math.round(totalSeconds / 60));
    const domains = new Set<string>();
    cards.forEach((card) => {
      const primary = domainOf(card.primarySourceUrl);
      if (primary) domains.add(primary);
      card.supportingSources.forEach((src) => {
        const d = domainOf(src.url);
        if (d) domains.add(d);
      });
    });
    return { stories, minutes, sources: domains.size };
  }, [cards]);

  // How many of today's loaded stories the reader has opened, so the Today panel can show real
  // progress through a FINITE day (the anti-doomscroll promise made tangible) rather than a
  // static vanity count. Round-scoped, like viewedIds.
  const readToday = useMemo(
    () => cards.reduce((n, c) => (viewedIds.includes(c.id) ? n + 1 : n), 0),
    [cards, viewedIds],
  );
  // Odometer numbers: the visible counters roll to their new value (the real values
  // stay in the aria labels, the roll is presentation only).
  const animReadToday = useAnimatedNumber(readToday);
  // "The Snip": a one-time gold underline that draws under a card's insight the moment the
  // card is registered as read. snipCardId marks which card is lit; snipKey remounts the
  // sweep element so the animation replays. snippedRef guards it to once per card per session.
  const [snipCardId, setSnipCardId] = useState<string | null>(null);
  const [snipKey, setSnipKey] = useState(0);
  const snippedRef = useRef<Set<string>>(new Set());

  // Reading DNA: the shape of the day so far, one bar per category the reader has actually
  // opened. Powers the Insights hub; empty until the first story is read.
  const readingDna = useMemo(() => {
    const viewed = new Set(viewedIds);
    const counts = new Map<string, number>();
    cards.forEach((c) => {
      if (viewed.has(c.id) && c.category) counts.set(c.category, (counts.get(c.category) ?? 0) + 1);
    });
    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    return [...counts.entries()]
      .map(([category, count]) => ({ category, count, pct: Math.max(10, Math.round((count / Math.max(total, 1)) * 100)) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [cards, viewedIds]);

  // "Today's Essential": the single shared talking point of the day. Among the loaded cards
  // published today (reader's local calendar day), pick the highest insightScore, newest wins
  // ties. Returns null when nothing is from today (then no ribbon and no row render).
  const todaysEssentialId = useMemo(() => {
    const now = new Date();
    const isToday = (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return false;
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    };
    let best: InSnipCard | null = null;
    for (const card of cards) {
      if (!isToday(card.publishedAt)) continue;
      if (!best) {
        best = card;
        continue;
      }
      const bScore = best.insightScore ?? 0;
      const cScore = card.insightScore ?? 0;
      if (cScore > bScore || (cScore === bScore && card.publishedAt > best.publishedAt)) {
        best = card;
      }
    }
    return best?.id ?? null;
  }, [cards]);

  const todaysEssential = useMemo(
    () => cards.find((card) => card.id === todaysEssentialId) ?? null,
    [cards, todaysEssentialId],
  );

  // Debounced server-side semantic search (#2): query /search (semantic + keyword,
  // never empty) so a term that isn't in the current round still surfaces the closest
  // cards from the whole pool. Falls back to local substring filtering in bundled mode.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2 || !sessionApiEnabled()) {
      setSearchResults(null);
      setSearchWeak(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        const res = await searchCards(q);
        if (cancelled) return;
        setSearchResults(res.cards);
        setSearchWeak(res.weak);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [searchQuery]);

  const openSearch = useCallback(() => {
    if (!member) { setGateFeature("search"); return; }
    setSearchDetail(null);
    setSearchOpen(true);
  }, [member]);

  /** Search for a term from somewhere else in the app (a topic chip, a category pill).
   *  It goes through the same door as the search button, because a side entrance that skips
   *  the gate is not a shortcut, it is a hole. */
  const openSearchFor = useCallback((q: string) => {
    if (!member) { setGateFeature("search"); return; }
    setSearchDetail(null);
    setSearchOpen(true);
    setSearchQuery(q);
  }, [member]);
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchDetail(null);
    setSearchQuery("");
  }, []);

  // Firebase Auth (#8): resolve sign-in state once, then keep it in sync. On a brand-new
  // sign-in we link the account (merging this device's seen-log + streak) and reload so
  // the whole app re-keys to the account uid; on sign-out we revert to the device id.
  useEffect(() => {
    if (!sessionApiEnabled()) {
      setAccount({ signedIn: false });
      return;
    }
    let cancelled = false;
    void completeRedirectSignIn();
    const unsub = onAuthChange(async (user) => {
      if (cancelled) return;
      if (user) {
        if (accountUid() !== user.uid) {
          try {
            const token = await user.getIdToken();
            const res = token ? await linkAccount(token) : null;
            // Web: a full reload re-seeds the feed for the signed-in identity. Native WKWebView:
            // a reload can DROP the just-created auth session and bounce the user back to the
            // sign-in gate (the "stuck after entering the code" bug), so we update in place there.
            if (res && !isNativeApp) {
              window.location.reload();
              return;
            }
          } catch {
            /* fall back to anonymous */
          }
        }
        setFirebaseAuthed(true);
        setAccount(await fetchAccount());
        // Signed in: always dismiss the sign-in sheet / feature gate so the reader is never
        // left stuck on it (independent of whether the worker link or a reload succeeded).
        setGateFeature(null);
        setSignInOpen(false);
      } else {
        if (isSignedIn()) {
          unlinkAccount();
          window.location.reload();
          return;
        }
        setFirebaseAuthed(false);
        setAccount({ signedIn: false });
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const handleGoogleSignIn = useCallback(async () => {
    setAuthError("");
    setAuthBusy(true);
    try {
      await signInWithGoogle(); // onAuthChange handles link + reload (or redirect returns)
    } catch (err) {
      const code = (err as { code?: string })?.code ?? "";
      setAuthError(code ? `Sign-in failed (${code}).` : "Google sign-in failed. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }, []);

  const handleAppleSignIn = useCallback(async () => {
    setAuthError("");
    setAuthBusy(true);
    try {
      await signInWithApple(); // onAuthChange handles link + reload (or redirect returns)
    } catch (err) {
      const code = (err as { code?: string })?.code ?? "";
      setAuthError(code ? `Sign-in failed (${code}).` : "Apple sign-in failed. Please try again.");
    } finally {
      setAuthBusy(false);
    }
  }, []);

  // Email step 1: validate locally, then ask the Worker to email a 5-digit code.
  const handleRequestEmailCode = useCallback(async () => {
    const email = signInEmail.trim();
    // Three different problems get three different sentences: nothing typed, a typo, and an
    // address the worker will not deliver to. Only a failure of ours says "try again".
    if (!email) { setAuthError("Enter your email."); return; }
    if (emailSyntaxError(email)) { setAuthError("That doesn't look like an email address."); return; }
    setAuthError("");
    setAuthBusy(true);
    try {
      await requestEmailCode(email);
      setEmailStep("code");
      setEmailCode("");
    } catch (err) {
      const msg = err instanceof EmailAuthError ? err.message : "";
      setAuthError(/network|connection/i.test(msg) ? msg : /check the address|deliver|domain|disposable|invalid|valid email/i.test(msg) ? "We can't deliver to that address. Try another." : "Couldn't send the code. Try again.");
    } finally {
      setAuthBusy(false);
    }
  }, [signInEmail]);

  // Email step 2: exchange the code for a Firebase sign-in.
  const handleVerifyEmailCode = useCallback(async () => {
    const code = emailCode.trim();
    if (!/^\d{5}$/.test(code)) {
      setAuthError("Enter the 5-digit code from your email.");
      return;
    }
    setAuthError("");
    setAuthBusy(true);
    try {
      await verifyEmailCode(signInEmail.trim(), code); // onAuthChange handles the reload
    } catch (err) {
      setAuthError(err instanceof EmailAuthError ? err.message : "Couldn't verify the code. Try again.");
    } finally {
      setAuthBusy(false);
    }
  }, [signInEmail, emailCode]);

  const handleSignOut = useCallback(async () => {
    setAuthBusy(true);
    await signOutUser(); // onAuthChange(null) handles unlink + reload
  }, []);

  // Permanent account deletion, initiated and completed in-app (App Store 5.1.1(v)). Deletes the
  // Firebase account, drops the synced state stored on this device, then reloads as a fresh guest.
  // If Firebase needs a fresh login first ("reauth"), or anything fails, we hand off to the bundled
  // deletion-request page so the user always has a working path and is never stuck.
  const handleDeleteAccount = useCallback(async () => {
    if (!window.confirm("Delete your InSnip account and the data linked to it? This is permanent and cannot be undone.")) return;
    setAuthBusy(true);
    try {
      const res = await deleteAccount();
      if (res === "deleted") {
        try { unlinkAccount(); } catch { /* ignore */ }
        try {
          Object.keys(localStorage).filter((k) => k.startsWith("insnip.")).forEach((k) => localStorage.removeItem(k));
        } catch { /* ignore */ }
        window.location.reload();
        return;
      }
      window.location.href = "/delete-user.html";
    } catch {
      window.location.href = "/delete-user.html";
    } finally {
      setAuthBusy(false);
    }
  }, []);

  // Every "sign in" entry point opens the ONE shared modal. A feature gate passes its reason; a
  // plain sign-in (account panel) opens it with no reason.
  const openSignIn = useCallback(() => { setAuthError(""); setEmailStep("form"); setEmailCode(""); setSignInOpen(true); }, []);
  // "/read#signin" and "/read#saved" (the home's You panel): do the one thing asked on arrival.
  // Noted once at mount, so the front page's first-visit greeting never lands on top of it.
  const arrivalAskRef = useRef<"signin" | "saved" | null>(
    typeof window === "undefined" ? null
      : /(?:^|[#&?])signin(?:[&=]|$)/.test(window.location.hash) ? "signin"
      : /(?:^|[#&?])saved(?:[&=]|$)/.test(window.location.hash) ? "saved"
      : null,
  );
  useEffect(() => {
    if (arrivalAskRef.current !== "signin") return;
    try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* fine */ }
    openSignIn();
  }, [openSignIn]);
  // MANDATORY SIGN-IN (product decision, 2026-08-09): the reader is members only, on the web
  // exactly as in the app. The wall keys on FIREBASE's answer, not the worker's account
  // summary, deliberately: fetchAccount falls back to { signedIn: false } when the worker
  // call fails, so keying on it would slam an uncloseable wall on a validly signed-in reader
  // during any worker hiccup. Firebase is the identity authority; the worker only enriches.
  // Null means "not answered yet", so nothing flashes during startup, and Firebase fires its
  // first callback only after restoring any persisted session. The server-rendered marketing
  // surfaces (SEO pages, /c/[id] share pages, /play) are deliberately not behind this: they
  // are the ads, and gating them would delist the acquisition machine.
  // THE FIRST DOOR IS NO LONGER LOCKED (2026-08-25, USER decision, ahead of a public launch).
  // A stranger arriving from a link now gets today's stories; the sign-in card still greets
  // them, but it carries a quiet "Skip for now" and reading starts on the other side of it.
  // The MEMBER features (Deep dive, the Brief, Search, Ask, saving, streaks) keep their own
  // gates exactly as before, so an account still means something. The choice is remembered:
  // re-walling a reader who already declined once is asking the same question twice.
  // THE FIRST DOOR IS SKIPPABLE ON THE WEB ONLY.
  // In the app, sign-in is MANDATORY by an explicit product decision the operator has already
  // made and asked not to have re-litigated. That decision lives in one place: a shipped app
  // build that carried this button would reverse it silently, and nobody would notice until a
  // reader was already reading without an account. The web needed the skip because 78% of
  // readers read exactly one card and an install-and-sign-in before the first word is the
  // bottleneck; someone who has ALREADY installed the app has answered that question.
  const canSkipWall = canSkipFirstDoor(isNativeApp);
  const [wallSkipped, setWallSkipped] = useState<boolean>(() => readStored<boolean>(WALL_SKIP_KEY, false));
  const skipWall = useCallback(() => {
    setWallSkipped(true);
    try {
      localStorage.setItem(WALL_SKIP_KEY, JSON.stringify(true));
    } catch {
      /* a full or blocked store just means the card greets them again next visit */
    }
    sendEvent({ type: "wall_skip" });
  }, []);
  // Stories BEHIND the reader. The card currently open is deliberately not counted: it is
  // being read, not finished, and counting it would put the invitation on top of the third
  // story instead of after it. Persisted, so a refresh neither spends nor resets the run.
  const storiesFinished = Math.max(0, seenCount - (activeId && seenRef.current.has(activeId) ? 1 : 0));
  const authWall = showsFirstDoor(firebaseAuthed, wallSkipped, isNativeApp, storiesFinished);
  tourBlockedRef.current = signInOpen || authWall || !!gateFeature || phoneDoorOpen;
  // And if a door rises WHILE the tour is running (the sign-in invitation is the one that did),
  // the tour steps aside and comes back on its own once the screen is clear. The deck position
  // it captured stays put: the next opening captures it again. Two sheets never share a screen.
  const tourBlocked = tourBlockedRef.current;
  useEffect(() => {
    if (!tourOpen || !tourBlocked) return;
    setTourOpen(false);
    openTourWhenClear();
  }, [tourOpen, tourBlocked, openTourWhenClear]);
  // ONE CARD, TWO MOMENTS, and they must not borrow each other's words. The web reader meets
  // it having finished stories, so it can name them. The app reader meets it on the first
  // frame with nothing behind them, so it cannot. `wallEarned` is which of the two is true.
  const wallEarned = authWall && storiesFinished >= INVITE_AFTER_CARDS;
  // WHAT THE INVITATION SHOWS is the reader's OWN last few stories, not a tease of unread
  // ones. Arriving after three finished reads, a list of things they have not seen is an
  // advertisement; a list of what they just did is a receipt, and the account is the offer
  // to keep it. Newest first, which is the order they were read in.
  const wallRead = useMemo(() => {
    if (!authWall) return [] as InSnipCard[];
    return cards.filter((c) => c.id !== activeId && seenRef.current.has(c.id)).slice(-3).reverse();
    // seenRef is a ref, so seenCount is the dependency that actually changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authWall, cards, activeId, seenCount]);
  // Fallback only: a reader whose finished stories are not in the current deck (a deep link,
  // a pruned resume) still sees real stories. Fetched only when it is going to be used.
  const [wallTease, setWallTease] = useState<LatestTeaser[]>([]);
  useEffect(() => {
    if (!authWall || wallTease.length || wallRead.length >= 2) return;
    let cancelled = false;
    void fetchLatestTeasers(3).then((t) => { if (!cancelled) setWallTease(t); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authWall, wallRead.length]);
  const closeSignIn = useCallback(() => { setGateFeature(null); setSignInOpen(false); }, []);

  // PWA install availability (the beforeinstallprompt event can arrive after mount).
  useEffect(() => {
    const update = () => setInstallReady(canPromptInstall() || isIOS());
    update();
    return onInstallChange(update);
  }, []);

  // Load recent daily Briefs (its own per-day feature, opened from a dedicated panel).
  useEffect(() => {
    if (!sessionApiEnabled()) return;
    let cancelled = false;
    void fetchBriefs(14).then((b) => {
      if (!cancelled) setBriefs(b);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Local weather for the open brief's day ("how the day looks where you are").
  useEffect(() => {
    const brief = briefOpen ? briefs[briefIndex] : null;
    if (!brief) return;
    let cancelled = false;
    setBriefWeather(null);
    void getBriefWeather(briefDayKey(brief)).then((w) => {
      if (!cancelled) setBriefWeather(w);
    });
    return () => {
      cancelled = true;
    };
  }, [briefOpen, briefIndex, briefs]);

  const dismissInstall = useCallback(() => {
    setInstallDismissed(true);
    try {
      localStorage.setItem("insnip.installDismissed", JSON.stringify(true));
    } catch {
      /* ignore */
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS()) return; // iOS has no prompt API; the banner shows manual steps
    setInstalling(true);
    const outcome = await promptInstall();
    setInstalling(false);
    if (outcome !== "dismissed") dismissInstall(); // accepted or unavailable: stop nudging
  }, [dismissInstall]);

  // The ONE sign-in form (Google + passwordless email link). Used only inside the single shared
  // sign-in modal below, so every entry point in the app leads to exactly the same sign-in UI.
  const appleMark = (
    <svg width="15" height="15" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM255.9 68.2c30.3-36 27.6-68.8 26.7-80.5-26.8 1.6-57.8 18.3-75.5 39.8-19.5 22.9-31 51.2-28.5 80 29 2.2 55.5-12.7 77.3-39.3z" />
    </svg>
  );
  const signInForm = emailStep === "code" ? (
    <>
      <p className="account-note">We emailed a 5-digit code to <strong>{signInEmail.trim()}</strong>. Enter it below.</p>
      <div className="search-wrap">
        <KeyRound size={14} aria-hidden="true" />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={5}
          placeholder="5-digit code"
          value={emailCode}
          onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
          onKeyDown={(e) => { if (e.key === "Enter") void handleVerifyEmailCode(); }}
          autoFocus
        />
      </div>
      <button type="button" className="account-btn primary" disabled={authBusy} onClick={handleVerifyEmailCode}>{authBusy ? "Verifying…" : "Verify and sign in"}</button>
      {authError ? <p className="account-error">{authError}</p> : null}
      <button type="button" className="account-btn ghost" disabled={authBusy} onClick={() => { setEmailStep("form"); setAuthError(""); setEmailCode(""); }}>Use a different email</button>
    </>
  ) : (
    <>
      <button type="button" className="account-btn primary" disabled={authBusy} onClick={handleGoogleSignIn}>Continue with Google</button>
      <button type="button" className="account-btn apple" disabled={authBusy} onClick={handleAppleSignIn}>{appleMark} Continue with Apple</button>
      <div className="account-or"><span>or with email</span></div>
      <div className="search-wrap">
        <Mail size={14} aria-hidden="true" />
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={signInEmail}
          onChange={(e) => setSignInEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void handleRequestEmailCode(); }}
        />
      </div>
      <button type="button" className="account-btn ghost" disabled={authBusy} onClick={handleRequestEmailCode}>{authBusy ? "Sending…" : "Email me a code"}</button>
      {authError ? <p className="account-error">{authError}</p> : null}
    </>
  );

  // Invite -> climb the referral ladder. Works signed-in or anonymous (the link carries this
  // reader's id), so it lives in both account states. Live counters come from getPerks
  // (best-effort). The ladder tiers are derived locally from the rewarded-referral count.
  const friendsJoined = Math.max(referralCount, perks.referralsRewarded);
  const ladder = computeReferralLadder(friendsJoined);
  // Collapsed by default: one calm row in the menu; the full ladder is a tap away.
  const inviteBlock = (
    <div className={`invite-box${inviteOpen ? " open" : ""}`}>
      <button
        type="button"
        className="invite-row"
        aria-expanded={inviteOpen}
        onClick={() => setInviteOpen((c) => !c)}
      >
        <span className="invite-row-title">Invite friends</span>
        <span className="invite-row-hint">
          {friendsJoined > 0 ? `${friendsJoined} joined` : "earn perks"}
        </span>
        <ChevronDown size={14} aria-hidden="true" className="invite-chev" />
      </button>
      {inviteOpen ? (<>
      <p className="invite-sub">Share InSnip. Every friend who joins unlocks a freeze, then a badge.</p>
      <div className="invite-metrics">
        <span className="invite-metric"><strong>{friendsJoined}</strong> {friendsJoined === 1 ? "friend joined" : "friends joined"}</span>
        <span className="invite-metric"><Snowflake size={12} aria-hidden="true" /> <strong>{perks.freezeTokens}</strong> {perks.freezeTokens === 1 ? "freeze earned" : "freezes earned"}</span>
      </div>
      <div className="ladder">
        <div
          className="ladder-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={ladder.progressPct}
          aria-label={ladder.nextTier ? `${ladder.nextTier.needed} more to unlock ${ladder.nextTier.name}` : "Top referral tier unlocked"}
        >
          <span className="ladder-fill" style={{ width: `${ladder.progressPct}%` }} />
        </div>
        <p className="ladder-caption">
          {ladder.nextTier
            ? `${ladder.nextTier.needed} more to unlock ${ladder.nextTier.name}`
            : "Top tier unlocked. You're an InSnip Ambassador."}
        </p>
        <ul className="ladder-tiers">
          {ladder.tiers.map((t) => (
            <li key={t.threshold} className={`ladder-tier${t.reached ? " reached" : ""}`} title={`${t.threshold} ${t.threshold === 1 ? "invite" : "invites"}: ${t.reward}`}>
              <span className="ladder-badge" aria-hidden="true">{t.reached ? "✓" : t.threshold}</span>
              <span className="ladder-name">{t.name}</span>
            </li>
          ))}
        </ul>
      </div>
      <button type="button" className="account-btn primary" onClick={shareInvite}>{friendsJoined > 0 ? "Invite another friend" : "Invite a friend"}</button>
      </>) : null}
    </div>
  );

  // Account panel (desktop rail + mobile menu): profile + sign-out when signed in; a single
  // "Sign in" button that opens the shared modal when signed out.
  //
  // Two options, both used by the You menu, where this block sits at the BOTTOM (account
  // management closes a menu, it does not open it): `stat` off because the stats row at the top
  // of that menu already carries the streak, and `invite` off because the invite ladder is
  // promoted into "Yours" there rather than buried under the sign-out button.
  const renderAccountCard = (opts?: { stat?: boolean; invite?: boolean }) => {
    const withStat = opts?.stat !== false;
    const withInvite = opts?.invite !== false;
    return account?.signedIn ? (
      <div className="account-card">
        <p className="account-email">{account.email ?? account.name ?? "Signed in"}</p>
        {withStat ? <p className="account-stat">{account.streak ?? 0}-day streak, {account.cardsRead ?? 0} read</p> : null}
        <p className="account-note">Synced across your devices.</p>
        {withInvite ? inviteBlock : null}
        <button type="button" className="account-btn ghost" disabled={authBusy} onClick={handleSignOut}>Sign out</button>
        <button type="button" className="account-btn danger" disabled={authBusy} onClick={handleDeleteAccount}>Delete account</button>
      </div>
    ) : (
      <div className="account-card">
        <p className="account-note">Sign in to sync your streak, saves &amp; insights across every device.</p>
        <button type="button" className="account-btn primary" onClick={() => openSignIn()}>Sign in</button>
        {withInvite ? inviteBlock : null}
      </div>
    );
  };
  const accountCard = renderAccountCard();

  // What the lens needs to order a deck. Plain state: the round loaders below are re-created
  // every render, and the mount-time loader's closure holds the stored lens and follows, which
  // is exactly what the first round should be ordered by.
  const lensContext = () => ({
    follows: new Set(Object.entries(topicPrefs).filter(([, p]) => p === "follow").map(([c]) => c)),
    savedIds,
    reactions,
    categoryOf: (id: string) => cards.find((x) => x.id === id)?.category,
    hidden: new Set(hiddenIds),
  });
  /** The card a freshly loaded round opens on: the first the reader will SEE under their lens.
   *  Found live 2026-09-14: with "Latest" remembered, opening on the server's first card put
   *  the reader on 10 of 10, then 4 of 10, because the lens had re-sorted the deck around it. */
  const landOn = (round: InSnipCard[]): InSnipCard | null =>
    landingCard(round, lens, lensContext()) ?? round[0] ?? null;

  const filteredCards = useMemo(() => {
    // Saved view: an independent reading list. Resolve every saved id from the loaded deck OR the
    // fetched cache (newest first), so cards saved in earlier rounds/sessions still appear, not just
    // ones in today's deck. None of the deck filters below apply in saved mode.
    if (showSavedOnly) {
      return [...savedIds]
        .reverse()
        .map((id) => cards.find((c) => c.id === id) ?? savedCardCache[id])
        .filter((c): c is InSnipCard => Boolean(c));
    }
    // The swipe deck is independent of search, it's always the reader's round of cards,
    // narrowed only by the category / content-type / saved chips. Search lives in its own
    // modal (it explores the whole library without touching the deck).
    let pool = cards;
    // "Make It Yours" muting in bundled-fallback mode: hide muted categories, but keep a
    // diversity floor, if muting would empty the feed, ignore it. (Server-driven mode
    // applies muting in the worker.)
    if (!serverDrivenRef.current) {
      const muted = topicPrefsToLists(topicPrefs).muted ?? [];
      if (muted.length) {
        const kept = pool.filter((card) => !card.category || !muted.includes(card.category));
        if (kept.length) pool = kept;
      }
    }
    if (activeTag !== "All") pool = pool.filter((card) => card.category === activeTag);
    if (contentFilter === "news") {
      pool = pool.filter((card) => (card.contentType ?? "news") === "news");
    } else if (contentFilter === "knowledge") {
      pool = pool.filter(
        (card) =>
          card.contentType === "knowledge" ||
          card.contentType === "brief" ||
          card.contentType === "explainer",
      );
    }
    if (hiddenIds.length) pool = pool.filter((card) => !hiddenIds.includes(card.id));
    // The lens re-orders the same deck, it never hides a story. "Top" is the curated
    // order the pipeline chose; "Latest" is strict recency; "For you" floats followed
    // topics first, then the fields this reader actually saves and reacts to, and keeps
    // the curated order as the tiebreak so the day still reads like a front page.
    // lensOrder() is ALSO what picks the card a new round opens on (see landingCard), so the
    // reader always starts on the first card they see, whatever the lens did to the order.
    return lensOrder(pool, lens, lensContext());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, activeTag, contentFilter, showSavedOnly, savedIds, savedCardCache, hiddenIds, topicPrefs, lens, reactions]);

  // The "Good News" franchise ACCUMULATES across the whole session, not just the current 10-card
  // round: the deck replaces every round, so a round-scoped section would reset to empty each time
  // and rarely catch the (deliberately rare, high-precision) good-news cards. Instead we remember
  // every genuinely-uplifting card seen in any loaded round, so the bright side fills as the reader
  // moves through the feed. Pipeline-flagged cards lead; a tapped card that has scrolled out of the
  // live deck is spliced back in (see openGoodNewsCard) so it always resolves.
  const goodNewsPoolRef = useRef<Map<string, InSnipCard>>(new Map());
  const [goodNewsVersion, setGoodNewsVersion] = useState(0);
  useEffect(() => {
    let changed = false;
    for (const c of cards) {
      const has = goodNewsPoolRef.current.has(c.id);
      if (isGoodNews(c)) {
        if (!has) {
          goodNewsPoolRef.current.set(c.id, c);
          changed = true;
        }
      } else if (has) {
        // The pool must be able to SHRINK. The judge can take a card off the shelf as well as put
        // it on one, and an add-only pool would keep showing a demoted story for the rest of the
        // session, which is the case that is hardest to notice and worst to see.
        goodNewsPoolRef.current.delete(c.id);
        changed = true;
      }
    }
    if (changed) setGoodNewsVersion((v) => v + 1);
  }, [cards]);

  // THE DECK IS NOT THE SHELF. Everything above fills the pool from cards the reader has been
  // served, which means the panel could only ever show good news they had already scrolled
  // past: open it on arrival and it was empty, and that empty panel got reported as "no good
  // news today" when the day's stories were sitting in the store the whole time. Ask the
  // server for the confirmed shelf directly, once, so the panel and its tile are right before
  // the reader has read anything.
  //
  // Merged into the SAME pool rather than kept beside it, so the demotion rule above still
  // applies: if a card comes back in a later round without the tag, it leaves the shelf.
  const [goodNewsToday, setGoodNewsToday] = useState<number | null>(null);
  const [goodNewsDays, setGoodNewsDays] = useState(0);
  useEffect(() => {
    if (!sessionApiEnabled()) return;
    let alive = true;
    void fetchGoodNews().then((shelf) => {
      // Null is a fetch failure, NOT an empty shelf, and the two must not look the same here:
      // on failure we leave whatever the deck contributed rather than blanking the panel.
      if (!alive || !shelf) return;
      let changed = false;
      for (const c of shelf.cards) {
        if (!goodNewsPoolRef.current.has(c.id)) {
          goodNewsPoolRef.current.set(c.id, c);
          changed = true;
        }
      }
      setGoodNewsToday(shelf.today);
      setGoodNewsDays(shelf.days);
      if (changed) setGoodNewsVersion((v) => v + 1);
    });
    return () => { alive = false; };
  }, []);

  const goodNewsCards = useMemo(() => {
    void goodNewsVersion; // recompute when the pool grows
    const picks = [...goodNewsPoolRef.current.values()];
    picks.sort((a, b) => {
      const flagged = Number(isFlaggedGoodNews(b)) - Number(isFlaggedGoodNews(a));
      if (flagged !== 0) return flagged;
      return (b.publishedAt || "").localeCompare(a.publishedAt || "");
    });
    return picks.slice(0, 12);
  }, [goodNewsVersion]);

  // The wording is a pure function in goodNews.ts, and it is tested there: every version of
  // this line so far has claimed "today" for cards that were not from today.
  const goodNewsLabel = useMemo(
    () => shelfLabel(goodNewsCards.length, goodNewsToday, goodNewsDays),
    [goodNewsCards.length, goodNewsToday, goodNewsDays],
  );

  // The topics this round holds, and how many stories each: the chips show only these, and
  // a topic's end card offers the others.
  const topicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cards) if (c.category && !hiddenIds.includes(c.id)) counts.set(c.category, (counts.get(c.category) ?? 0) + 1);
    return counts;
  }, [cards, hiddenIds]);
  // Has this reader told For you anything yet (a follow, a save, a reaction)?
  const lensTuned = Object.values(topicPrefs).some((p) => p === "follow") || savedIds.length > 0 || Object.values(reactions).some((r) => r?.length);
  // Any other way off the last story (a thumbnail, the wire, a topic) takes the end card away.
  useEffect(() => { setTopicEnd(false); }, [activeId, activeTag, showSavedOnly]);
  const activeCard = useMemo(() => {
    return filteredCards.find((card) => card.id === activeId) ?? filteredCards[0] ?? null;
  }, [activeId, filteredCards]);

  // When the saved view is open, fetch any saved cards that aren't in the loaded deck (older saves),
  // best-effort, so the reading list is complete instead of showing an empty state.
  useEffect(() => {
    if (!showSavedOnly) return;
    const missing = savedIds.filter((id) => !cards.some((c) => c.id === id) && !savedCardCache[id]);
    if (missing.length === 0) return;
    let alive = true;
    void Promise.all(missing.slice(0, 40).map((id) => fetchCard(id))).then((results) => {
      if (!alive) return;
      setSavedCardCache((prev) => {
        const next = { ...prev };
        for (const c of results) if (c) next[c.id] = c;
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [showSavedOnly, savedIds, cards, savedCardCache]);

  // Recall deck: the ideas worth revisiting, saved cards first (the reader chose to keep them),
  // then any knowledge/explainer cards loaded, so even a reader who hasn't saved anything yet has
  // something meaningful to refresh. The Recall modal handles the spaced-repetition scheduling.
  const recallDeck = useMemo(() => {
    const saved = cards.filter((card) => savedIds.includes(card.id));
    const knowledge = cards.filter(
      (card) =>
        !savedIds.includes(card.id) &&
        (card.contentType === "knowledge" || card.contentType === "explainer"),
    );
    return [...saved, ...knowledge].slice(0, 15);
  }, [cards, savedIds]);

  // "Read it your way": the body broken into scannable key points. Only offered when there are
  // enough substantial sentences to make a clean list (the toggle stays hidden otherwise).
  // Prefer the authoring pipeline's structured key points; fall back to deriving them from the body.
  const bodyPoints = useMemo(() => {
    const structured = (activeCard?.keyPoints ?? []).filter(Boolean);
    if (structured.length >= 3) return structured;
    // A thin structured list (0-2 points) would hide the depth toggle and make this card look
    // inconsistent next to richer ones, so fall back to the body-derived scan whenever it offers
    // more points; keep the structured list when it is already the fuller of the two.
    const derived = keyPoints(activeCard?.body ?? "", 5);
    return derived.length > structured.length ? derived : structured;
  }, [activeCard]);
  // THE FRONT: three or four pointers within a character budget (frontPoints.ts). Same rule
  // as the app, same day: four short lines read as a glance, four long ones as an article.
  const cardPoints = useMemo(() => frontPoints(bodyPoints, activeCard?.whyItMatters), [bodyPoints, activeCard?.whyItMatters]);
  // Each new card opens in Quick (the per-card choice doesn't carry over to the next card).
  useEffect(() => {
    setReadLevel("quick");
    setStoryOpen(false);
    setDeepDiveOpen(false);
  }, [activeCard?.id]);
  // Whether the card copy overflows its scroll box with the body below the fold. Drives the
  // "scroll for the full story" cue. Re-measured on card change, depth switches and resize; the
  // copy scrolls back to the top for each new card so the cue starts visible when there is more.
  const checkCopyHasMore = useCallback(() => {
    const el = copyRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight;
    const remaining = overflow - el.scrollTop;
    setCopyHasMore(overflow > 28 && remaining > 28);
  }, []);
  // Reset the reading scroll to the top only when the card or reading depth changes (new content).
  // Deliberately NOT on bodyExpanded/deepDiveOpen: resetting scrollTop mid-read is what made the
  // copy suddenly jump to the top while scrolling.
  useEffect(() => {
    const el = copyRef.current;
    if (el) el.scrollTop = 0;
  }, [activeCard?.id, readLevel]);
  // Re-measure the "more below" cue after any change that can alter the copy height (a frame's
  // wait lets the swapped-in content lay out first). This never moves the scroll position.
  useEffect(() => {
    const raf = requestAnimationFrame(checkCopyHasMore);
    return () => cancelAnimationFrame(raf);
  }, [activeCard?.id, readLevel, bodyExpanded, deepDiveOpen, storyOpen, checkCopyHasMore]);
  useEffect(() => {
    const onResize = () => checkCopyHasMore();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [checkCopyHasMore]);
  // "The Snip": fire the completion flourish once, the moment a card is registered as read.
  useEffect(() => {
    const id = activeCard?.id;
    if (!id || !viewedIds.includes(id) || snippedRef.current.has(id)) return;
    snippedRef.current.add(id);
    const t = window.setTimeout(() => {
      setSnipCardId(id);
      setSnipKey((k) => k + 1);
      haptic([10]);
    }, 260);
    return () => window.clearTimeout(t);
  }, [activeCard?.id, viewedIds]);

  // "More like this": when the sources drawer opens for a card, fetch semantically similar
  // stories (best-effort) so the reader can go deeper on a topic.
  const sourcesCardId = sourcesOpen ? (searchDetail ?? activeCard)?.id : undefined;
  // "This story has moved": the cross-day chain for the card under Verify. Fetched only
  // while the drawer is open; a chain of 0 or 1 renders nothing (stillness is not news).
  const [storyChain, setStoryChain] = useState<StoryChainStep[]>([]);
  useEffect(() => {
    setStoryChain([]);
    if (!sourcesCardId) return;
    let cancelled = false;
    void fetchStoryChain(sourcesCardId).then((c) => { if (!cancelled) setStoryChain(c); });
    return () => { cancelled = true; };
  }, [sourcesCardId]);
  useEffect(() => {
    if (!sourcesCardId || !sessionApiEnabled()) {
      setRelatedCards([]);
      return;
    }
    let cancelled = false;
    setRelatedCards([]);
    void fetchRelated(sourcesCardId).then((r) => {
      if (!cancelled) setRelatedCards(r);
    });
    return () => {
      cancelled = true;
    };
  }, [sourcesCardId]);

  const activeIndex = activeCard ? filteredCards.findIndex((card) => card.id === activeCard.id) : 0;

  // JUMP BACK IN. The story the reader was on is kept with the day; a return to the same round
  // on the same day, landing on story one, offers the story they stopped at. Read once at
  // mount (before the effect below overwrites it), shown until taken or until they move on.
  const [resumeAt, setResumeAt] = useState<{ id: string; index: number } | null>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("insnip.resume") || "null") as { day?: string; id?: string; index?: number } | null;
      const today = new Date().toISOString().slice(0, 10);
      return v && v.day === today && typeof v.id === "string" && typeof v.index === "number" && v.index > 0 ? { id: v.id, index: v.index } : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (!activeCard || showSavedOnly || activeIndex < 0) return;
    if (resumeAt && activeIndex > 0) setResumeAt(null); // they moved on; the offer is over
    try {
      localStorage.setItem("insnip.resume", JSON.stringify({ day: new Date().toISOString().slice(0, 10), id: activeCard.id, index: activeIndex }));
    } catch {
      /* storage may be blocked */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard?.id]);
  const resumeCard = resumeAt && activeIndex === 0 && !showSavedOnly ? filteredCards.find((c) => c.id === resumeAt.id) ?? null : null;

  // THE DEFERRED DOOR, released when the shared story is finished with.
  //
  // A phone that arrived on somebody's link gets that story first, untouched. The moment they
  // move to a second one they have chosen to keep reading, which is the best moment this
  // product has to offer them the app, and a far better one than their first second on the
  // site. Fires once: doorDeferred is cleared as it opens.
  // While the swipe lesson stands (a first-ever phone visit) the door waits for it to be
  // learned, card 4, rather than landing on top of "swipe again" on card 2 (USER 2026-10-30).
  useEffect(() => {
    if (!doorDeferred) return;
    if (activeIndex < (lesson >= 0 && lesson < 3 ? 3 : 1)) return;
    setDoorDeferred(false);
    setPhoneDoorOpen(true);
  }, [doorDeferred, activeIndex, lesson]);

  // The lesson advances with the reader (a swipe or the arrow, either counts) and ends on
  // card 4. The interlude follows only on a device the door has NOT asked this session: a
  // visitor who has just answered the full-screen door is not asked again two cards later.
  const doorThisSession = useRef(shouldShowPhoneDoor());
  useEffect(() => {
    if (lesson < 0 || lesson >= 3) return;
    if (activeIndex >= 3) {
      setLesson(3);
      try {
        localStorage.setItem("insnip.lessonV", "1");
        localStorage.setItem("insnip.hintSeen", JSON.stringify(true));
      } catch { /* storage unavailable: the lesson simply returns next visit */ }
      if (!doorThisSession.current && readStored<number>("insnip.interludeV", 0) < 1) {
        setInterlude(true);
        interludeShown.current = true;
        try { localStorage.setItem("insnip.interludeV", "1"); } catch { /* same */ }
      }
      return;
    }
    if (activeIndex > lesson) setLesson(activeIndex);
  }, [activeIndex, lesson]);
  // The interlude stands on one card: moving to another card is a dismissal.
  useEffect(() => {
    if (!interlude) { interludeAt.current = null; return; }
    if (interludeAt.current === null) { interludeAt.current = activeIndex; return; }
    if (activeIndex !== interludeAt.current) setInterlude(false);
  }, [interlude, activeIndex]);

  // THE ROUND PROMPT, released when the screen is free.
  //
  // A finished round already puts the summary up, and the caught-up screen takes the other
  // branch, so the prompt waits for whichever of them is showing to close. Waiting rather
  // than stacking is the whole difference between a celebration and an interruption.
  useEffect(() => {
    if (!roundPromptReady({
      due: roundPromptDue,
      summaryOpen: showInsight,
      deskOpen,
      doorOpen: phoneDoorOpen,
      caughtUp,
      signInOpen: authWall || signInOpen,
    })) return;
    setRoundPromptDue(false);
    setPromptedRound(dayRound);
    setRoundPromptOpen(true);
  }, [roundPromptDue, showInsight, deskOpen, phoneDoorOpen, caughtUp, dayRound, authWall, signInOpen]);

  const currentRoundStart = roundStartForIndex(activeIndex, READ_TARGET);
  const currentRoundEnd = roundEndForStart(filteredCards.length, currentRoundStart, READ_TARGET);
  const currentRoundTarget = roundSizeForStart(filteredCards.length, currentRoundStart, READ_TARGET);
  const roundViewedIds = viewedIds.filter((id) => {
    const index = filteredCards.findIndex((card) => card.id === id);
    return index >= currentRoundStart && index < currentRoundEnd;
  });
  const progress = Math.min(roundViewedIds.length, currentRoundTarget || READ_TARGET);
  const pattern = readingPattern(reactions, roundViewedIds, cards);
  // Tip of the hour, picked fresh each time the round-summary opens.
  // A subtle, stable-per-card motivation line, shown in the band between the reactions and the
  // round bar (not crammed into the round line, where it used to truncate).
  const hookLine = activeCard ? hookForCard(activeCard.id) : "";
  const roundNumber = filteredCards.length ? Math.floor(currentRoundStart / READ_TARGET) + 1 : 0;
  const totalRounds = Math.max(1, Math.ceil(filteredCards.length / READ_TARGET));

  const stopSpeech = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    // Detach first: cancel() fires "error" (interrupted) on the utterance being stopped, and a
    // live handler would then act on a read the caller has already abandoned.
    const current = ttsRef.current;
    if (current) {
      current.onend = null;
      current.onerror = null;
    }
    window.speechSynthesis.cancel();
    ttsRef.current = null;
    setSpeechState("idle");
  }, []);

  const visitCard = useCallback((cardId: string) => {
    const nextIndex = filteredCards.findIndex((card) => card.id === cardId);
    const nextRoundStart = roundStartForIndex(nextIndex, READ_TARGET);
    const activeRoundStart = roundStartForIndex(activeIndex, READ_TARGET);
    stopSpeech();
    // The card change is ONE view transition: the named headline + hero morph between
    // stories where supported (vt no-ops otherwise; flushSync makes the swap atomic so
    // the transition captures a genuine before/after).
    vt(() => flushSync(() => {
      setBodyExpanded(false);
      if (nextRoundStart !== activeRoundStart) setShowInsight(false);
      setActiveId(cardId);
      setViewedIds((current) => {
        const currentRoundIds = nextRoundStart === activeRoundStart
          ? current.filter((id) => {
              const index = filteredCards.findIndex((card) => card.id === id);
              return index >= nextRoundStart && index < nextRoundStart + READ_TARGET;
            })
          : [];
        if (currentRoundIds[currentRoundIds.length - 1] === cardId) return currentRoundIds;
        // Mark the card viewed, but do NOT open the insight here, doing so would
        // cover the 10th card itself. The summary is shown when the reader tries
        // to advance PAST a completed round (see move).
        return [...currentRoundIds.filter((id) => id !== cardId), cardId];
      });
    }));
    // The feature tour is a live demo, not reading: while it owns the deck, navigation
    // still happens (the reader sees the real move) but nothing is recorded anywhere.
    if (tourOpenRef.current) return;
    logEngagement("view", cardId);
    // Tiny local ritual logs alongside the view event: which days you read (feeds the
    // Insights 14-day calendar, capped at 30 days) and at what hours (best-hour line).
    try {
      const dayKey = localDateKey();
      const days = readStored<string[]>("insnip.readDays", []);
      if (!days.includes(dayKey)) {
        days.push(dayKey);
        localStorage.setItem("insnip.readDays", JSON.stringify(days.slice(-30)));
      }
      const stored = readStored<number[]>("insnip.readHours", []);
      const hours = Array.from({ length: 24 }, (_, i) => Number(stored[i]) || 0);
      hours[new Date().getHours()] += 1;
      localStorage.setItem("insnip.readHours", JSON.stringify(hours));
    } catch {
      /* best-effort, the insights extras degrade gracefully */
    }
    // Record this card as seen (persisted) so a future session resumes past it.
    if (!seenRef.current.has(cardId)) {
      seenRef.current.add(cardId);
      setSeenCount(seenRef.current.size);
      try {
        localStorage.setItem("insnip.seen", JSON.stringify([...seenRef.current]));
      } catch {
        /* storage full / unavailable, resume is best-effort */
      }
      // Mirror to the server seen-log so the never-repeat rule holds across devices.
      if (serverDrivenRef.current) void markSeen([cardId]);
      recordRead();
    }
  }, [activeIndex, filteredCards, stopSpeech]);

  // Open a Good News card from the franchise section. Because the section accumulates across the
  // session, a tapped card may have scrolled out of the live 10-card deck; splice it back in so it
  // always resolves in the feed (mirrors how out-of-deck search results are handled).
  const openGoodNewsCard = useCallback((card: InSnipCard) => {
    setGoodNewsOpen(false);
    setActiveTag("All");
    setContentFilter("all");
    setShowSavedOnly(false);
    setBodyExpanded(false);
    setCards((prev) => (prev.some((c) => c.id === card.id) ? prev : [...prev, card]));
    setActiveId(card.id);
    logEngagement("view", card.id);
  }, [logEngagement]);

  const move = useCallback((delta: number) => {
    if (!filteredCards.length) return;
    const baseIndex = activeIndex === -1 ? 0 : activeIndex;
    // A topic's end card stands over its last story: back takes it away, forward waits for
    // the reader to pick (the rest of the round, another topic, or the next round).
    if (topicEnd) {
      if (delta < 0) setTopicEnd(false);
      return;
    }
    // The last story in a topic is not the end of the round (USER 2026-11-03): the rest of
    // the round is still unread under the other topics, so say so instead of closing it.
    if (delta > 0 && activeTag !== "All" && !showSavedOnly && !showInsight && baseIndex === filteredCards.length - 1) {
      haptic(6);
      setTopicEnd(true);
      return;
    }
    // Advancing forward off the last card of a fully-read round → show the
    // insight summary instead of sliding on. The reader has actually finished
    // the 10th card by now, so it never covers it.
    // Saved view is an independent reading list, NOT the daily deck: skip all round logic
    // (no summary, no next-round fetch, no caught-up screen), just step through saved cards.
    if (delta > 0 && !showInsight && !showSavedOnly) {
      // Server-driven: the feed IS the round (exactly 10 from the store). Reaching
      // the last card and advancing completes it -> show the summary, then "Next 10"
      // fetches a fresh round. (No fragile per-card viewed-count requirement.)
      if (serverDrivenRef.current && baseIndex === filteredCards.length - 1) {
        haptic([12, 40, 12]);
        markRoundDone();
        // A finished round is the best moment this product has to ask a phone-browser reader
        // for the app: they have just done the thing the app is for, on the worse of the two
        // surfaces. Armed here, opened once the summary is out of the way (see the effect
        // below), so a reader never gets two sheets over one screen.
        if (phoneWeb) setRoundPromptDue(true);
        // If the bucket is empty, go straight to the caught-up screen (no stray
        // partial-round summary). Otherwise show the round summary -> Next 10.
        if (!hasMoreCards) {
          void showCaughtUp();
        } else {
          setShowInsight(true);
        }
        return;
      }
      const roundStart = roundStartForIndex(baseIndex, READ_TARGET);
      const roundTarget = roundSizeForStart(filteredCards.length, roundStart, READ_TARGET);
      const isLastOfRound = baseIndex === roundStart + roundTarget - 1;
      const viewedInRound = viewedIds.filter((id) => {
        const index = filteredCards.findIndex((card) => card.id === id);
        return index >= roundStart && index < roundStart + roundTarget;
      }).length;
      if (isLastOfRound && roundTarget > 0 && viewedInRound >= roundTarget) {
        haptic([12, 40, 12]);
        markRoundDone();
        if (phoneWeb) setRoundPromptDue(true);
        setShowInsight(true);
        return;
      }
    }
    const nextIndex = Math.min(
      Math.max(baseIndex + delta, 0),
      filteredCards.length - 1
    );
    if (nextIndex === baseIndex) return;
    haptic(6);
    visitCard(filteredCards[nextIndex].id);
  }, [activeIndex, filteredCards, visitCard, viewedIds, showInsight, showSavedOnly, phoneWeb, topicEnd, activeTag]);

  // Deep dive open/close as a view transition: the card headline (view-transition-name
  // "card-headline") morphs into the dive title and back. The dive title's name is set
  // imperatively right after the synchronous flush (DeepDivePanel stays untouched), and
  // the card's own h1 drops the name while the dive is open so it is never duplicated.
  const openDeepDive = useCallback(() => {
    // A member feature, and it stayed one when the first door opened: the deep dive is the
    // single biggest thing an account buys, so it asks here rather than opening for everyone.
    if (!member) { setGateFeature("deepdive"); return; }
    if (activeId) trackDiveOpened(activeId); // v3: depth usage feeds the reader archetype
    vt(() => {
      flushSync(() => setDeepDiveOpen(true));
      try {
        const t = document.querySelector<HTMLElement>(".dd-title");
        if (t) t.style.setProperty("view-transition-name", "card-headline");
      } catch {
        /* name plumbing is polish only */
      }
    });
  }, [activeId, member]);
  const closeDeepDive = useCallback(() => {
    vt(() => flushSync(() => setDeepDiveOpen(false)));
  }, []);
  // ── Feedback popup ──────────────────────────────────────────────────────
  const openFeedback = useCallback((target: { kind: "general" } | { kind: "card"; cardId: string }) => {
    setFbCategory("");
    setFbMessage("");
    setFbStatus("idle");
    setFeedbackFor(target);
  }, []);
  const submitFeedback = useCallback(async () => {
    if (!feedbackFor) return;
    const message = fbMessage.trim();
    if (!fbCategory && !message) return; // need at least a category or a note
    setFbStatus("sending");
    const ok = await sendFeedback({
      kind: feedbackFor.kind,
      cardId: feedbackFor.kind === "card" ? feedbackFor.cardId : undefined,
      category: fbCategory,
      message,
      platform: "web",
      appVersion: (import.meta.env.VITE_RELEASE as string | undefined) ?? "",
    });
    setFbStatus(ok ? "sent" : "error");
    if (ok) window.setTimeout(() => setFeedbackFor(null), 1500);
  }, [feedbackFor, fbCategory, fbMessage]);
  // Insights hub opens as a view transition too (a soft cross-fade rather than a pop).
  const openInsightsHub = useCallback(() => {
    vt(() => flushSync(() => setInsightsOpen(true)));
  }, []);

  // v3: assemble the calm catch-up set from cards already in memory (finite by design).
  const openCalm = useCallback(() => {
    setCalmCards(pickCalmCards(cards, seenRef.current));
    setCalmOpen(true);
  }, [cards]);

  // Saved view ↔ feed. Entering remembers the current feed card; "Home" returns to it and resumes.
  function openSavedView() {
    feedReturnIdRef.current = activeId;
    setShowSavedOnly(true);
  }
  function goHome() {
    setShowSavedOnly(false);
    if (feedReturnIdRef.current) visitCard(feedReturnIdRef.current);
  }
  function toggleSavedView() {
    if (showSavedOnly) goHome();
    else openSavedView();
  }
  // The same asks while the deck is already open (the dock's Edition, the menu's Saved stories):
  // a link that only changes the hash is answered in place, never by a reload.
  const openSavedRef = useRef(openSavedView);
  openSavedRef.current = openSavedView;
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      const ask = /(?:^|[#&?])edition(?:[&=]|$)/.test(h) ? "edition" : /(?:^|[#&?])saved(?:[&=]|$)/.test(h) ? "saved" : /(?:^|[#&?])signin(?:[&=]|$)/.test(h) ? "signin" : null;
      if (!ask) return;
      try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* fine */ }
      setToolsMenuOpen(false);
      if (ask === "edition") setDeskOpen(true);
      else if (ask === "saved") { setDeskOpen(false); openSavedRef.current(); }
      else openSignIn();
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [openSignIn]);
  // "/read#saved" from the home's You panel: the saved list, once the cards are in.
  const savedAskDoneRef = useRef(false);
  useEffect(() => {
    if (arrivalAskRef.current !== "saved" || savedAskDoneRef.current || !cards.length) return;
    savedAskDoneRef.current = true;
    try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* fine */ }
    if (!showSavedOnly) openSavedView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.length]);

  // Preload nearby card images so swiping feels instant: the browser fetches +
  // caches them before the card is shown, so the <img> renders from cache. The
  // topic-tinted panel still covers the brief first fetch of the current card.
  useEffect(() => {
    if (!filteredCards.length) return;
    const base = activeIndex < 0 ? 0 : activeIndex;
    for (const delta of [1, 2, 3, -1]) {
      const card = filteredCards[base + delta];
      if (card?.imageUrl) {
        const img = new Image();
        img.decoding = "async";
        img.src = resolveImageUrl(card.imageUrl);
      }
    }
  }, [activeIndex, filteredCards]);

  // Log dwell time per card, the strongest interest signal (feeds taste/ranking).
  // When the active card changes, close out the time spent on the previous one.
  const dwellRef = useRef<{ id: string; at: number } | null>(null);
  useEffect(() => {
    const now = Date.now();
    const prev = dwellRef.current;
    if (prev && prev.id !== activeCard?.id) {
      const ms = now - prev.at;
      if (ms >= 1500 && ms <= 30 * 60 * 1000) logEngagement("dwell", prev.id, ms);
    }
    dwellRef.current = activeCard ? { id: activeCard.id, at: now } : null;
  }, [activeCard?.id]);

  function changeTag(tag: string | undefined) {
    if (!tag) return;
    setActiveTag(tag);
    setTopicEnd(false);
    // The deck the reader is about to see, in the order they will see it, and the first story
    // in it they have not read yet. A topic is a narrower view of the same round, not a new
    // one, so what was read stays read: clearing the filter returns to the unread stories.
    const shown = cards.filter((card) => !hiddenIds.includes(card.id) && (tag === "All" || card.category === tag));
    const nextPool = lensOrder(shown, lens, lensContext());
    const firstCard = nextPool.find((card) => !viewedIds.includes(card.id)) ?? nextPool[0] ?? null;
    stopSpeech();
    setBodyExpanded(false);
    setShowInsight(false);
    setActiveId(firstCard?.id ?? null);
    if (firstCard && !viewedIds.includes(firstCard.id)) setViewedIds((ids) => [...ids, firstCard.id]);
  }

  function applyInterests(picked: string[]) {
    setInterests(picked);
    // Server-driven feed is recency-first (latest -> oldest); never client-resort it
    // by interest, or onboarding would surface older cards before newer ones. The
    // picks are still saved server-side (completeOnboarding) for muting/future use.
    if (serverDrivenRef.current) {
      setActiveTag("All");
      setShowInsight(false);
      return;
    }
    const weights = readerWeights(picked, affinity);
    const ordered = personalizeFeed(cards, weights);
    setCards(ordered);
    setActiveId(ordered[0]?.id ?? null);
    setViewedIds(ordered[0] ? [ordered[0].id] : []);
    setActiveTag("All");
    setShowInsight(false);
  }

  // Close out the v3 onboarding component: persist what the reader gave us, apply the picks,
  // then chain into the gesture tour for genuine first-timers (brand moment came first).
  function completeOnboarding(r: OnboardResult) {
    localStorage.setItem("insnip.onboardV", JSON.stringify(ONBOARD_VERSION));
    if (r.dob) {
      localStorage.setItem("insnip.dob", JSON.stringify(r.dob));
      setDob(r.dob);
    }
    if (r.mode === "profile" || r.firstName || r.email) {
      localStorage.setItem("insnip.profile", JSON.stringify({
        mode: "profile", firstName: r.firstName, lastName: r.lastName, email: r.email,
      }));
    }
    // Persist to the server (best-effort) so personalisation + cross-device sync work.
    void saveProfile({
      mode: r.mode,
      firstName: r.firstName || undefined,
      lastName: r.lastName || undefined,
      email: r.email || undefined,
      interests: r.picks,
    });
    setOnboardingOpen(false);
    if (r.picks.length) applyInterests(r.picks);
    if (onboardEntry === "first-run" && shouldShowTour(readStored<number>("insnip.tourV", 0))) {
      openTourWhenClear();
    }
    setOnboardEntry("first-run");
  }

  function skipOnboarding(r: OnboardResult) {
    localStorage.setItem("insnip.onboardV", JSON.stringify(ONBOARD_VERSION));
    void saveProfile({ mode: r.mode, interests: r.picks });
    setOnboardingOpen(false);
    if (onboardEntry === "first-run" && shouldShowTour(readStored<number>("insnip.tourV", 0))) {
      openTourWhenClear();
    }
    setOnboardEntry("first-run");
  }

  // Mirror tourOpen into a ref for visitCard's read-record guard, and capture the deck
  // position on the rising edge so finishTour can put the reader back where the tour
  // found them (the browse demo really advances a card; it must not cost a story).
  useEffect(() => {
    if (tourOpen && !tourOpenRef.current) {
      tourReturnRef.current = activeId ? { id: activeId, viewed: viewedIds } : null;
    }
    tourOpenRef.current = tourOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourOpen]);

  // The gesture tour finished (first run, a skip, or a replay from the menu). Mark it done,
  // close, and rewind the deck to the pre-tour card so the demo advance never counts.
  function finishTour() {
    localStorage.setItem("insnip.tourV", JSON.stringify(TOUR_VERSION));
    setTourOpen(false);
    const back = tourReturnRef.current;
    tourReturnRef.current = null;
    if (back && back.id) {
      setActiveId(back.id);
      setViewedIds(back.viewed);
    }
  }

  // Real actions the feature tour drives so it can demo features LIVE (advance a card, open the
  // story, run a "climate" search with results, open the Brief / menu). The tour keeps the latest
  // via a ref, so re-creating this object never re-fires its step effect.
  const tourControls = useMemo(() => ({
    nextCard: () => move(1),
    expand: (open: boolean) => setBodyExpanded(open),
    // Flip the reader between the Full article and Key points, so the tour demos the real toggle.
    showFull: (open: boolean) => setReadLevel(open ? "deeper" : "quick"),
    search: (q: string) => { setSearchDetail(null); setSearchOpen(true); setSearchQuery(q); },
    closeSearch: () => { setSearchOpen(false); setSearchDetail(null); setSearchQuery(""); },
    openBrief: () => { setBriefIndex(0); setBriefOpen(true); },
    closeBrief: () => setBriefOpen(false),
    openMenu: () => setMobileMenuOpen(true),
    closeMenu: () => setMobileMenuOpen(false),
    revealNav: () => setMenuOpen(true),
    closeNav: () => setMenuOpen(false),
    // Verify and the deep dive, driven as the reader would drive them. The dive is opened
    // directly (not through openDeepDive) because the tour has already decided whether this
    // reader may see it: a locked step never calls this at all.
    openVerify: () => setSourcesOpen(true),
    closeVerify: () => setSourcesOpen(false),
    openDive: () => { vt(() => flushSync(() => setDeepDiveOpen(true))); },
    closeDive: () => setDeepDiveOpen(false),
    // The same predicate the card's own deep-dive button renders on, so the tour can never
    // spotlight a control that is not on the screen.
    hasDeepDive: () => !phoneWeb && !!deepDiveFor(activeCard),
  }), [move, activeCard]);

  // A bottom-nav tab routes to its surface. Discover/Library/You open their panel; Feed closes any
  // open panel and returns to the deck. The bar is always visible, so there is nothing to hide.
  const selectNavTab = (tab: NavTab) => {
    setMenuOpen(false); // picking a destination returns the bar to status
    if (tab === "stories") { setInsightsOpen(false); setMobileMenuOpen(false); setOverviewOpen(true); return; }
    if (tab === "insights") { setOverviewOpen(false); setMobileMenuOpen(false); openInsightsHub(); return; }
    if (tab === "you") { setOverviewOpen(false); setInsightsOpen(false); setMobileMenuOpen(true); return; }
    setSearchOpen(false);
    setSearchDetail(null);
    setLibraryOpen(false);
    setRecallOpen(false);
    setProOpen(false);
    setMobileMenuOpen(false);
    setOverviewOpen(false);
    setInsightsOpen(false);
    if (showSavedOnly) goHome();
  };

  // One bar at a time. barMode -> <html data-bar>: "hidden" (fresh card, clean) -> "status" (after
  // ~2s the round/nav/vote toolbar) -> "menu" (pulled up; the toolbar collapses). CSS does the rest.
  const barMode = menuOpen ? "menu" : statusReady ? "status" : "hidden";
  useEffect(() => {
    document.documentElement.setAttribute("data-bar", barMode);
    return () => document.documentElement.removeAttribute("data-bar");
  }, [barMode]);
  useEffect(() => {
    setStatusReady(false);
    setMenuOpen(false);
    const t = window.setTimeout(() => setStatusReady(true), 2000);
    return () => window.clearTimeout(t);
  }, [activeCard?.id]);

  // Mirror an engagement signal to the backend (best-effort, anonymous).
  function logEngagement(type: EngagementType, cardId: string, ms = 0) {
    const card = cards.find((c) => c.id === cardId);
    if (!card) return;
    sendEvent({ type, cardId, category: card.category, tag: card.tags?.[0], ms });
  }

  // A tap on any "Get the app" store link. The surface rides in `tag`, not in cardId: these
  // taps are not about a card, and routing them through logEngagement silently dropped every
  // one of them (it requires the id to match a loaded card).
  function logAppGetClick(surface: string) {
    sendEvent({ type: "app_get_click", tag: surface, cardId: activeCard?.id });
  }

  // Ask the LLM a grounded follow-up about the active card. Used by both the
  // suggested-question chips and the free-text input; appends to a small thread
  // so the reader can keep asking. Logs an "expand" engagement signal per ask.
  async function askQuestion(rawQuestion: string) {
    const question = rawQuestion.trim().slice(0, 300);
    // Ask acts on the search card when a search detail is open, otherwise the active feed card.
    const card = searchDetail ?? activeCard;
    if (!question || askBusy || !card) return;
    setAskInput("");
    setAskError(null);
    setAskBusy(true);
    logEngagement("expand", card.id);
    haptic(6);
    try {
      const { answer, sources } = await askCard(card.id, question);
      setAskThread((current) => [...current, { q: question, a: answer, sources }]);
    } catch (err) {
      const reason = err instanceof Error ? err.message : "ask failed";
      setAskError(
        reason === "unavailable"
          ? "Ask isn't available right now."
          : "Couldn't get an answer. Try again in a moment.",
      );
    } finally {
      setAskBusy(false);
    }
  }

  function openBrief() {
    if (!member) { setGateFeature("brief"); return; }
    setBriefIndex(0);
    setBriefOpen(true);
  }

  // Navigate the deck to today's essential card. Reset the deck filters first (as jumpToCategory
  // does) so the card is guaranteed to be in the navigable pool regardless of the active chips.
  function goToEssential() {
    if (!todaysEssentialId) return;
    setActiveTag("All");
    setShowSavedOnly(false);
    setContentFilter("all");
    setSearchQuery("");
    visitCard(todaysEssentialId);
    haptic(6);
  }

  // Share a day's Brief: a dated one-line hook to the /brief/<date> SEO page, carrying this
  // reader's id as the referrer. Native share sheet where available, clipboard fallback with a
  // "Copied" confirmation. The link lives in the text once (never also as `url`).
  async function shareBrief(brief: InSnipCard) {
    const dayKey = briefDayKey(brief);
    const essence = (brief.essence ?? "").trim();
    const trimmed = essence.length > 180 ? `${essence.slice(0, 179).trimEnd()}…` : essence;
    const url = withReferral(`https://insnip.com/brief/${dayKey}`);
    const text = `The Brief, ${briefDayLabel(brief)}\n${trimmed}\nRead the day in a minute: ${url}`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "The Brief", text });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setBriefCopied(true);
        window.setTimeout(() => setBriefCopied(false), 1800);
      }
      sendEvent({ type: "share", tag: "brief" });
    } catch {
      /* share cancelled */
    }
  }

  function openAsk() {
    if (!(searchDetail ?? activeCard)) return;
    if (!member) { setGateFeature("ask"); return; }
    setAskOpen(true);
    setAskError(null);
  }

  // After a guest signs in via the unlock prompt, open the feature they were reaching for.
  useEffect(() => {
    if (!member || !gateFeature) return;
    const f = gateFeature;
    setGateFeature(null);
    if (f === "search") { setSearchDetail(null); setSearchOpen(true); }
    else if (f === "brief") { setBriefIndex(0); setBriefOpen(true); }
    else if (f === "ask") { setAskOpen(true); setAskError(null); }
  }, [member, gateFeature]);

  // Dismiss the "new snips" pill and snooze it for an hour (persisted across reloads).
  function dismissFresh() {
    const until = Date.now() + 60 * 60 * 1000;
    setFreshSnooze(until);
    try { localStorage.setItem("insnip.freshSnooze", String(until)); } catch { /* ignore */ }
  }

  // Referral incentive: record a bare invite link's referrer on arrival (the card-link flow already
  // handles shared-card links), and load this reader's invite count once signed in.
  useEffect(() => {
    try {
      const r = parseReferrer(typeof window !== "undefined" ? window.location.search : "");
      if (r && r !== anonId() && r !== accountUid() && !localStorage.getItem("insnip.refSeen")) {
        localStorage.setItem("insnip.refSeen", r);
        sendEvent({ type: "referral", tag: r });
        setReferralWelcome(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!account?.signedIn) return;
    void fetchReferrals(accountUid() || anonId()).then(setReferralCount);
  }, [account?.signedIn]);

  function inviteLink(): string {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
    const base = /^https:\/\//.test(origin) && !/localhost/.test(origin) ? origin : "https://insnip.com";
    return withReferral(`${base}/`);
  }

  async function shareInvite() {
    const url = inviteLink();
    const data = {
      title: "InSnip",
      text: "I've been reading InSnip, the day's best stories in under a minute. Join me, it's free:",
      url,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(data);
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setTopicsToast("Invite link copied. Paste to share!");
        window.setTimeout(() => setTopicsToast(null), 2600);
      }
      sendEvent({ type: "share", tag: "invite" });
    } catch { /* share cancelled */ }
  }

  function closeAsk() {
    setAskOpen(false);
    setAskBusy(false);
  }

  // Deep Dive -> real Ask AI. Close the deep dive, open Ask (which gates on sign-in), and if a
  // suggested question was tapped, remember it so it fires the moment Ask is actually open (even
  // after the sign-in unlock). Called with no argument to just open Ask for free-form typing.
  function askFromDeepDive(question?: string) {
    if (!(searchDetail ?? activeCard)) return;
    setDeepDiveOpen(false);
    setPendingAsk(question && question.trim() ? question.trim() : null);
    openAsk();
  }

  useEffect(() => {
    if (askOpen && pendingAsk) {
      const q = pendingAsk;
      setPendingAsk(null);
      void askQuestion(q);
    }
    // askQuestion is stable enough for this one-shot handoff; deps kept minimal on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askOpen, pendingAsk]);

  // Share a card's Deep Dive as a branded "how the story flows" image (native share, else download).
  async function shareDeepDive(card: InSnipCard) {
    const dd = deepDiveFor(card);
    if (!dd) return;
    try {
      await shareDeepDiveImage(card, dd);
      logEngagement("share", card.id);
      sendEvent({ type: "share", tag: "deepdive" });
    } catch {
      /* share cancelled */
    }
  }

  // Fresh card → clear the Q/A thread so answers always match the card on screen. The ask target is
  // the search card when a search detail is open, so reset on either changing (no stale answers).
  useEffect(() => {
    setAskThread([]);
    setAskInput("");
    setAskError(null);
    setAskOpen(false);
  }, [activeCard?.id, searchDetail?.id]);

  // Learn which categories a reader engages with, so the next session's feed
  // leans their way (the personalisation signal behind "Tuned for you").
  function bumpAffinity(cardId: string, amount: number) {
    const category = cards.find((card) => card.id === cardId)?.category;
    if (!category) return;
    setAffinity((current) => ({ ...current, [category]: (current[category] ?? 0) + amount }));
  }

  // "Call it: Signal or Noise": the one-tap commitment on the active story. Optimistic
  // reveal (the crowd split shows instantly), then the server response trues it up. A call
  // is also an affinity signal, same tier as a reaction.
  function callIt(cardId: string, stance: "signal" | "noise") {
    haptic(12);
    bumpAffinity(cardId, 1);
    setStances((prev) => {
      const cur = prev[cardId] ?? { cardId, signal: 0, noise: 0, mine: null, verdict: null };
      if (cur.mine === stance) return prev;
      const signal = cur.signal + (stance === "signal" ? 1 : 0) - (cur.mine === "signal" ? 1 : 0);
      const noise = cur.noise + (stance === "noise" ? 1 : 0) - (cur.mine === "noise" ? 1 : 0);
      return { ...prev, [cardId]: { ...cur, signal, noise, mine: stance } };
    });
    void sendStance(cardId, stance).then((state) => {
      if (state) setStances((prev) => ({ ...prev, [cardId]: state }));
    });
  }

  function toggleSave(cardId: string) {
    haptic(10);
    const willSave = !savedIds.includes(cardId);
    setSavedIds((current) => current.includes(cardId) ? current.filter((id) => id !== cardId) : [...current, cardId]);
    if (willSave) {
      bumpAffinity(cardId, 2);
      logEngagement("save", cardId);
    }
  }

  function react(cardId: string, reactionId: string, event: React.MouseEvent<HTMLButtonElement>) {
    haptic(12);
    bumpAffinity(cardId, 1);
    logEngagement("reaction", cardId);
    const btn = event.currentTarget;
    btn.setAttribute("data-bursting", "1");
    window.setTimeout(() => btn.removeAttribute("data-bursting"), 920);

    // Spawn 5 graffiti emojis that escape the reaction box and scatter onto the card
    const reaction = REACTIONS.find((r) => r.id === reactionId);
    if (reaction) {
      const rect = btn.getBoundingClientRect();
      const startX = rect.left + rect.width / 2;
      const startY = rect.top + rect.height / 2;
      const stamp = Date.now();
      const fresh = Array.from({ length: 5 }).map((_, i) => {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI * 0.85);
        const distance = 140 + Math.random() * 180;
        return {
          id: `${stamp}-${i}-${Math.random().toString(36).slice(2, 7)}`,
          emoji: reaction.emoji,
          startX,
          startY,
          tx: Math.cos(angle) * distance,
          ty: Math.sin(angle) * distance,
          r: (Math.random() - 0.5) * 80,
          s: 0.9 + Math.random() * 1.4
        };
      });
      setGraffiti((current) => [...current, ...fresh]);
      window.setTimeout(() => {
        setGraffiti((current) => current.filter((g) => !fresh.some((f) => f.id === g.id)));
      }, 1600);
    }

    setReactions((current) => ({ ...current, [cardId]: [...(current[cardId] ?? []), reactionId] }));
  }


  async function shareCardAsImage(card: InSnipCard) {
    if (imageShareState === "busy") return;
    setImageShareState("busy");
    try {
      const result = await shareCardImage(card);
      setImageShareState(result === "failed" ? "idle" : "done");
      if (result !== "failed") logEngagement("share", card.id);
    } finally {
      window.setTimeout(() => setImageShareState("idle"), 1800);
    }
  }

  // Share the day's recap as a branded image, turns the daily pride moment (streak +
  // stories read) into a growth loop (friends see it → install).
  async function shareDay() {
    if (imageShareState === "busy") return;
    setImageShareState("busy");
    try {
      const recap = computeRecap({
        seenIds: seenRef.current,
        cards,
        streak: streak.count,
        reactions,
        savedCount: savedIds.length,
      });
      // Every finished day becomes a landing page: mint the public /d/<code> recap (numbers
      // only) and put the link on the clipboard beside the image share. Best-effort: a miss
      // just means the image shares alone, exactly as before.
      const daySources = cards.reduce(
        (n, c) => (seenRef.current.has(c.id) ? n + 1 + (Array.isArray(c.supportingSources) ? c.supportingSources.length : 0) : n),
        0,
      );
      const code = await mintDayShare({
        day: new Date().toLocaleDateString("en-CA"),
        cards: insights?.cardsRead ?? recap.cardsRead,
        sources: daySources,
        streak: streak.count,
        fields: new Set(cards.filter((c) => seenRef.current.has(c.id)).map((c) => String(c.category || ""))).size,
        name: (localStorage.getItem("insnip.displayName") || "").trim().slice(0, 40) || undefined,
      });
      if (code) {
        try { await navigator.clipboard?.writeText(`https://insnip.com/d/${code}`); } catch { /* image share still carries the day */ }
      }
      const result = await shareRecapImage({
        streak: streak.count,
        cardsRead: insights?.cardsRead ?? recap.cardsRead,
        topCategory: insights?.topCategory ?? recap.topCategory,
        rounds: dayRound,
      });
      setImageShareState(result === "failed" ? "idle" : "done");
      if (result !== "failed") sendEvent({ type: "share", tag: "recap" });
    } finally {
      window.setTimeout(() => setImageShareState("idle"), 1800);
    }
  }

  // Count each newly-read card (lifetime + per local day) and fire a depth-insight
  // reward every 25 cards, with a big celebration at 100 in a single day (#6).
  function recordRead() {
    const total = readStored<number>("insnip.totalRead", 0) + 1;
    try { localStorage.setItem("insnip.totalRead", JSON.stringify(total)); } catch { /* ignore */ }
    const dayKey = localDateKey(); // local day, matching the daily reset + round numbering
    const rec = readStored<{ d: string; n: number }>("insnip.dayRead", { d: "", n: 0 });
    const dayN = (rec.d === dayKey ? rec.n : 0) + 1;
    try { localStorage.setItem("insnip.dayRead", JSON.stringify({ d: dayKey, n: dayN })); } catch { /* ignore */ }

    if (dayN === 100) {
      if (readStored<string>("insnip.century", "") !== dayKey) {
        try { localStorage.setItem("insnip.century", JSON.stringify(dayKey)); } catch { /* ignore */ }
        setReward({
          emoji: "🏆",
          title: "Century, 100 in a day",
          body: "You read 100 cards today. That's top-tier curiosity. You're in the rarest slice of InSnip readers. Take the crown.",
          big: true,
        });
        haptic([16, 60, 16, 60, 30]);
      }
      return;
    }
    if (total % 25 === 0) {
      const mins = Math.round((total * 32) / 60); // ~32s/card
      const likes = Object.values(verdicts).filter((v) => v === "up").length;
      const topCat = insights?.topCategory ?? "the topics you pick";
      setReward({
        emoji: "🎯",
        title: `${total} cards read`,
        body: `Depth unlocked. You lean into ${topCat.toLowerCase()}${likes ? `, with ${likes} like${likes === 1 ? "" : "s"} logged` : ""}. InSnip keeps tuning to your taste. ~${mins} focused minutes well spent.`,
        big: false,
      });
      haptic([12, 40, 12]);
    }
  }

  // Celebrate streak milestones once each (a key retention hook).
  function celebrateStreak(count: number) {
    const milestones = [3, 7, 14, 30, 50, 100, 365];
    if (!milestones.includes(count)) return;
    if (readStored<number>("insnip.lastMilestone", 0) >= count) return;
    localStorage.setItem("insnip.lastMilestone", JSON.stringify(count));
    setMilestone(`${count}-day streak. Keep it going.`);
    haptic([14, 50, 14, 50, 14]);
    window.setTimeout(() => setMilestone(null), 5200);
  }

  // Turn on web push (daily-drop + streak-at-risk nudges). Must run from a click.
  async function enablePush() {
    setPushState("busy");
    const ok = await enableWebPush();
    setPushState(ok ? "on" : "off");
    if (ok) {
      haptic([10, 40, 10]);
      setMilestone("🔔 Daily drop on. We'll nudge you when fresh cards land.");
      window.setTimeout(() => setMilestone(null), 4200);
    }
  }

  async function openQuiz() {
    setMobileMenuOpen(false);
    haptic(8);
    let card = quizCard;
    if (!card) {
      card = await fetchQuiz();
      // v3: no server quiz card is no longer a dead end, the Front Page Quiz
      // builds itself from today's edition (read stories lead the questions).
      if (!card) {
        card = buildFrontPageQuiz(
          filteredCards.length ? filteredCards : cards,
          new Set(viewedIds),
          new Date().toLocaleDateString("en-CA"),
        );
      }
      if (!card) {
        setTopicsToast("Today's quiz lands with the morning drop. Check back soon.");
        window.setTimeout(() => setTopicsToast(null), 2600);
        return;
      }
      setQuizCard(card);
    }
    setQuizOpen(true);
  }

  async function shareQuizScore(score: number, total: number, results: boolean[]) {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
    // The Wordle loop: a paste-anywhere text block with the result grid and a link
    // straight into today's quiz. Falls back to the plain sentence if the per-question
    // record is somehow missing (an old share caller).
    const dayLabel = new Date().toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
    const text = results.length
      ? quizShareText(results, dayLabel, `${origin}/read#quiz`)
      : `🧠 I scored ${score}/${total} on today's InSnip news quiz. Think you can beat it? ${origin}`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "InSnip Front Page Quiz", text });
      } else {
        await navigator.clipboard?.writeText(text);
        setTopicsToast("Result copied. Paste it anywhere!");
        window.setTimeout(() => setTopicsToast(null), 2600);
      }
    } catch {
      /* share cancelled */
    }
    sendEvent({ type: "share", tag: "quiz" });
  }

  // v4: share today's front page as a paste-anywhere text ledger (masthead, top three
  // headlines, the promise line, one link). Same loop as the quiz grid.
  async function shareEdition(which: InSnipCard[] = filteredCards.length ? filteredCards : cards, title = "The Daily Edition") {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
    const dayLabel = new Date().toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
    const text = editionShareText(which, dayLabel, paperStats(which), `${origin}/read`);
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: `InSnip, ${title}`, text });
      } else {
        await navigator.clipboard?.writeText(text);
        setTopicsToast("Front page copied. Paste it anywhere!");
        window.setTimeout(() => setTopicsToast(null), 2600);
      }
    } catch {
      /* share cancelled */
    }
    sendEvent({ type: "share", tag: "edition" });
  }

  function refreshFeed() {
    haptic(8);
    if (serverDrivenRef.current) {
      void loadServerRound("refresh");
    } else if (typeof window !== "undefined") {
      window.location.reload();
    }
  }

  async function showCaughtUp() {
    // Make sure the whole final round counts, then read the SERVER's authoritative
    // total (the client round-scoped list undercounts), so "you read N today" is exact.
    if (cards.length) await markSeen(cards.map((c) => c.id));
    const ins = await fetchInsights();
    if (ins) setInsights(ins);
    setHasMoreCards(false);
    setShowInsight(false);
    setCaughtUp(true);
  }

  // Build the prefs payload from the reader's "Make It Yours" choices + learned
  // signals: followed categories (and interests / positive tuning) weight up;
  // muted categories are hidden (the server keeps a diversity floor).
  function currentPrefs(): { preferred: string[]; prefs: TopicPrefs } {
    const lists = topicListsRef.current;
    const followed = lists.preferred ?? [];
    const storedInterests = readStored<string[]>("insnip.interests", []);
    const storedSignals = readStored<Record<string, number>>("insnip.topicSignals", {});
    const preferred = [
      ...followed,
      ...storedInterests,
      ...Object.entries(storedSignals).filter(([, v]) => v > 0).map(([k]) => k),
    ];
    return { preferred, prefs: { preferred, muted: lists.muted ?? [] } };
  }

  async function loadServerRound(kind: "next" | "refresh") {
    // "next" completes the current round (server marks it seen) and serves the
    // next 10; "refresh" abandons the current round for a fresh non-overlapping 10.
    const { preferred, prefs } = currentPrefs();
    const mode = feedModeRef.current;
    const round =
      kind === "refresh"
        ? await refreshSession(preferred, prefs, viewedIds, mode) // only the read cards are consumed
        : await nextSession(preferred, prefs, mode);
    if (round && round.cards.length) {
      setCards(round.cards);
      setHasMoreCards(round.hasMore);
      feedLoadedAtRef.current = Math.floor(Date.now() / 1000); // reset the "new cards" baseline
      setFreshCount(0);
      // "Next" advances the round-of-the-day; "refresh" re-rolls the same round.
      if (kind === "next") {
        const rnd = Math.max(readDayRound(), 1) + 1;
        setDayRound(rnd);
        writeDayRound(rnd);
      }
      const first = landOn(round.cards) ?? round.cards[0];
      stopSpeech();
      setBodyExpanded(false);
      setActiveId(first.id);
      setViewedIds([first.id]);
      setReactions({});
      setShowInsight(false);
      return;
    }
    // Caught up: no unseen cards left right now. Show the end-of-day screen with
    // the day's analysis rather than leaving the reader stuck.
    await showCaughtUp();
  }

  // Escape dismisses the daily insight (the scrim click already does).
  useEffect(() => {
    if (!insightOfDay) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") dismissInsightOfDay(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightOfDay]);

  function dismissInsightOfDay() {
    try {
      localStorage.setItem("insnip.insightSeen", JSON.stringify(isoDay(new Date())));
    } catch {
      /* storage unavailable, it'll just show again next open, no harm */
    }
    setInsightOfDay(null);
  }

  // Switch the session feed ordering (Latest ⇄ Trending) and immediately rebuild the deck in
  // that mode. Session-only: the ref is set synchronously so the refresh below uses the new mode.
  function switchFeedMode(mode: FeedMode) {
    setMobileMenuOpen(false);
    if (mode === feedModeRef.current) return;
    feedModeRef.current = mode;
    setFeedMode(mode);
    haptic(8);
    if (serverDrivenRef.current) void loadServerRound("refresh");
  }

  function startNextRound() {
    if (!activeCard) return;
    if (serverDrivenRef.current) { void loadServerRound("next"); return; }
    const baseIndex = activeIndex === -1
      ? filteredCards.findIndex((card) => card.id === activeCard.id)
      : activeIndex;
    const nextStart = nextRoundStartIndex(baseIndex, filteredCards.length, READ_TARGET);
    if (nextStart == null) {
      setShowInsight(false);
      return;
    }
    const nextCard = filteredCards[nextStart] ?? activeCard;
    stopSpeech();
    setBodyExpanded(false);
    setActiveId(nextCard.id);
    setViewedIds([nextCard.id]);
    setReactions({});
    setShowInsight(false);
  }

  function toggleTheme() {
    setTheme((current) => current === "dark" ? "light" : "dark");
  }

  // ---- Command palette (Ctrl/Cmd+K): every destination, one keystroke. ----
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  // Palette doubles as search: live story hits for the typed query (top 5), appended
  // under the command rows. Members only (search stays a sign-in gated feature) and
  // fully best-effort, the palette keeps working offline.
  const [paletteHits, setPaletteHits] = useState<InSnipCard[]>([]);
  useEffect(() => {
    function onPaletteKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteQuery("");
        setPaletteOpen((open) => !open);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    }
    window.addEventListener("keydown", onPaletteKey);
    return () => window.removeEventListener("keydown", onPaletteKey);
  }, []);
  useEffect(() => {
    const q = paletteQuery.trim();
    if (!paletteOpen || q.length <= 2 || !sessionApiEnabled() || !member) {
      setPaletteHits([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        const res = await searchCards(q);
        if (!cancelled) setPaletteHits(res.cards.slice(0, 5));
      } catch {
        if (!cancelled) setPaletteHits([]);
      }
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [paletteQuery, paletteOpen, member]);
  // Open a palette search hit exactly the way search results open (the search detail view).
  function openPaletteCard(card: InSnipCard) {
    setSearchDetail(card);
    setSearchOpen(true);
  }

  /** Open any card from the wire/ticker/history: jump in the deck when it is part of
   *  today's round, otherwise show it the way a search result opens. */
  function openAnyCard(card: InSnipCard) {
    if (filteredCards.some((f) => f.id === card.id)) visitCard(card.id);
    else openPaletteCard(card);
  }

  // The ticker turns over on its own, one fresh headline at a time.
  useEffect(() => {
    const t = window.setInterval(() => setTickerIx((i) => (i + 1) % 1000), 6000);
    return () => window.clearInterval(t);
  }, []);

  function toggleSlideshow() {
    setSlideshowMode((mode) => (mode === "off" ? "essence" : "off"));
  }

  // Slideshow timer: essence/why for 6s -> body for 9s -> advance card
  useEffect(() => {
    if (slideshowMode === "off") {
      setBodyExpanded(false);
      return;
    }
    if (slideshowMode === "essence") {
      setBodyExpanded(false);
      const t = window.setTimeout(() => setSlideshowMode("body"), 6000);
      return () => window.clearTimeout(t);
    }
    if (slideshowMode === "body") {
      setBodyExpanded(true);
      const t = window.setTimeout(() => {
        move(1);
        setSlideshowMode("essence");
      }, 9000);
      return () => window.clearTimeout(t);
    }
  }, [slideshowMode, activeId]);

  // "Play the drop", continuous hands-free audio briefing. Effect-driven like the
  // slideshow, so there are no stale closures: it speaks the active card and, on
  // end, advances to the next (which re-runs this effect). Stops at the feed end.
  useEffect(() => {
    if (!briefingActive) return;
    if (typeof window === "undefined" || !window.speechSynthesis || !activeCard) return;
    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(
      `${activeCard.headline}. ${activeCard.essence}. ${activeCard.body}`
    );
    utter.lang = "en-AU";
    utter.rate = 1.03 * briefingRate;
    const voice = pickAussieVoice();
    if (voice) utter.voice = voice;
    utter.onend = () => {
      const idx = filteredCards.findIndex((card) => card.id === activeCard.id);
      if (idx >= 0 && idx < filteredCards.length - 1) {
        visitCard(filteredCards[idx + 1].id);
      } else {
        setBriefingActive(false);
        setBriefingSession(false);
        setSpeechState("idle");
      }
    };
    utter.onerror = () => setBriefingActive(false);
    ttsRef.current = utter;
    synth.cancel();
    synth.speak(utter);
    setSpeechState("speaking");
    return () => {
      // Detach BOTH handlers before cancelling. Cancelling a speaking utterance fires an
      // "error" (interrupted) event, so leaving onerror attached made every manual skip or
      // swipe during a briefing stop the playback it was supposed to advance.
      utter.onend = null;
      utter.onerror = null;
      synth.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefingActive, activeId, briefingRate]);

  // Open/close the whole briefing session (the speaker fab).
  function toggleBriefing() {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (briefingSession) {
      setBriefingSession(false);
      setBriefingActive(false);
      stopSpeech();
    } else {
      stopSpeech();
      setBriefingSession(true);
      setBriefingActive(true);
    }
  }

  // Mini-player play/pause: stop or resume speech without ending the session.
  function toggleBriefingPlay() {
    if (briefingActive) {
      setBriefingActive(false);
      stopSpeech();
    } else {
      setBriefingActive(true);
    }
  }
  function closeBriefing() {
    setBriefingSession(false);
    setBriefingActive(false);
    stopSpeech();
  }
  function cycleBriefingRate() {
    setBriefingRate((r) => (r >= 1.5 ? 1 : r >= 1.25 ? 1.5 : 1.25));
  }

  function shareWeek() {
    const cw = weeklyReadCount();
    const saved = savedIds.length;
    // Carry the referral id so the week-share doubles as an invite (growth loop).
    const url = inviteLink();
    const text = `My week in InSnip\n${streak.count} day streak, ${cw} ${cw === 1 ? "story" : "stories"} this week${saved ? `, ${saved} saved` : ""}\nPure signal, no noise:`;
    if (typeof navigator !== "undefined" && navigator.share) {
      void navigator.share({ title: "My week in InSnip", text, url }).catch(() => {});
    } else if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(`${text} ${url}`).catch(() => {});
    }
  }

  function pickAussieVoice(): SpeechSynthesisVoice | null {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    // Prefer named AU voices, then any en-AU, then en-GB, then any English
    const auNamed = voices.find((v) => /karen|catherine|james|lee|matilda|russell|jenny.*au|aria.*au/i.test(v.name) && v.lang.toLowerCase().startsWith("en"));
    if (auNamed) return auNamed;
    const enAU = voices.find((v) => v.lang.toLowerCase() === "en-au" || v.lang.toLowerCase().startsWith("en-au"));
    if (enAU) return enAU;
    const enGB = voices.find((v) => v.lang.toLowerCase().startsWith("en-gb"));
    if (enGB) return enGB;
    return voices.find((v) => v.lang.toLowerCase().startsWith("en")) ?? null;
  }

  function speakWithAussieVoice(card: InSnipCard) {
    const utterance = new SpeechSynthesisUtterance(`${card.headline}. ${card.essence}. ${card.body} Why it matters. ${card.whyItMatters}`);
    utterance.rate = 1.02;
    utterance.pitch = 1;
    utterance.lang = "en-AU";
    const voice = pickAussieVoice();
    if (voice) utterance.voice = voice;
    utterance.onend = () => setSpeechState("idle");
    utterance.onerror = () => setSpeechState("idle");
    ttsRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setSpeechState("speaking");
  }

  function toggleListen(card: InSnipCard) {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (briefingActive) setBriefingActive(false); // single-card listen stops the briefing
    if (speechState === "speaking") { stopSpeech(); return; }
    // Voices may load asynchronously on some browsers · wait one tick if empty
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) {
      const onVoices = () => {
        window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
        speakWithAussieVoice(card);
      };
      window.speechSynthesis.addEventListener("voiceschanged", onVoices);
      // Trigger the load
      window.speechSynthesis.getVoices();
      // Fallback in case the event never fires
      window.setTimeout(() => {
        if (!ttsRef.current) speakWithAussieVoice(card);
      }, 400);
      return;
    }
    speakWithAussieVoice(card);
  }

  // "Play the Brief": narrate the day's Brief as a spoken 60-second briefing (a commute/eyes-free
  // habit anchor). Reuses the same Aussie-voice TTS as per-card Listen.
  function speakBrief(brief: InSnipCard) {
    const points = brief.body.split("\n").map((l) => l.replace(/^[•\-\s]+/, "").trim()).filter(Boolean).join(". ");
    const utter = new SpeechSynthesisUtterance(`Here is your Brief. ${brief.essence}. ${points}. The through line. ${brief.whyItMatters}`);
    utter.rate = 1.02;
    utter.pitch = 1;
    utter.lang = "en-AU";
    const voice = pickAussieVoice();
    if (voice) utter.voice = voice;
    utter.onend = () => setSpeechState("idle");
    utter.onerror = () => setSpeechState("idle");
    ttsRef.current = utter;
    window.speechSynthesis.speak(utter);
    setSpeechState("speaking");
  }

  function toggleBriefAudio(brief: InSnipCard) {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (speechState === "speaking") { stopSpeech(); return; }
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) {
      const onVoices = () => { window.speechSynthesis.removeEventListener("voiceschanged", onVoices); speakBrief(brief); };
      window.speechSynthesis.addEventListener("voiceschanged", onVoices);
      window.speechSynthesis.getVoices();
      window.setTimeout(() => { if (!ttsRef.current) speakBrief(brief); }, 400);
      return;
    }
    speakBrief(brief);
  }

  // First-party subscribe: the email goes straight to our own worker (POST /subscribe),
  // no Beehiiv tab, no confirm step. The chosen editions still land in localStorage so
  // the routing survives until the sender learns to read them.
  async function submitSubscribe(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValidEmail(emailDraft)) { setSubscribeStatus("invalid"); return; }
    setSubscribeStatus("saving");
    localStorage.setItem("insnip.waitlistEmail", JSON.stringify(emailDraft.trim()));
    const picked = editions.length ? editions : EDITIONS.map((e) => e.id);
    localStorage.setItem("insnip.editions", JSON.stringify(picked));
    const source = subscribeOpen || caughtUp ? "roundend" : "rail";
    const result = await subscribeEmail(emailDraft, source);
    setSubscribeStatus(result === "ok" ? "saved" : result === "invalid_email" ? "invalid" : "error");
  }

  // The Desk's compact join line (same first-party flow as the rail form, minus the
  // edition picker: an empty pick means "all editions"). Fire-and-forget by design:
  // the Desk's own inline "You're in" is the whole ceremony.
  function deskJoin(email: string): boolean {
    if (!isValidEmail(email)) return false;
    localStorage.setItem("insnip.waitlistEmail", JSON.stringify(email.trim()));
    const picked = editions.length ? editions : EDITIONS.map((e) => e.id);
    localStorage.setItem("insnip.editions", JSON.stringify(picked));
    void subscribeEmail(email, "desk");
    setSubscribeStatus("saved");
    return true;
  }

  // Front page first: the Desk opens once per day when a round is ready. Shared-card
  // deep links and #game challenges land directly on their target instead.
  //
  // So does anyone who ARRIVED ON A STORY (a share, a referral, a push): they have had a
  // front page, it was the share page, and they pressed a button that said "Read the other
  // nine". Measured on a phone 2026-09-27 before this line existed: that press opened the
  // Desk, closing the Desk opened the app door, and the first swipe after that opened the
  // welcome, three full-screen covers before a second story, on the one visitor who had
  // already shown they wanted to read. That is the wall in front of the story again, built
  // out of three well-meaning pieces.
  useEffect(() => {
    if (deskAutoRef.current || !filteredCards.length || showSavedOnly) return;
    deskAutoRef.current = true;
    if (
      sharedCardId ||
      arrivalAskRef.current ||
      arrivedOnAStory() ||
      parseCardLink(window.location.hash) ||
      parseCardLink(window.location.search) ||
      wantsGameFromHash(window.location.hash)
    ) return;
    // "/read#edition" (Today's news, from the home) and "front page first" open it on every
    // visit; otherwise the front page greets the first visit of the day.
    const wantsEdition = /(?:^|[#&?])edition(?:[&=]|$)/.test(window.location.hash);
    if (!wantsEdition && !frontPageFirst) {
      try {
        if (localStorage.getItem("insnip.deskSeen") === localDateKey()) return;
      } catch { /* best-effort */ }
    }
    setDeskOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCards.length, sharedCardId, showSavedOnly]);

  const closeDesk = useCallback(() => {
    setDeskOpen(false);
    try { localStorage.setItem("insnip.deskSeen", localDateKey()); } catch { /* best-effort */ }
    // "/read#edition" has done its work; a reload should not put the page back over a story.
    try {
      if (/(?:^|[#&?])edition(?:[&=]|$)/.test(window.location.hash)) {
        history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    } catch { /* fine */ }
  }, []);

  // While the front page is up: the most-read list once, and a look at the newest stories
  // every three minutes, so the page can say how many landed since it was opened (and never
  // swaps them in under the reader's eyes; the line offers to set the page afresh).
  // A look at the wire: how many stories have landed that the page does not carry. The first
  // look after the page is set only takes the baseline, so the page's own stories never count.
  const deskLook = useCallback(async () => {
    const t = await fetchLatestTeasers(12).catch(() => [] as LatestTeaser[]);
    const ids = t.map((x) => x.id);
    if (!deskBaselineRef.current) {
      deskBaselineRef.current = new Set([...ids, ...(deskPaperRef.current?.cards ?? []).map((c) => c.id)]);
      setDeskFresh(0);
      return;
    }
    const base = deskBaselineRef.current;
    setDeskFresh(ids.filter((id) => !base.has(id)).length);
  }, []);
  // Set the page: once when it opens, again when the reader asks (the fresh line). Nothing is
  // swapped under the reader's eyes between those two moments.
  const setPaper = useCallback(async (how: "open" | "again") => {
    setDeskPaperState(how === "open" ? "loading" : "setting");
    const p = await fetchEdition(12).catch(() => null);
    deskPaperRef.current = p;
    setDeskPaper(p);
    setDeskPaperState(p ? "ready" : "failed");
    deskBaselineRef.current = null;
    await deskLook();
  }, [deskLook]);
  useEffect(() => {
    if (!deskOpen) { setDeskFresh(0); deskBaselineRef.current = null; return; }
    void setPaper("open");
    const id = window.setInterval(() => void deskLook(), 180_000);
    return () => window.clearInterval(id);
  }, [deskOpen, setPaper, deskLook]);
  // Open a story from the page in the reader. The page's stories are mostly outside the
  // reader's round, so the push-tap door takes the story (already in hand, no fetch), leads
  // with it and lines the next unread stories from the page up behind it, the way a
  // notification lands; from there the reader is in the ordinary card flow.
  const openFromPaper = useCallback((id: string, chain: string[], listen: boolean) => {
    const paperCards = deskPaperRef.current?.cards ?? [];
    if (!chain.length && !listen && filteredCards.some((c) => c.id === id)) { visitCard(id); return; }
    const next = chain.filter((c) => c !== id).slice(0, 5);
    const hash = `card=${id}${next.length ? `&next=${next.join(",")}` : ""}`;
    window.dispatchEvent(new CustomEvent("insnip:pushnav", { detail: { url: `/read#${hash}`, cardId: id, kind: "", hash, from: "home", cards: paperCards, listen } }));
  }, [filteredCards, visitCard]);
  const toggleFrontPageFirst = useCallback(() => {
    setFrontPageFirst((v) => {
      const next = !v;
      try { localStorage.setItem("insnip.frontPageFirst", next ? "1" : "0"); } catch { /* fine */ }
      return next;
    });
  }, []);
  // Every story the reader has opened, this round and before: the page spans rounds.
  const deskReadIds = useMemo(
    () => (deskOpen ? Array.from(new Set([...viewedIds, ...seenRef.current])) : viewedIds),
    [deskOpen, viewedIds],
  );

  const PULL_TRIGGER = 44;

  // Pull-to-refresh (Inshorts-style): bring the freshest unseen round when the
  // reader drags down from the top of the insights view (the next round follows the
  // 10-card + category + never-seen rules; the feed is already recency-first).
  async function doPullRefresh() {
    setPullY(0);
    setRefreshing(true);
    haptic([8, 30, 8]);
    try {
      if (serverDrivenRef.current) {
        await loadServerRound("refresh");
      } else if (typeof window !== "undefined") {
        window.location.reload();
      }
    } finally {
      setRefreshing(false);
    }
  }

  // Whether a swipe in this direction can actually go somewhere (else we rubber-band).
  // A topic's last story still swipes on (onto its end card); the end card itself waits.
  const swipeBlocked = (delta: number) =>
    delta > 0
      ? topicEnd || !((serverDrivenRef.current && !showSavedOnly) || activeIndex < filteredCards.length - 1 || (activeTag !== "All" && !showSavedOnly))
      : activeIndex <= 0 && !topicEnd;

  // Release without a commit: let the card spring back to centre, riding the spring's own
  // trajectory when the engine can bake it (planGlide -> linear() easing); the legacy fixed
  // curve everywhere else. Velocity hands off from the finger either way.
  function settleSwipe() {
    if (supportsLinearEasing && dragX) {
      const plan = planGlide(dragX, 0, swipeVel.current.v, springFor(0.42, 0.14));
      setGlideRide(plan ? { ms: plan.durationMs, easing: plan.easing } : null);
    } else {
      setGlideRide(null);
    }
    setSwipeSettling(true);
    setDragX(0);
  }

  // Commit: fling the card off in the drag direction, then advance once it is gone. move()
  // still owns all the round/insight logic; we only animate the hand-off (reduced motion skips it).
  function flingSwipe(delta: number) {
    haptic(8);
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDragX(0);
      move(delta);
      return;
    }
    const off = (typeof window !== "undefined" ? window.innerWidth : 400) * 1.15;
    const target = delta > 0 ? -off : off;
    // The exit rides the finger's own throw: duration from remaining distance + velocity
    // (Apple's guidance: never wait for a spring's asymptotic tail), trajectory baked into
    // linear() so the whole journey runs on the compositor. Fallback: the legacy 240ms.
    let rideMs = 240;
    if (supportsLinearEasing) {
      const dur = exitDurationSec(Math.abs(target - dragX), swipeVel.current.v);
      const plan = planGlide(dragX, target, swipeVel.current.v, springFor(dur, EXIT_BOUNCE), (x) => Math.abs(x) >= off * 0.96);
      if (plan) {
        setGlideRide({ ms: plan.durationMs, easing: plan.easing });
        rideMs = plan.durationMs;
      } else {
        setGlideRide(null);
      }
    }
    setSwipeSettling(true);
    setDragX(target); // advancing (delta>0) flings the card left
    if (flingTimer.current) clearTimeout(flingTimer.current);
    flingTimer.current = setTimeout(() => {
      setSwipeSettling(false);
      setDragX(0);
      setGlideRide(null);
      move(delta); // re-keys the card -> the fresh card plays its entrance
      flingTimer.current = null;
    }, rideMs + 20);
  }

  function handleTouchStart(event: React.TouchEvent) {
    if (flingTimer.current) {
      clearTimeout(flingTimer.current);
      flingTimer.current = null;
    }
    touchStartX.current = event.touches[0]?.clientX ?? null;
    touchStartY.current = event.touches[0]?.clientY ?? null;
    swipeVel.current = { x: event.touches[0]?.clientX ?? 0, t: performance.now(), v: 0 };
    setSwipeSettling(false); // track the finger 1:1 until release
    setGlideRide(null);
  }

  function handleTouchMove(event: React.TouchEvent) {
    if (bodyExpanded || refreshing) return;
    const startY = touchStartY.current;
    const startX = touchStartX.current;
    const t = event.touches[0];
    if (startY == null || startX == null || !t) return;
    const dy = t.clientY - startY;
    const dxRaw = t.clientX - startX;
    const dx = Math.abs(dxRaw);
    // A downward, mostly-vertical drag from the top ~60%: pull-to-refresh, but ONLY when the
    // reading copy is already at its top. If the copy can still scroll up, let it scroll natively
    // (a downward drag mid-article is a read-scroll, not a refresh) so the card never jumps.
    if (dy > 0 && dy > dx + 4 && startY < window.innerHeight * 0.6) {
      const copy = copyRef.current;
      if (copy && copy.scrollTop > 0) { if (pullY) setPullY(0); return; }
      if (dragX) setDragX(0);
      setPullY(Math.min(dy * 0.6, 120)); // rubber-band resistance (lighter = easier)
      return;
    }
    if (pullY) setPullY(0);
    // A mostly-horizontal drag: the card follows the finger, with resistance at the ends.
    if (dx > Math.abs(dy) && dx > 6) {
      const blocked = swipeBlocked(dxRaw < 0 ? 1 : -1);
      // Velocity rides along (EMA-smoothed): the release animation inherits the throw
      // instead of resetting to a canned curve, the single biggest "feels native" ingredient.
      const nowT = performance.now();
      const prev = swipeVel.current;
      const dt = Math.max(1, nowT - prev.t);
      const inst = (t.clientX - prev.x) / dt;
      swipeVel.current = { x: t.clientX, t: nowT, v: prev.t ? prev.v * 0.4 + inst * 0.6 : inst };
      setDragX(blocked ? dxRaw * 0.25 : dxRaw);
    }
  }

  function handleTouchEnd(event: React.TouchEvent) {
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    const endX = event.changedTouches[0]?.clientX;
    const endY = event.changedTouches[0]?.clientY;
    touchStartX.current = null;
    touchStartY.current = null;
    // Release a sufficient pull -> refresh to the latest round. Not in the saved view: that's an
    // independent reading list, not the live deck, so there's no round to refresh to.
    if (!bodyExpanded && pullY >= PULL_TRIGGER && !showSavedOnly) {
      void doPullRefresh();
      return;
    }
    if (pullY) setPullY(0);
    if (startX == null || endX == null || startY == null || endY == null) {
      if (dragX) settleSwipe();
      return;
    }
    const dx = endX - startX;
    const dy = endY - startY;
    // Vertical swipe: up uncovers the full story, down returns to insights. But when the reading
    // copy can scroll, a vertical drag is a read-scroll, NOT a mode switch, so only toggle from a
    // scroll boundary (up-swipe once at the very bottom, down-swipe once at the very top).
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) >= SWIPE_THRESHOLD) {
      const copy = copyRef.current;
      const canScroll = !!copy && copy.scrollHeight - copy.clientHeight > 8;
      if (canScroll) {
        const atTop = copy.scrollTop <= 0;
        const atBottom = copy.scrollTop >= copy.scrollHeight - copy.clientHeight - 2;
        // Scrolling down to read (up-drag) but not yet at the bottom, or scrolling up (down-drag)
        // but not yet at the top: let the native scroll own it and do not switch modes.
        if ((dy < 0 && !atBottom) || (dy > 0 && !atTop)) {
          if (dragX) settleSwipe();
          return;
        }
      }
      if (dragX) settleSwipe();
      haptic(6);
      setBodyExpanded(dy < 0);
      return;
    }
    // Horizontal: commit past the threshold (fling + advance), otherwise spring back.
    if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      const delta = dx < 0 ? 1 : -1;
      // Back off a topic's end card: the card stays, the end card goes.
      if (topicEnd && delta < 0) { settleSwipe(); setTopicEnd(false); return; }
      if (swipeBlocked(delta)) settleSwipe();
      else flingSwipe(delta);
      return;
    }
    settleSwipe();
  }

  useEffect(() => {
    function handler(event: KeyboardEvent) {
      // The Signal or Noise overlay owns the keyboard while open (its own Escape + swipe keys),
      // so no feed shortcut leaks through underneath it.
      if (gameOpen) return;
      // Feedback is the topmost surface when open, and it holds a text field: swallow every feed
      // shortcut under it so typing a note never also flips a card.
      if (feedbackFor) { if (event.key === "Escape") setFeedbackFor(null); return; }
      // The browse overview is a modal: swallow feed shortcuts under it, close it on Escape.
      if (overviewOpen) { if (event.key === "Escape") setOverviewOpen(false); return; }
      // The Good News section is a modal too: same treatment.
      if (goodNewsOpen) { if (event.key === "Escape") setGoodNewsOpen(false); return; }
      if (insightsOpen) { if (event.key === "Escape") setInsightsOpen(false); return; }
      // The Deep Dive owns the keyboard while open (its own Escape closes it).
      if (deepDiveOpen) return;
      const target = event.target as HTMLElement | null;
      const inField = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (event.key === "Escape") {
        if (gateFeature || signInOpen) closeSignIn();
        else if (quotePill) setQuotePill(null);
        else if (trustOpen) setTrustOpen(false);
        else if (searchOpen) { if (searchDetail) setSearchDetail(null); else closeSearch(); }
        else if (askOpen) closeAsk();
        else if (topicsOpen) setTopicsOpen(false);
        else if (sourcesOpen) setSourcesOpen(false);
        else if (shortcutsOpen) setShortcutsOpen(false);
        else if (subscribeOpen) setSubscribeOpen(false);
        // Masthead popovers and every remaining full-screen surface: Escape must always
        // mean "close the thing on top". A modal Escape cannot reach is a trap; the Brief
        // (and anything opened after it) used to strand the whole page behind its overlay.
        else if (aaOpen) setAaOpen(false);
        else if (accountMenuOpen) { setAccountMenuOpen(false); setInviteOpen(false); }
        else if (briefOpen) { stopSpeech(); setBriefOpen(false); }
        else if (digestOpen) setDigestOpen(false);
        else if (recallOpen) setRecallOpen(false);
        else if (libraryOpen) setLibraryOpen(false);
        else if (proOpen) setProOpen(false);
        else if (mobileMenuOpen) setMobileMenuOpen(false);
        else if (showInsight) setShowInsight(false);
        else if (focusMode) setFocusMode(false);
        return;
      }
      // Touch devices get no single-letter shortcuts. With the on-screen keyboard up (or a
      // stray hardware key) a typed "e" or "g" teleported a phone reader into the Edition
      // or the game with no way to know why; the audit reproduced exactly that. Escape
      // handling above stays for phones with keyboards attached.
      if (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches) return;
      if (event.key === "/" && !inField && !searchOpen) { event.preventDefault(); openSearch(); return; }
      if (inField) return;
      if (!activeCard) return;
      if (event.key === "ArrowRight") { event.preventDefault(); move(1); }
      else if (event.key === "ArrowLeft") { event.preventDefault(); move(-1); }
      else if (event.key.toLowerCase() === "s") { event.preventDefault(); toggleSave(activeCard.id); }
      else if (event.key.toLowerCase() === "r") { event.preventDefault(); setSourcesOpen((c) => !c); }
      else if (event.key.toLowerCase() === "t") { event.preventDefault(); toggleTheme(); }
      else if (event.key.toLowerCase() === "f") { event.preventDefault(); setFocusMode((c) => !c); }
      else if (event.key.toLowerCase() === "l") { event.preventDefault(); toggleListen(activeCard); }
      else if (event.key.toLowerCase() === "b") { event.preventDefault(); setBionicMode((c) => !c); }
      // Reader vocabulary: j/k step stories (vim-style), d dives deeper, g opens the game.
      // "s" already meant save (matching the spec), so it stays exactly as bound above.
      // Modifier chords stay untouched (Ctrl/Cmd+K is the palette, not "previous card").
      else if (event.ctrlKey || event.metaKey || event.altKey) { /* leave browser/app chords alone */ }
      else if (event.key.toLowerCase() === "j") { event.preventDefault(); move(1); }
      else if (event.key.toLowerCase() === "k") { event.preventDefault(); move(-1); }
      else if (event.key.toLowerCase() === "d") { if (hasDeepDiveFor(activeCard)) { event.preventDefault(); openDeepDive(); } }
      else if (event.key.toLowerCase() === "g") { event.preventDefault(); setGameOpen(true); }
      // v4: e opens today's front page (the Edition desk), the reader's daily anchor.
      else if (event.key.toLowerCase() === "e") { event.preventDefault(); setDeskOpen(true); }
      else if (event.key === "?") { event.preventDefault(); setShortcutsOpen((c) => !c); }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard, move, focusMode, shortcutsOpen, showInsight, sourcesOpen, subscribeOpen, speechState, askOpen, topicsOpen, searchOpen, searchDetail, openSearch, closeSearch, gameOpen, trustOpen, overviewOpen, goodNewsOpen, insightsOpen, deepDiveOpen, quotePill, openDeepDive, gateFeature, signInOpen, closeSignIn, aaOpen, accountMenuOpen, briefOpen, digestOpen, recallOpen, libraryOpen, proOpen, mobileMenuOpen, feedbackFor]);

  // Living color: sample the active card's image for the ambient glow, falling back to brand
  // gold. todWarm nudges the sampled color by local hour (cool mornings, warm evenings).
  useEffect(() => {
    const url = activeCard ? resolveImageUrl(activeCard.imageUrl) : "";
    if (!url) { setAmbient(todWarm(GOLD.r, GOLD.g, GOLD.b)); return; }
    let cancelled = false;
    void loadDominantColor(url).then((c) => {
      if (cancelled) return;
      const base = c ?? GOLD;
      setAmbient(todWarm(base.r, base.g, base.b));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard?.imageUrl]);

  // Tally distinct cards read this week for the "Your Week" recap (deduped by id in weekStats).
  // The feature tour drives real navigation as a demo, so nothing it passes through counts
  // here either. Without this guard the week recap and the signal diet wheel both opened on
  // "2 read" for a reader who had read nothing (the same lie visitCard already refuses).
  useEffect(() => {
    if (!activeId || tourOpenRef.current) return;
    recordWeeklyRead(activeId);
    // v3: long-horizon per-category counters (deduped by card id) feed the signal diet
    // wheel and the reader archetype. Best-effort; a miss just means smaller numbers.
    const c = cards.find((x) => x.id === activeId);
    if (c) trackCardRead(c.id, String(c.category ?? ""), Boolean(c.goodNews) || isFlaggedGoodNews(c));
  }, [activeId, cards]);

  // v3: the daily game survives pipeline outages. When the worker has no round
  // for today (the fetch came back empty), build the deterministic local
  // fallback from today's cards once they are in, same round for every
  // reader, scored locally.
  useEffect(() => {
    if (!gameFetchMissed || gameToday || cards.length === 0) return;
    const local = buildLocalGame(cards, new Date().toLocaleDateString("en-CA"));
    if (local) setGameToday(local);
  }, [gameFetchMissed, gameToday, cards]);

  // A challenge deep link (#game) drops the recipient straight into today's round.
  // Wait for the game set to load so the overlay opens ready, not on its empty state.
  // v4 fix: also listen for hashchange. A reader ALREADY on the site who follows a
  // challenge link (or any in-app #game navigation) is a same-document navigation, so
  // the mount-time check alone never saw it and the link silently did nothing.
  const gameHashRef = useRef<boolean>(
    typeof window !== "undefined" && wantsGameFromHash(window.location.hash),
  );
  useEffect(() => {
    const maybeOpen = () => {
      if (wantsGameFromHash(window.location.hash)) gameHashRef.current = true;
      if (!gameHashRef.current || !gameToday) return;
      gameHashRef.current = false;
      try {
        history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        /* cosmetic only */
      }
      setGameOpen(true);
    };
    maybeOpen();
    window.addEventListener("hashchange", maybeOpen);
    return () => window.removeEventListener("hashchange", maybeOpen);
  }, [gameToday]);

  // v4: #quiz mirrors #game. A shared quiz result links straight into today's round;
  // wait for the feed so the Front Page Quiz fallback has stories to build from.
  const quizHashRef = useRef<boolean>(
    typeof window !== "undefined" && wantsQuizFromHash(window.location.hash),
  );
  useEffect(() => {
    if (status !== "ready") return;
    const maybeOpenQuiz = () => {
      if (wantsQuizFromHash(window.location.hash)) quizHashRef.current = true;
      if (!quizHashRef.current) return;
      quizHashRef.current = false;
      try {
        history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {
        /* cosmetic only */
      }
      void openQuiz();
    };
    maybeOpenQuiz();
    window.addEventListener("hashchange", maybeOpenQuiz);
    return () => window.removeEventListener("hashchange", maybeOpenQuiz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Teach the product once the reader is ON a story, not before it and not three stories later.
  // Opening the 8 step tour and the 3 step onboarding on arrival put up to 11 interruptions
  // between a reader and their first headline; waiting for three stories fixed that but pushed
  // the welcome past the point most readers leave, so it was written for an audience that had
  // already gone. It now arms on card one and opens after TOUR_DWELL_MS, which is enough to read
  // the first headline and see its photograph first. A referral arrival is still never
  // interrupted, and a due Insight of the Day still owns its one slot.
  // The blockers, mirrored into a ref so the deferred intro can re-check them when it FIRES and
  // not only when it was armed. Between the two there are seconds in which the reader may open
  // the edition desk, a deep dive or the game, and the welcome must never land on top of one.
  const introBlockedRef = useRef(false);
  // `authWall` joins this list because the sign-in invitation now waits for three stories, and
  // the welcome waits for one plus a dwell, so a slow dwell can land the welcome ON TOP of the
  // invitation. It wins that race silently: .drawer-scrim is z-index 220 and .gate-scrim is
  // 120, so the invitation is not hidden in any way a check on "did it render" would notice.
  // Postponed, not cancelled, exactly like every other blocker here: the welcome runs once the
  // reader has signed in or waved the invitation away.
  // `phoneDoorOpen` is here for the same reason as authWall: the door is a full-screen layer
  // and a dwell can end while it is up, which would put the welcome on top of the one ask this
  // surface makes. Postponed, like the others, never cancelled.
  introBlockedRef.current =
    tourOpen || onboardingOpen || deepDiveOpen || gameOpen || showInsight || deskOpen || authWall || phoneDoorOpen;
  const introTimerRef = useRef<number | null>(null);
  const introArmedRef = useRef(false);
  useEffect(
    () => () => {
      if (introTimerRef.current !== null) window.clearTimeout(introTimerRef.current);
    },
    [],
  );
  useEffect(() => {
    if (isNativeApp || insightDue) return;
    // The comment above has promised for some time that a referral arrival is never
    // interrupted, and nothing here enforced it: a phone arriving from a shared link got the
    // welcome 3.5 seconds into story one, on its first swipe. Someone who came in on a story
    // already knows what this is; they were just reading it. They get the reader, not the tour.
    if (arrivedOnAStory()) return;
    if (viewedIds.length < TOUR_AFTER_CARDS) return;
    if (introArmedRef.current) return; // arm once; swiping on must not keep postponing it
    introArmedRef.current = true;
    // The dwell is deliberately not cancelled by a blocker, only postponed by it. A reader who
    // arrives into the edition desk would otherwise lose the walkthrough permanently, because
    // the one moment it was armed for was a moment it was not allowed to use. And when the
    // blocker clears, the dwell starts AGAIN from there: an arrival through the edition desk
    // should still get its few seconds of reading before the welcome, not have them counted
    // while the reader was looking at something else entirely.
    let wasBlocked = false;
    const fire = () => {
      if (introBlockedRef.current) {
        wasBlocked = true;
        introTimerRef.current = window.setTimeout(fire, 400);
        return;
      }
      if (wasBlocked) {
        wasBlocked = false;
        introTimerRef.current = window.setTimeout(fire, TOUR_DWELL_MS);
        return;
      }
      // ONBOARDING FIRST, then the tour. v4 made the brand-first onboarding lead deliberately and
      // chains the tour off its onComplete (see onboardEntry === "first-run"), so running the tour
      // first would leave onboarding stranded: nothing chains back to it.
      if (readStored<number>("insnip.onboardV", 0) < ONBOARD_VERSION) setOnboardingOpen(true);
      else if (shouldShowTour(readStored<number>("insnip.tourV", 0))) openTourWhenClear();
    };
    introTimerRef.current = window.setTimeout(fire, TOUR_DWELL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedIds.length, insightDue]);

  // Deep dives no longer ride in the feed round: a round announces which cards HAVE one and the
  // body is fetched per card. That took a 10-card round from 48 KiB to about 14 KiB, which is
  // the first thing a new reader waits on. To keep the dive instant anyway, hydrate the card the
  // reader is on AND the one after it, so the body is normally already in hand before the tap.
  useEffect(() => {
    if (!sessionApiEnabled()) return;
    const idx = filteredCards.findIndex((c) => c.id === activeId);
    if (idx < 0) return;
    const wanted = [filteredCards[idx], filteredCards[idx + 1]].filter(
      (c): c is InSnipCard => !!c && needsDiveHydration(c),
    );
    if (!wanted.length) return;
    let cancelled = false;
    void (async () => {
      for (const card of wanted) {
        const full = await fetchCard(card.id).catch(() => null);
        if (cancelled || !full?.deepDive) continue;
        setCards((cur) =>
          cur.map((c) => (c.id === card.id ? { ...c, deepDive: full.deepDive } : c)),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, filteredCards]);

  // Hydrate the active card's Call It state (my call + crowd split + verdict) once per card.
  useEffect(() => {
    const id = activeId;
    if (!id || !sessionApiEnabled()) return;
    if (stances[id]) return;
    let cancelled = false;
    void fetchStance(id).then((state) => {
      if (state && !cancelled) setStances((prev) => (prev[id] ? prev : { ...prev, [id]: state }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (status === "loading") {
    return (
      <main className="state-shell" id="main">
        <AppIcon size={56} className="state-icon pulse" />
        <p>Loading snippets…</p>
        <div className="skeleton-cards" aria-hidden="true"><div /><div /><div /></div>
      </main>
    );
  }

  if (status === "error") {
    return (
      <main className="state-shell" id="main">
        <AppIcon size={56} className="state-icon" />
        <h1>Couldn't load your cards.</h1>
        <p>Check your connection, then pull down to refresh.</p>
      </main>
    );
  }

  if (!activeCard) {
    return (
      <main className="state-shell" id="main">
        <AppIcon size={56} className="state-icon" />
        <h1>{showSavedOnly ? "Nothing saved yet." : "Nothing here for that filter."}</h1>
        <p>{showSavedOnly ? "Tap the bookmark on any card to start your reading list." : "Try another topic, or clear the filter to see everything."}</p>
        <div className="state-actions">
          {showSavedOnly ? <button type="button" onClick={goHome}>Show all cards</button> : null}
          {searchQuery ? <button type="button" onClick={() => setSearchQuery("")}>Clear search</button> : null}
          {activeTag !== "All" ? <button type="button" onClick={() => setActiveTag("All")}>Reset topic</button> : null}
        </div>
      </main>
    );
  }

  const cardReactions = reactions[activeCard.id] ?? [];
  // "Whole picture" cue: present only when the active card carries a warranted trust layer with a
  // real spread of views (drives the on-card sources-pill affordance).
  const activeTrust = (activeCard.trust ?? (activeCard.payload?.trust as TrustLayer | undefined)) || undefined;
  const activeWholePicture =
    activeTrust && activeTrust.warranted && (activeTrust.perspectives?.length ?? 0) >= 2 ? activeTrust : undefined;
  const totalSources = 1 + activeCard.supportingSources.length;
  // prettySourceName at CONSTRUCTION, not at render: every consumer downstream (the
  // Verify receipt, the share sheet, the meta row) then shows the masthead, and no
  // pipeline id (reg-smh-top) can ever reach a reader through a path this misses.
  const allSources = [
    { name: sourceDisplayName(activeCard.primarySourceName, activeCard.primarySourceUrl), url: activeCard.primarySourceUrl, primary: true },
    ...activeCard.supportingSources.map((source) => ({ ...source, name: sourceDisplayName(source.name, source.url), primary: false }))
  ].filter((s) => !/google.?trends/i.test(s.name));
  // The publisher on the card face: the masthead, and how many others stand behind it.
  const publisherLine = allSources.length ? `${allSources[0].name || "The source"}${allSources.length > 1 ? `, and ${allSources.length - 1} other${allSources.length > 2 ? "s" : ""}` : ""}` : "";
  // Intelligence signals: how many independent outlets corroborate, and how
  // original the synthesis is (lower verbatim overlap = more fully rewritten).
  const independentDomains = new Set(allSources.map((s) => domainOf(s.url))).size;
  const verbatimPct = Math.round((activeCard.originalityOverlap ?? 0) * 100);
  const originalPct = Math.max(0, 100 - verbatimPct);
  // The Intelligence drawer + Ask act on the search card when a search detail is open. These are
  // kept SEPARATE from the active card's own stats above, so the feed behind the search overlay is
  // never altered (no collision with the user's current cards).
  const intelCard = searchDetail ?? activeCard;
  const intelSources = [
    { name: sourceDisplayName(intelCard.primarySourceName, intelCard.primarySourceUrl), url: intelCard.primarySourceUrl, primary: true },
    ...intelCard.supportingSources.map((source) => ({ ...source, name: sourceDisplayName(source.name, source.url), primary: false })),
  ].filter((s) => !/google.?trends/i.test(s.name));
  const intelIndependentDomains = new Set(intelSources.map((s) => domainOf(s.url))).size;
  const intelVerbatimPct = Math.round((intelCard.originalityOverlap ?? 0) * 100);
  const intelOriginalPct = Math.max(0, 100 - intelVerbatimPct);
  // "The Whole Picture" trust layer for the drawer's card. The agent-generated half (perspectives)
  // rides in payload.trust; the sourcing facts are taken from there if present, else derived
  // deterministically from the source set so the credibility block works on every card today.
  const intelTrust = (intelCard.trust ?? (intelCard.payload?.trust as TrustLayer | undefined)) || undefined;
  const intelSourcing = intelTrust?.sourcing ?? {
    independentDomains: intelIndependentDomains,
    primaryCount: intelSources.filter((s) => s.primary).length,
    singleSource: intelIndependentDomains <= 1,
    attributed: Boolean(intelCard.primarySourceName),
  };
  const wholePicture = intelTrust && intelTrust.warranted && (intelTrust.perspectives?.length ?? 0) >= 2
    ? intelTrust : undefined;
  // Receipt v2: the pipeline's provenance record (claim check, last-checked, per-domain tiers),
  // decoded defensively in receipt.ts. Null on every card that predates it, so the drawer
  // renders exactly as before on those.
  const intelReceipt = receiptOf(intelCard);
  const intelCheckedLine = intelReceipt ? lastCheckedLabel(intelReceipt) : null;
  const speechSupported = typeof window !== "undefined" && Boolean(window.speechSynthesis);
  const progressPct = Math.round((progress / READ_TARGET) * 100);
  const ringDash = `${progressPct}, 100`;
  // Catch-me-up banner: away > 3 days, a Brief to offer, not dismissed today, and never
  // on top of the first-run tour/onboarding.
  const showCatchup =
    awayDays > 3 && briefs.length > 0 && !catchupDismissed && !tourOpen && !onboardingOpen;

  // THE PHONE WALL. On a phone browser the reader is not served at all: the wall replaces the
  // entire shell and sends the reader to the store, where the deck actually works. The whole
  // decision table lives in shouldShowAppWall (appPromo.ts) with its own test corpus: phones
  // only, never the native app (this file IS the app in the Capacitor shell), never a PWA the
  // reader already installed, never a crawler or link unfurler, and ?desktop=1 as the spelled
  // escape hatch. Tablets, desktop, and every server-rendered growth surface (/c/, /play,
  // /brief, the homepage: Pages Functions that never mount this component) are untouched.
  // Placed after every hook (hooks must run unconditionally); the data effects still fire
  // behind it, which is harmless and keeps this a one-line decision.
  if (shouldShowAppWall({
    nativeApp: isNativeApp,
    standalone:
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        (navigator as { standalone?: boolean }).standalone === true),
  })) {
    return (
      <AppWall
        platform={webPlatform()}
        onSeen={() => sendEvent({ type: "app_get_click", tag: "wall_view" })}
        onStoreClick={(surface) => logAppGetClick(surface)}
      />
    );
  }

  return (
    <main
      className={`app-shell v4${isNativeApp ? " native" : ""}${focusMode ? " focused" : ""}${overture ? " overture" : ""}`}
      id="main"
      data-tod={timeOfDay()}
      style={{ ["--amb-r" as string]: ambient.r, ["--amb-g" as string]: ambient.g, ["--amb-b" as string]: ambient.b }}
    >
      <div className="tod-tint" aria-hidden="true" />
      <div className="ambient-glow" aria-hidden="true" />
      {activeCard?.imageUrl ? (
        <div
          key={`scene-${activeCard.id}`}
          className="scene-backdrop"
          aria-hidden="true"
          style={{ backgroundImage: `url(${resolveImageUrl(activeCard.imageUrl)})` }}
        />
      ) : null}
      <div className="scene-scrim" aria-hidden="true" />

      {(gateFeature || signInOpen || authWall) ? (() => {
        // The teasers show the real thing, blurred: this story's own words, never an invented
        // headline about a day that did not happen.
        const gateHeadline = tidyText(activeCard.headline);
        const gateLine = tidyText(activeCard.whyItMatters || activeCard.essence || "");
        const g =
          gateFeature === "search"
            ? {
                icon: <Search size={22} aria-hidden="true" />,
                name: "Search",
                hint: "Find any story on InSnip in a second: a name, a topic, a question.",
                teaser: (
                  <div className="gate-mock gate-mock-search">
                    <span className="gate-mock-bar">{gateHeadline.split(/\s+/).slice(0, 4).join(" ")}</span>
                    <div className="gate-mock-chips">{[activeCard.category, ...(activeCard.tags ?? [])].filter(Boolean).slice(0, 3).map((t) => <span key={String(t)}>{String(t)}</span>)}</div>
                  </div>
                ),
              }
            : gateFeature === "brief"
              ? {
                  icon: <FileText size={22} aria-hidden="true" />,
                  name: "the daily Brief",
                  hint: "The whole day on one page in about a minute: the five things that matter and what connects them.",
                  teaser: (
                    <div className="gate-mock gate-mock-brief">
                      <strong>Today, in 5 stories</strong>
                      <p className="gate-blur">{gateHeadline}. {gateLine}</p>
                    </div>
                  ),
                }
              : gateFeature === "deepdive"
                ? {
                    icon: <Compass size={22} aria-hidden="true" />,
                    name: "Deep Dive",
                    hint: "The whole story in a few steps: what happened, what came before it, and what it sets up next.",
                    teaser: (
                      <div className="gate-mock gate-mock-dive">
                        <span className="gate-beat"><i /> How it started</span>
                        <span className="gate-beat"><i /> What changed today</span>
                        <span className="gate-beat gate-blur"><i /> What happens next</span>
                      </div>
                    ),
                  }
                : gateFeature === "ask"
                ? {
                    icon: <MessageCircle size={22} aria-hidden="true" />,
                    name: "Ask about this",
                    hint: "Ask the evidence: answers come only from this story's sources, and name them.",
                    teaser: (
                      <div className="gate-mock gate-mock-ask">
                        <span className="gate-q">Why does this matter?</span>
                        <span className="gate-a gate-blur">{gateLine || gateHeadline}</span>
                      </div>
                    ),
                  }
                : null;
        return (
          <div className="gate-scrim" role="dialog" aria-modal="true" aria-label="Sign in" onClick={authWall ? undefined : closeSignIn}>
            <div className="gate-card" onClick={(e) => e.stopPropagation()}>
              {/* The wall has no close: signing in IS the way forward. Feature gates keep theirs. */}
              {!authWall ? (
                <button type="button" className="gate-close" onClick={closeSignIn} aria-label="Close">&times;</button>
              ) : null}
              {g ? (
                <>
                  <div className="gate-emoji gate-icon" aria-hidden="true">{g.icon}</div>
                  <h2 className="gate-title">{g.name.charAt(0).toUpperCase() + g.name.slice(1)}</h2>
                  <p className="gate-hint">{g.hint}</p>
                  {g.teaser}
                  {/* One line for every feature gate, so it lists what an account actually
                      opens. It used to name only Ask and Search, which read as a non sequitur
                      on the Deep dive and Brief gates. */}
                  <p className="gate-cta-line">A free account opens Deep Dive, the daily Brief, Search and Ask, saves your stories, and keeps them and your streak on every device.</p>
                </>
              ) : wallEarned ? (
                <>
                  {/* THE EARNED INVITATION. It opens with the reader's own work, not ours: a
                      count they made and the stories behind it. Only then does it ask, and
                      what it asks for is the thing that keeps what they just built. */}
                  <div className="gate-emoji gate-icon" aria-hidden="true"><Flame size={22} aria-hidden="true" /></div>
                  <h2 className="gate-title">{storiesFinished} {storiesFinished === 1 ? "story" : "stories"} in. Start your streak here</h2>
                  {wallRead.length ? (
                    <div className="gate-tease gate-mine">
                      <span className="gate-tease-label">You have read</span>
                      {wallRead.map((c) => (
                        <div key={c.id} className="gate-tease-row">
                          <span className="gt-h">{c.headline}</span>
                          {c.primarySourceName && !/google.?trends/i.test(c.primarySourceName) ? <span className="gt-s">{sourceDisplayName(c.primarySourceName, c.primarySourceUrl)}</span> : null}
                        </div>
                      ))}
                    </div>
                  ) : wallTease.length ? (
                    <div className="gate-tease" aria-hidden="true">
                      <span className="gate-tease-label">Inside today</span>
                      {wallTease.map((t) => (
                        <div key={t.id} className="gate-tease-row">
                          <span className="gt-h">{t.headline}</span>
                          {t.source && !/google.?trends/i.test(t.source) ? <span className="gt-s">{sourceDisplayName(t.source)}</span> : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {/* Named, not listed in a sentence. A reader deciding right now wants to
                      know what each thing IS, and a comma-separated run of feature names is
                      the one shape that tells them nothing. */}
                  <ul className="gate-perks">
                    <li><strong>Deep Dive</strong><span>How the story got here, beat by beat.</span></li>
                    <li><strong>The daily Brief</strong><span>Your whole day on one page, finishable.</span></li>
                    <li><strong>Search and Ask</strong><span>Any story in context, with its sources.</span></li>
                    <li><strong>Your streak and saves</strong><span>Carried to every device you read on.</span></li>
                  </ul>
                  <p className="gate-hint">InSnip stays free. This takes a few seconds.</p>
                </>
              ) : authWall ? (
                <>
                  <div className="gate-emoji gate-icon" aria-hidden="true"><Flame size={22} aria-hidden="true" /></div>
                  <h2 className="gate-title">Sign in to keep your day</h2>
                  {wallTease.length ? (
                    <div className="gate-tease" aria-hidden="true">
                      <span className="gate-tease-label">Inside today</span>
                      {wallTease.map((t) => (
                        <div key={t.id} className="gate-tease-row">
                          <span className="gt-h">{t.headline}</span>
                          {t.source && !/google.?trends/i.test(t.source) ? <span className="gt-s">{sourceDisplayName(t.source)}</span> : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <p className="gate-hint">InSnip is free either way. An account keeps your streak, your saves and your insights on every device, and opens Deep Dive, the Brief, Search and Ask.</p>
                </>
              ) : (
                <>
                  <div className="gate-emoji gate-icon" aria-hidden="true"><UserRound size={22} aria-hidden="true" /></div>
                  <h2 className="gate-title">Sign in</h2>
                  <p className="gate-hint">Keep your streak, saves and insights on every device you read on. Free, and it takes a few seconds.</p>
                </>
              )}
              {signInForm}
              {/* The way past the first door. Quiet on purpose: an account is still the better
                  path and the buttons above say so, but a stranger who came for a story can
                  now go and read it. Feature gates do not get one, they have their own close. */}
              {authWall && canSkipWall ? (
                <button type="button" className="gate-skip" onClick={skipWall}>
                  {wallEarned ? "Not now, keep reading" : "Skip for now, just let me read"}
                </button>
              ) : null}
            </div>
          </div>
        );
      })() : null}
      <div className="top-progress" style={{ width: `${Math.min(100, progressPct)}%` }} aria-hidden="true" />
      {focusMode ? (
        <>
          {/* The reading room's only chrome: a gold filament tracing the round, and a hint
              that fades once it has said its piece. */}
          <div className="focus-filament" style={{ width: `${Math.min(100, progressPct)}%` }} aria-hidden="true" />
          <span className="focus-hint" aria-hidden="true">Esc leaves the room &middot; arrows turn the page</span>
        </>
      ) : null}
      {freshCount > 0 && !refreshing && Date.now() >= freshSnooze ? (
        <div className="fresh-pill-wrap">
          <button
            type="button"
            className="fresh-pill"
            onClick={() => void doPullRefresh()}
            aria-label="Refresh and read the new snips"
          >
            <RotateCw size={13} aria-hidden="true" />
            {/* No count on purpose: "57 new snips" reads as a backlog and overwhelms; an
                invitation reads as a treat. The count still gates WHEN the pill appears. */}
            Fresh snips are in · tap to read
          </button>
          <button
            type="button"
            className="fresh-dismiss"
            onClick={dismissFresh}
            aria-label="Dismiss, not now"
            title="Not now"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}
      {/* CALM (2026-10-23): the same shelf as /more. One row: the brand, a crumb naming the
          page, then every tool as one glass pill (calm.css). The legacy class names stay on
          each control because their behaviour and their phone rules are keyed on them. */}
      <header className="topbar mo-top">
       <div className="mo-top-row">
        {/* On a phone: the menu, as on the home (the same drawer, every tool and page). */}
        {phoneWeb ? (
          <button type="button" className="mo-menu-btn" onClick={() => setToolsMenuOpen(true)} aria-label="Open the menu" aria-expanded={toolsMenuOpen}>
            <MenuIcon size={20} aria-hidden="true" />
          </button>
        ) : null}
        <a className="brand" href="/" aria-label="InSnip home">
          <div className="brand-mark-wrap" title={`${progress}/${currentRoundTarget || READ_TARGET} cards this round`}>
            <svg className="brand-ring" viewBox="0 0 36 36" aria-hidden="true">
              <circle className="ring-bg" cx="18" cy="18" r="15.9155" />
              <circle className="ring-fg" cx="18" cy="18" r="15.9155" strokeDasharray={ringDash} />
            </svg>
            <span className="brand-mark" aria-label="InSnip">
              <svg className="brand-monogram" viewBox="0 0 64 64" aria-hidden="true">
                <circle className="mono-lens" cx="29" cy="28" r="15" />
                <path className="mono-handle" d="M40.5 40.5 52 52" />
                <path className="mono-spark" d="M29 17 L30.6 25.4 L39 28 L30.6 30.6 L29 39 L27.4 30.6 L19 28 L27.4 25.4 Z" />
                <circle className="mono-spark-core" cx="29" cy="28" r="1.5" />
              </svg>
            </span>
          </div>
          <span className="brand-text">
            <strong className="brand-word" aria-label="InSnip">
              <span className="bw-in">In</span><span className="bw-snip">Snip</span>
            </strong>
            <small className="brand-tag">Pure signal, no noise.</small>
          </span>
        </a>
        {/* The crumb names the page, the way "Check out more" does on the tools; the date
            moved to the edition's masthead, where a front page carries it. */}
        {/* The two faces of InSnip, side by side beside the brand: the home (around you and your
            numbers) and today's news. The lit one is where you are. */}
        {/* THE PILLARS (2026-10-30): Home, News, Edition beside the name; Edition opens in place. */}
        <Pillars
          active="news"
          onPillar={(id) => {
            if (id === "edition") { setDeskOpen(true); return true; }
            if (id === "news") { if (showSavedOnly) goHome(); else if (activeTag !== "All") changeTag("All"); window.scrollTo({ top: 0 }); return true; }
            return false;
          }}
        />
        <span className="mo-top-gap" />
        <nav className="top-actions" aria-label="Primary">
          {refreshReady ? (
            <button
              type="button"
              className="refresh-ready"
              onClick={() => { if (serverDrivenRef.current) { void loadServerRound("refresh"); setRefreshReady(false); } else { window.location.reload(); } }}
              title="Load the newest card drop"
            >
              <Sparkles size={14} aria-hidden="true" />
              <span>New drop</span>
            </button>
          ) : null}
          {/* The Brief, Search, Deep Dive, Ask and the full story are the app's (USER 2026-11-03): the
              phone browser keeps the plain card, the five-second read. */}
          {briefs.length && !phoneWeb ? (
            <button
              type="button"
              className="brief-header-btn"
              data-tour="brief-btn"
              onClick={openBrief}
              aria-label="Read today's brief"
              title="Today's Brief, the day in a minute"
            >
              <Sparkles size={14} aria-hidden="true" />
              <span>Brief</span>
            </button>
          ) : null}
          {/* One quiet capsule for the two explore verbs: search and browse. Insights
              moved out to its own chip beside the recap. (display: contents on phones,
              so the mobile bar layout is untouched.) */}
          <div className="top-cluster" role="group" aria-label="Explore">
            {phoneWeb ? null : <button
              type="button"
              className="search-btn"
              data-tour="search-btn"
              onClick={openSearch}
              aria-label="Search snippets"
              title="Search every snippet (/)"
            >
              <Search size={17} aria-hidden="true" />
              <span className="search-btn-label">Search</span>
              <kbd className="mo-pal-kbd" aria-hidden="true">/</kbd>
            </button>}
            <button
              type="button"
              className="icon-button ghost browse-btn"
              onClick={() => setDeskOpen(true)}
              aria-label="Open today's edition"
              title="Today's edition, the whole round on one front page"
            >
              <LayoutGrid size={17} aria-hidden="true" />
              <span className="browse-btn-label">Edition</span>
            </button>
          </div>
          {/* Insights lives here now (moved out of the explore pill): one clean gold chip
              that opens the hub, where the recap, streak and reading shape all live. */}
          <button
            type="button"
            className="insights-chip"
            onClick={openInsightsHub}
            title="Your insights and recap: today's shape, streak, reading DNA and rituals"
            aria-label="Your insights and recap"
          >
            <Lightbulb size={15} aria-hidden="true" />
            <span className="insights-chip-label">Insights</span>
          </button>
          {/* Theme lives on the bar again (moved out of the profile sheet): one quiet
              swatch that opens the four looks. Desktop only; phones theme from the menu. */}
          <Favorites
            savedIds={savedIds}
            cards={cards}
            onUnsave={(id) => toggleSave(id)}
            onOpenStory={(id, card) => { const c = cards.find((x) => x.id === id) ?? savedCardCache[id] ?? card; if (c) openAnyCard(c); else window.location.hash = `card=${encodeURIComponent(id)}`; }}
            compact={phoneWeb}
          />
          <div className="aa-wrap">
            {phoneWeb ? (
              /* THE EDITION DOOR (USER 2026-10-30): on a phone the Aa sat on the bar while the
                 same Display settings live in the menu, so its place goes to today's edition,
                 the front page, which had no door on this surface. The sheet below still renders
                 here for the menu's Display settings entry. */
              <button
                type="button"
                className="edition-door"
                onClick={() => setDeskOpen(true)}
                aria-label="Open today's edition"
                title="Today's edition, the front page"
              >
                <Newspaper size={16} aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className={`aa-trigger${aaOpen ? " active" : ""}`}
                onClick={() => setAaOpen((c) => !c)}
                aria-haspopup="menu"
                aria-expanded={aaOpen}
                aria-label="Display settings"
                title="Display settings"
              >
                Aa
              </button>
            )}
            {aaOpen ? (
              <>
                <button type="button" className="view-menu-scrim" aria-label="Close display settings" onClick={() => setAaOpen(false)} />
                <DisplaySheet
                  display={display}
                  onChange={(patch) => setDisplay((d) => ({ ...d, ...patch }))}
                  onReset={() => setDisplay({ ...DISPLAY_DEFAULTS })}
                  theme={theme}
                  themes={THEME_OPTIONS}
                  onTheme={(id) => setTheme(id as Theme)}
                  measure={measure}
                  onMeasure={setMeasure}
                  bionic={bionicMode}
                  onBionic={() => setBionicMode((c) => !c)}
                  phone={phoneWeb}
                  onClose={() => setAaOpen(false)}
                />
              </>
            ) : null}
          </div>
          {/* The theme dot is gone (the Kindle layer): the page colour is the first row of the
              Aa sheet, where a Kindle reader expects it. */}
          <div className="account-menu-wrap">
            <button
              type="button"
              className={`account-trigger${accountMenuOpen ? " active" : ""}${account?.signedIn ? " is-in" : ""}`}
              /* On a phone browser this is the ONLY way into the profile: the bottom nav that
                 used to own the "You" tab is gone. It opens the mobile SHEET, not the desktop
                 dropdown, because the sheet is the one built for a thumb and the one that was
                 pruned for this surface. */
              onClick={() => (phoneWeb ? setMobileMenuOpen(true) : setAccountMenuOpen((c) => !c))}
              aria-haspopup={phoneWeb ? "dialog" : "menu"}
              aria-expanded={phoneWeb ? mobileMenuOpen : accountMenuOpen}
              aria-label="Your profile"
              title={account?.signedIn ? "Your profile" : "Sign in and profile"}
            >
              {account?.signedIn ? (
                <span className="account-initial" aria-hidden="true">{(account.email || account.name || "Y").trim().charAt(0).toUpperCase()}</span>
              ) : (
                <User size={17} aria-hidden="true" />
              )}
              {account?.signedIn ? <span className="account-trigger-dot" aria-hidden="true" /> : null}
              {/* On a phone the button is the menu, and a bare initial does not say so: the
                  three lines, in a small badge on its shoulder, do. */}
              {phoneWeb ? <span className="account-menu-badge" aria-hidden="true"><MenuIcon size={9} strokeWidth={3} /></span> : null}
            </button>
            {accountMenuOpen ? (
              <>
                <button
                  type="button"
                  className="view-menu-scrim"
                  aria-label="Close profile menu"
                  onClick={() => setAccountMenuOpen(false)}
                />
                <div className="view-menu account-menu" role="menu">
                  <span className="view-menu-label">You</span>
                  {/* Signed out, the one thing worth doing is signing in, so the card leads.
                      Signed in, identity and account management close the menu instead (see the
                      Profile section at the bottom) and the menu opens on your numbers. */}
                  {!account?.signedIn ? renderAccountCard({ invite: false }) : null}
                  {/* Your day at a glance: the three numbers that are yours. */}
                  {/* Signed in only: a fresh signed-out visitor was shown "1 day streak,
                      10/20 goal" before ever doing anything, phantom gamification that
                      reads as fake on a receipts brand. Their numbers begin when they do. */}
                  {account?.signedIn ? (
                    <div className="menu-stats" aria-label="Your reading at a glance">
                      <span className="menu-stat"><strong>{streak.count}</strong><small>day streak</small></span>
                      <span className="menu-stat"><strong>{savedIds.length}</strong><small>saved</small></span>
                      <span className="menu-stat"><strong>{Math.min(dayRound * READ_TARGET, dailyGoal)}<i>/{dailyGoal}</i></strong><small>goal today</small></span>
                    </div>
                  ) : null}

                  {/* Get the native app: always one tap away from the profile menu, and exactly
                      once (it used to appear here AND under App settings). Live on the App
                      Store (iOS) and Google Play (Android); desktop routes to /get for the QR. */}
                  {!isNativeApp ? (
                    <a
                      role="menuitem"
                      className="view-item view-link app-get-item"
                      href={storeHref()}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => logAppGetClick("menu")}
                    >
                      <AppIcon size={18} />
                      <span>Get the app</span>
                      <span className="view-item-hint">free</span>
                    </a>
                  ) : null}

                  {/* YOURS: your list, your topics, your pace, your history. */}
                  <span className="view-menu-label">Yours</span>
                  <button
                    type="button"
                    role="menuitem"
                    className={`view-item${showSavedOnly ? " on" : ""}`}
                    onClick={() => { setAccountMenuOpen(false); toggleSavedView(); }}
                  >
                    <Bookmark size={16} />
                    <span>{showSavedOnly ? "Showing saved" : "Saved stories"}</span>
                    <span className="view-item-count">{savedIds.length}</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={`view-item${Object.keys(topicPrefs).length ? " on" : ""}`}
                    onClick={() => { setAccountMenuOpen(false); setTopicsOpen(true); }}
                  >
                    <SlidersHorizontal size={16} />
                    <span>Personalise topics</span>
                    {Object.keys(topicPrefs).length ? <span className="view-item-count">{Object.keys(topicPrefs).length}</span> : null}
                  </button>
                  <button
                    type="button"
                    className="view-item menu-goal"
                    onClick={() => setDailyGoal((g) => (g >= 30 ? 10 : g + 10))}
                    title="Tap to change your daily reading goal"
                  >
                    <Target size={16} />
                    <span>Daily goal</span>
                    <em>{dailyGoal} stories</em>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="view-item"
                    onClick={() => { setAccountMenuOpen(false); setSubscribeStatus("idle"); setSubscribeOpen(true); window.setTimeout(() => emailInputRef.current?.focus(), 60); }}
                  >
                    <Mail size={16} />
                    <span>Get the daily email</span>
                  </button>
                  {/* The invite ladder belongs with the rest of what is yours. It used to sit
                      inside the profile card between the identity line and the sign-out button,
                      which is now the last block in the menu: too far down for the one row here
                      that earns the reader something. */}
                  {inviteBlock}
                  {!isNativeApp ? (
                    <a role="menuitem" className="view-item" href="/">
                      <Home size={16} />
                      <span>Home</span>
                      <em>Around you</em>
                    </a>
                  ) : null}
                  {/* APP SETTINGS: the generic machinery, clearly apart from what is yours.
                      Text size and bionic live in the masthead's Aa popover now, not here. */}
                  <span className="view-menu-label">App settings</span>
                  <button type="button" role="menuitem" className={`view-item${focusMode ? " on" : ""}`} onClick={() => { setFocusMode((c) => !c); setAccountMenuOpen(false); }}>
                    {focusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    <span>Focus mode</span>
                  </button>
                  {/* "Fresh stories" and "Tip of the hour" left this menu: the feed refreshes
                      itself and from the keyboard (R), and the tip has its own moment. A reader
                      opens this menu for an account, a setting or help. */}
                  <button type="button" role="menuitem" className="view-item" onClick={() => { setShortcutsOpen(true); setAccountMenuOpen(false); }}>
                    <Keyboard size={16} />
                    <span>Keyboard shortcuts</span>
                  </button>
                  {/* About and contact: real, crawlable links so the contact page, source policy and
                      privacy policy are always one tap from the profile menu (mirrors the app's
                      About and contact sheet and the Play News-policy contact requirement). These are
                      static pages, so plain anchors navigate to them and search engines can see them. */}
                  <span className="view-menu-label">About &amp; contact</span>
                  <a role="menuitem" className="view-item view-link" href="/contact">
                    <Info size={16} />
                    <span>Contact &amp; about</span>
                  </a>
                  {/* Feedback about the product as a whole, filed beside Contact because that is
                      where a reader goes looking for a way to reach us. Email still sits below it
                      for anyone who would rather write properly; this is the two-tap version. */}
                  <button
                    type="button"
                    role="menuitem"
                    className="view-item"
                    onClick={() => { setAccountMenuOpen(false); openFeedback({ kind: "general" }); }}
                  >
                    <MessageCircle size={16} />
                    <span>Send feedback</span>
                  </button>
                  <a role="menuitem" className="view-item view-link" href="mailto:support@insnip.com">
                    <Mail size={16} />
                    <span>Email us</span>
                    <span className="view-item-hint">support@insnip.com</span>
                  </a>
                  <a role="menuitem" className="view-item view-link" href="/sources">
                    <ShieldCheck size={16} />
                    <span>Sources &amp; publishers</span>
                  </a>
                  <a role="menuitem" className="view-item view-link" href="/privacy">
                    <Scale size={16} />
                    <span>Privacy &amp; content policy</span>
                  </a>
                  {/* PROFILE, last. Who you are and the two irreversible buttons (sign out,
                      delete account) are what you leave a menu by, not what you should have to
                      read past to reach your saved stories. The streak is dropped here: the
                      stats row at the top of this menu already carries it. */}
                  {account?.signedIn ? (
                    <>
                      <span className="view-menu-label profile-label">Profile</span>
                      {renderAccountCard({ stat: false, invite: false })}
                    </>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </nav>
       </div>
       {/* THE SECTIONS: the topics as chips under the shelf; they narrow the deck, Saved shows the list.
           Only the topics this round actually has, each with its count (USER 2026-11-03): a chip
           that opens onto nothing is a dead end. Saved appears once something is saved. */}
       {/* On a phone browser the bar is the name and nothing under it (USER 2026-11-03: "keep it
           simple and easy to read"): no topic chips, no order menu. The card names its own topic,
           and the topics come back only inside Saved, where "All" is the way out. */}
       {!phoneWeb || showSavedOnly ? <Sections
         label="Topics"
         sections={[
           { id: "All", label: "All", on: !showSavedOnly && activeTag === "All", onClick: () => { if (showSavedOnly) goHome(); changeTag("All"); } },
           ...ANCHOR_CATEGORIES
             .map((c) => ({ c, n: topicCounts.get(c.id) ?? 0 }))
             .filter(({ c, n }) => n > 0 || activeTag === c.id)
             .map(({ c, n }) => ({ id: c.id, label: c.id, count: n, on: !showSavedOnly && activeTag === c.id, onClick: () => { if (showSavedOnly) goHome(); changeTag(c.id); } })),
           ...(savedIds.length || showSavedOnly ? [{ id: "saved", label: "Saved", count: savedIds.length, on: showSavedOnly, onClick: () => { if (!showSavedOnly) openSavedView(); } }] : []),
         ]}
       /> : null}
      </header>

      {/* A single gold hairline across the top of the viewport: your progress through the round.
          The quietest possible instrument, always true. */}
      <div
        className={`round-progressline${roundFlare ? " round-complete" : ""}`}
        aria-hidden="true"
        style={{ ["--rp"]: `${Math.min(100, Math.round((progress / (currentRoundTarget || READ_TARGET)) * 100))}%` } as React.CSSProperties}
      />
      {/* Warm light (the Kindle layer): an amber veil multiplied over the page, off at zero. */}
      {display.warmth > 0 ? <div className="warm-veil" aria-hidden="true" /> : null}

      {/* The wire strip: a live ticker on the left (the freshest drops, one at a time),
          the reader's lens on the right (Top / For you / Latest). Desktop chrome only. */}
      {!isNativeApp && !showSavedOnly && filteredCards.length > 0 ? (() => {
        const tickerPool = [...cards]
          .filter((c) => c.contentType !== "knowledge" && c.publishedAt)
          .sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""))
          .slice(0, 5);
        const tick = tickerPool.length ? tickerPool[tickerIx % tickerPool.length] : null;
        return (
          <div className="wire-strip">
            {tick ? (
              <button
                key={tick.id}
                type="button"
                className="ws-ticker"
                onClick={() => openAnyCard(tick)}
                title={tick.headline}
              >
                <span className="ws-live" aria-hidden="true" />
                <span className="ws-label">{cardTimeLabel(tick) || "Latest"}</span>
                <span className="ws-headline">{tick.headline}</span>
              </button>
            ) : <span />}
            <LensMenu lens={lens} onLens={setLens} tuned={lensTuned} />
          </div>
        );
      })() : null}

      {/* Native-only round strip. On the app the gold progress hairline above lives behind the
          camera/status bar (invisible on a real device), and the dock's round row is hidden for a
          clean one-row dock, so "which round am I on" was lost. This slim strip sits just under the
          top bar, clear of the notch: ROUND N, the count to the next round, and the round's own
          progress fill along its bottom edge. Hidden on the web (CSS default) where the reader is
          full-height and the dock already carries this. */}
      {!showSavedOnly && serverDrivenRef.current ? (() => {
        const left = Math.max(0, filteredCards.length - (activeIndex + 1));
        const note = activeTag !== "All" ? `${activeTag}, ${activeIndex + 1} of ${filteredCards.length}`
          : !hasMoreCards
          ? (left > 0 ? `${left} left today` : "You're up to date")
          : left === 0 ? `Round ${dayRound + 1} unlocked`
          : left === 1 ? `1 more to Round ${dayRound + 1}`
          : `${left} more to Round ${dayRound + 1}`;
        const pct = Math.min(100, Math.round((progress / (currentRoundTarget || READ_TARGET)) * 100));
        return (
          <div
            className={`nat-roundbar${roundFlare ? " round-complete" : ""}`}
            aria-hidden="true"
            style={{ ["--rp"]: `${pct}%` } as React.CSSProperties}
          >
            <span className="nat-rb-round">Round {dayRound}</span>
            <span className="nat-rb-dot" aria-hidden="true" />
            <span className="nat-rb-note">{note}</span>
          </div>
        );
      })() : null}

      <section className="reader-frame" aria-label="InSnip reader">
        <aside className="left-rail" aria-label="Feed filters">
          {/* Get the app: the first thing in the rail so the store links are prominent from first
              load. Web-only; hidden inside the native app. Live on the App Store and Google Play. */}
          {!isNativeApp ? (
            <section className="mini-panel getapp-panel" aria-label="Get the InSnip app">
              <span className="getapp-eyebrow"><AppIcon size={18} /> On iPhone and Android</span>
              <strong className="getapp-title">Get the free app</strong>
              <p className="getapp-sub">The whole feed in your pocket. Source-checked, calm, no doomscroll.</p>
              <div className="getapp-badges">
                <a
                  className="getapp-badge"
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  onClick={() => logAppGetClick("sidebar_ios")}
                >
                  <img src="/app-store-badge.svg" alt="Download on the App Store" width={162} height={48} />
                </a>
                <a
                  className="getapp-badge"
                  href={PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  onClick={() => logAppGetClick("sidebar_android")}
                >
                  <img src="/google-play-badge.png" alt="Get it on Google Play" width={162} height={48} />
                </a>
              </div>
            </section>
          ) : null}
          {/* Search now lives in the top bar; the rail opens straight into the day.
              Today masthead: a light typographic ledger that frames the whole rail as one calm,
              finite day (day + progress + the source-checked promise), not another boxed tile. */}
          <section className="mini-panel today-panel today-ledger">
            <div className="today-top">
              <span className="today-eyebrow">Today</span>
              <span className="today-date">
                {new Date().toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
              </span>
            </div>
            <div className="today-figure">
              <span className="today-count" aria-label={`${Math.min(readToday, dailyStats.stories)} of ${dailyStats.stories} read so far`}>
                <span className="num-roll">{Math.min(animReadToday, dailyStats.stories)}</span><i>/{dailyStats.stories}</i>
              </span>
              <span className="today-figure-label">read so far</span>
            </div>
            {/* The day drawn as a calm gold spectrum: one tick per story, lit as it is
                read and bright gold where the reader is now. It is the progress bar and
                the jump-list in one (no separate bar above it). Each tick is a real
                button that jumps straight to its story (same jump the round
                console and overview use: visitCard). */}
            {filteredCards.length > 1 ? (
              <div className="today-spectrum" aria-label="Jump to a story">
                {filteredCards.slice(0, 12).map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    aria-label={`Story ${i + 1}`}
                    title={c.headline}
                    data-on={viewedIds.includes(c.id) ? "1" : "0"}
                    data-live={c.id === activeId ? "1" : "0"}
                    style={{ "--hue": `${tagHue(c.category)}` } as React.CSSProperties}
                    onClick={() => visitCard(c.id)}
                  />
                ))}
              </div>
            ) : null}
            <button
              type="button"
              className="today-note"
              onClick={() => setTrustOpen(true)}
              aria-label={`The whole day in about ${dailyStats.minutes} minutes across ${dailyStats.sources} sources. See how every story is source-checked.`}
              title="See how every story is sourced and gate-checked"
            >
              <ShieldCheck size={13} aria-hidden="true" />
              <span>~{dailyStats.minutes} min, every story source-checked</span>
            </button>
          </section>
          {/* The Brief lives in the top bar (its gold doorway); a second Brief tile in
             the rail was redundant. The rail keeps the daily ritual (Signal or Noise),
             the one to read, and trending, so each instrument appears exactly once. */}
          <SignalOrNoiseCard today={gameToday} result={gameResult} onPlay={() => setGameOpen(true)} />
          {todaysEssential ? (
            <section className="mini-panel">
              <button type="button" className="essential-row" onClick={goToEssential}>
                <span className="essential-row-eyebrow">
                  <Star size={11} aria-hidden="true" /> The one to read today
                </span>
                <span className="essential-row-headline">{todaysEssential.headline}</span>
              </button>
            </section>
          ) : null}
          {trending.length > 0 ? (
            <section className="mini-panel">
              <div className="section-label">🔥 Trending now</div>
              <div className="tag-list">
                {trending.map((cat) => (
                  <button key={cat} type="button" className="tag-chip trending-chip" onClick={() => jumpToCategory(cat)} title={`Jump to a trending ${cat} story`}>
                    {cat}
                  </button>
                ))}
              </div>
            </section>
          ) : null}
          {!serverDrivenRef.current ? (
            <section className="mini-panel">
              <div className="section-label">Categories</div>
              <div className="tag-list">
                {categories.map((tag) => {
                  const hue = tag === "All" ? null : tagHue(tag);
                  return (
                    <button key={tag} type="button" className={tag === activeTag ? "tag-chip active" : "tag-chip"} onClick={() => changeTag(tag)} style={hue != null ? ({ "--chip-hue": `${hue}` } as React.CSSProperties) : undefined}>
                      {tag}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}
          <section className="mini-panel join-panel" aria-label="Join InSnip">
            {subscribeStatus === "saved" ? (
              <div className="join-done">
                <span className="join-done-check"><Check size={16} aria-hidden="true" /></span>
                <strong>You're in.</strong>
                <span>Tomorrow's signal lands in your inbox.</span>
              </div>
            ) : (
              <>
                <span className="join-eyebrow"><Sparkles size={12} aria-hidden="true" /> Join InSnip</span>
                <strong className="join-title">One calm read a day</strong>
                <p className="join-sub">
                  {streak.count >= 2
                    ? `${streak.count}-day streak. Keep it going with the morning email.`
                    : readToday > 0
                      ? `You've read ${readToday} today. Get tomorrow's signal in your inbox.`
                      : "Pick your editions. One calm email, no noise, no doomscroll."}
                </p>
                <div className="join-editions" role="group" aria-label="Newsletter editions">
                  {EDITIONS.map((ed) => {
                    const on = editions.includes(ed.id);
                    return (
                      <button
                        key={ed.id}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        className={`join-edition${on ? " on" : ""}`}
                        onClick={() => setEditions((cur) => (cur.includes(ed.id) ? cur.filter((x) => x !== ed.id) : [...cur, ed.id]))}
                      >
                        {on ? <Check size={12} aria-hidden="true" /> : null}
                        <span>{ed.label}</span>
                      </button>
                    );
                  })}
                </div>
                <form className="join-form" onSubmit={submitSubscribe}>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    aria-label="Email address"
                    aria-invalid={subscribeStatus === "invalid"}
                    value={emailDraft}
                    onChange={(e) => { setEmailDraft(e.target.value); if (subscribeStatus === "invalid") setSubscribeStatus("idle"); }}
                  />
                  <button type="submit" disabled={subscribeStatus === "saving" || editions.length === 0}>
                    {subscribeStatus === "saving" ? "Joining…" : editions.length ? `Subscribe · ${editions.length}` : "Pick an edition"}
                  </button>
                </form>
                {subscribeStatus === "invalid" ? <small className="join-error">Enter a valid email.</small> : null}
                <small className="join-fine">Free forever. Unsubscribe any time.</small>
              </>
            )}
          </section>
          <section className="mini-panel guide-panel">
            <Sparkles size={16} aria-hidden="true" />
            <p>Swipe ←/→, press <kbd>S</kbd> to save, <kbd>L</kbd> to listen, <kbd>F</kbd> for focus, <kbd>B</kbd> for bionic reading.</p>
          </section>
        </aside>

        <section className="reader-main">
          {showCatchup ? (
            <div className="catchup-banner" role="note" aria-label="Welcome back">
              <div className="catchup-copy">
                <span className="catchup-eyebrow">WELCOME BACK</span>
                <p className="catchup-text">You were away {awayDays} days. The Brief catches you up in a minute.</p>
              </div>
              <button type="button" className="catchup-cta" onClick={openBrief}>Read the Brief</button>
              <button
                type="button"
                className="catchup-dismiss"
                aria-label="Dismiss welcome back"
                onClick={() => {
                  setCatchupDismissed(true);
                  try { localStorage.setItem("insnip.catchupDismiss", localDateKey()); } catch { /* best-effort */ }
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          ) : null}
          {pullY > 0 || refreshing ? (
            <div
              className={`pull-refresh${refreshing ? " spinning" : pullY >= PULL_TRIGGER ? " ready" : ""}`}
              style={{ opacity: refreshing ? 1 : Math.min(pullY / PULL_TRIGGER, 1) }}
              aria-hidden="true"
            >
              <RotateCw size={18} style={refreshing ? undefined : { transform: `rotate(${Math.round(pullY * 3)}deg)` }} />
              <span>{refreshing ? "Loading latest…" : pullY >= PULL_TRIGGER ? "Release for the latest" : "Pull for the latest"}</span>
            </div>
          ) : null}
          {activeIndex > 0 ? (
            <button
              type="button"
              className="nav-arrow nav-prev"
              data-tour="edge-prev"
              onClick={() => move(-1)}
              aria-label="Previous card"
              title="Previous (←)"
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
          ) : null}
          <article
            key={activeCard.id}
            data-tour="card"
            className={`snip-card card-enter${(showHint && activeIndex === 0) || (lesson >= 0 && lesson < 3 && activeIndex === lesson) ? " swipe-lesson" : ""}${lesson >= 0 && lesson < 3 && activeIndex === lesson ? ` lesson-${lesson}` : ""}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={(() => {
              if (!dragX && !pullY && !swipeSettling) return undefined;
              // Card follows the finger: translate, a touch of rotation, and a slight scale-down
              // so it recedes toward the deck as it leaves (revealing the stacked-card shadow).
              const rot = (dragX * 0.025).toFixed(2);
              const scale = (1 - Math.min(Math.abs(dragX), 240) / 2400).toFixed(4);
              return {
                transform: `translate3d(${dragX}px, ${pullY}px, 0) rotate(${rot}deg) scale(${scale})`,
                transition: swipeSettling
                  ? glideRide
                    ? `transform ${glideRide.ms}ms ${glideRide.easing}`
                    : "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)"
                  : "none",
              };
            })()}
          >
            {/* One swipe teacher. On a phone the lesson's caption on the photograph (below) is
                the whole lesson; the ghost card and the finger belong to the desktop's first
                visit only, so a new phone reader is never taught the same swipe four ways. */}
            {showHint && activeIndex === 0 && lesson < 0 ? (
              <>
                <div className="swipe-ghost" aria-hidden="true">
                  <div className="swipe-ghost-card" />
                </div>
                <div className="swipe-finger" aria-hidden="true">
                  <div className="swipe-finger-dot" />
                  <div className="swipe-finger-trail" />
                </div>
                <div className="swipe-caption" aria-hidden="true">
                  <span className="swipe-caption-chevrons">
                    <ChevronLeft size={14} />
                    <ChevronLeft size={14} />
                  </span>
                  Swipe left for the next story
                </div>
              </>
            ) : null}
            {/* THE INTERLUDE: the ask after three stories, inside the deck. A card-shaped panel
                over card 4, once per device, dismissed by its own button or by swiping on (the
                touches bubble to this card's handlers). The claims are the app's true edges over
                a browser tab: haptics on every swipe, offline, Deep dive, Ask, the streak. */}
            {/* A TOPIC, READ TO ITS END (USER 2026-11-03). The last Power & Policy story is not
                the end of the round, so this card says what is left: the round's other stories
                (clearing the filter lands on the first unread one), the other topics with their
                counts, and the next round when there is one. Back takes it away. */}
            {topicEnd && activeTag !== "All" ? (() => {
              const others = cards.filter((c) => !hiddenIds.includes(c.id) && c.category !== activeTag);
              const unread = others.filter((c) => !viewedIds.includes(c.id));
              const moreTopics = ANCHOR_CATEGORIES
                .map((c) => ({ id: c.id, n: unread.filter((x) => x.category === c.id).length }))
                .filter((t) => t.n > 0);
              const nextRound = serverDrivenRef.current && hasMoreCards;
              return (
                <div className="topic-end" role="dialog" aria-label={`Every ${activeTag} story read`}>
                  <div className="topic-end-card">
                    <span className="topic-end-mark" aria-hidden="true"><Check size={18} strokeWidth={2.25} /></span>
                    <h3>That is every {activeTag} story{serverDrivenRef.current ? " in this round" : " for now"}.</h3>
                    <p>
                      {unread.length
                        ? `${unread.length} more ${unread.length === 1 ? "story waits" : "stories wait"} under the other topics.`
                        : nextRound ? "You have read the whole round." : "You have read everything here for now."}
                    </p>
                    {unread.length ? (
                      <button type="button" className="topic-end-go" onClick={() => changeTag("All")}>
                        Read the other {unread.length}
                        <ArrowRight size={16} aria-hidden="true" />
                      </button>
                    ) : null}
                    {moreTopics.length > 1 ? (
                      <div className="topic-end-topics" aria-label="Other topics">
                        {moreTopics.map((t) => (
                          <button key={t.id} type="button" onClick={() => changeTag(t.id)}>
                            {t.id}<span>{t.n}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {nextRound ? (
                      <button type="button" className={unread.length ? "topic-end-next" : "topic-end-go"} onClick={() => { setTopicEnd(false); setActiveTag("All"); startNextRound(); }}>
                        {unread.length ? `Skip to Round ${dayRound + 1}` : `Start Round ${dayRound + 1}`}
                        {unread.length ? null : <ArrowRight size={16} aria-hidden="true" />}
                      </button>
                    ) : !unread.length ? (
                      <button type="button" className="topic-end-go" onClick={() => changeTag("All")}>
                        Back to all stories
                        <ArrowRight size={16} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })() : null}
            {interlude ? (
              <div className="interlude" role="dialog" aria-label="Three stories read, seven to go">
                <div className="interlude-card">
                  <span className="interlude-eyebrow">Three down, seven to go</span>
                  <h3>That is the whole idea.</h3>
                  <p>Ten stories a day, {minutesWord(10)}, and it ends. In the app it feels like it should:</p>
                  <ul>
                    <li><Sparkles size={16} aria-hidden="true" />Every swipe clicks under your thumb, and it reads offline</li>
                    <li><Compass size={16} aria-hidden="true" />Deep Dive: any story, beat by beat</li>
                    <li><Flame size={16} aria-hidden="true" />Your streak and your saved stories, kept</li>
                  </ul>
                  <a className="interlude-go" href={storeHref(storePlatform)} target="_blank" rel="noopener noreferrer" onClick={() => takeApp("interlude")}>
                    <AppIcon size={22} />Get the app, free
                  </a>
                  <button type="button" className="interlude-stay" onClick={() => { setInterlude(false); sendEvent({ type: "app_door_declined", tag: "interlude" }); }}>
                    Keep reading here
                  </button>
                </div>
              </div>
            ) : null}
            <div
              className={`image-panel${lowResHero[activeCard.id] ? " type-cover" : ""}`}
              style={{
                ["--tint" as string]: String(imageTintHue(activeCard)),
                // While the Desk is up its lead plate owns "card-media", so opening the lead
                // story morphs the front-page photograph into this plate (duplicate names abort
                // the transition, hence the handoff).
                ...(deskOpen ? {} : { viewTransitionName: "card-media" }),
              } as React.CSSProperties}
              onClick={onImageTap}
              role="presentation"
            >
              {lesson === 0 && activeIndex === 0 ? (
                <div className="swipe-caption" aria-hidden="true">
                  <span className="swipe-caption-chevrons">
                    <ChevronLeft size={14} />
                    <ChevronLeft size={14} />
                  </span>
                  Swipe left for the next story
                </div>
              ) : null}
              {lowResHero[activeCard.id] ? (
                <>
                  {/* Editorial type cover: the honest, handsome fallback when the source image is
                      too small to carry a cinematic hero. Hue follows the category chip. */}
                  <div className="tc-field" aria-hidden="true" style={{ ["--hue" as string]: `${tagHue(activeCard.category)}` } as React.CSSProperties} />
                  <span className="tc-initial" aria-hidden="true">{(tidyText(activeCard.headline).trim().charAt(0) || "I").toUpperCase()}</span>
                  <span className="tc-kicker">{activeCard.category}</span>
                  {activeCard.imageUrl ? (
                    <img
                      className="tc-stamp"
                      src={resolveImageUrl(activeCard.imageUrl)}
                      alt={activeCard.imageAlt || activeCard.headline}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <div className="image-fallback" aria-hidden="true">
                    <span className="image-fallback-cat">{activeCard.category}</span>
                  </div>
                  {(() => {
                    const lqip = imageLqip(resolveImageUrl(activeCard.imageUrl));
                    return lqip ? (
                      <div
                        key={`blur-${activeCard.id}`}
                        className="image-blur"
                        aria-hidden="true"
                        style={{ backgroundImage: `url("${lqip}")` }}
                      />
                    ) : null;
                  })()}
                  {activeCard.imageUrl ? (
                    <img
                      key={activeCard.id}
                      src={resolveImageUrl(activeCard.imageUrl)}
                      alt={activeCard.imageAlt || activeCard.headline}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      data-lqip="1"
                      onLoad={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        img.style.display = "";
                        img.classList.add("is-loaded"); // LQIP settle: CSS fades the blur out
                        // A tiny source can't carry a cinematic hero: flip to the type cover.
                        if (img.naturalWidth > 0 && img.naturalWidth < 640) {
                          const id = activeCard.id;
                          setLowResHero((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
                        }
                      }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                  ) : null}
                  <div className="image-overlay" />
                </>
              )}
              <button
                type="button"
                className={`image-save${savedIds.includes(activeCard.id) ? " is-saved" : ""}`}
                onClick={(e) => { e.stopPropagation(); toggleSave(activeCard.id); }}
                aria-pressed={savedIds.includes(activeCard.id)}
                aria-label={savedIds.includes(activeCard.id) ? "Saved to your library. Tap to remove." : "Save to your library"}
                title={savedIds.includes(activeCard.id) ? "Saved" : "Save"}
              >
                <Bookmark size={17} fill={savedIds.includes(activeCard.id) ? "currentColor" : "none"} aria-hidden="true" />
              </button>
              {/* SIGNAL, back on the plate, top-left. v4 moved it into the Receipt line on the
                  one-trust-object-per-card argument, which reads well but costs the score its
                  glance: the Receipt line sits below the fold on a phone and below the story on
                  a desktop, so the number that tells a reader how well sourced this card is only
                  arrived after they had already read it. On the photo it is the first thing on
                  the card, which is where a quality mark belongs. It stays one room: this chip
                  and the Receipt line both open the same drawer. */}
              {activeCard.contentType !== "brief" && activeCard.contentType !== "quiz" ? (
                <div className="image-badges">
                  {(() => {
                    const score = computeSignalScore(activeCard);
                    if (!(score > 0)) return null; // no score, no chip: never render "Signal 0.0"
                    return (
                      <button
                        type="button"
                        className="img-chip signal-chip"
                        onClick={(e) => { e.stopPropagation(); setSourcesOpen(true); }}
                        title={`Signal ${score.toFixed(1)} out of 10: ${signalBand(score)}. Tap to see how it was built.`}
                        aria-label={`Signal score ${score.toFixed(1)} out of 10, ${signalBand(score)}. Open the sources and see how this score was built.`}
                      >
                        <span className="diamond" aria-hidden="true">&#9670;</span>
                        <span className="sig-label">Signal</span>
                        <b>{score.toFixed(1)}</b>
                      </button>
                    );
                  })()}
                  {(activeCard.goodNews || activeCard.tags?.some((t) => t.toLowerCase() === "good news")) && !isGraveStory(activeCard) ? (
                    <span className="img-chip good-news-badge" title="Good News, the daily bright side">☀ Good News</span>
                  ) : null}
                  {/* Must Know: one of the day's three essentials says so on its chrome, and
                      says why on hover. Cards without the payload field show nothing. */}
                  {(() => {
                    const mk = mustKnowOf(activeCard);
                    return mk ? (
                      <span className="img-chip mustknow-chip" title={mk.reason || undefined}>
                        Must know {mk.rank}/3
                      </span>
                    ) : null;
                  })()}
                  {/* Native app: Today's Essential rides on the image as a small chip beside
                      Signal (the roomy in-copy ribbon is hidden on the app), keeping the top tidy. */}
                  {isNativeApp && activeCard.id === todaysEssentialId ? (
                    <span className="img-chip essential-chip" title="Today's Essential">
                      <Star size={11} aria-hidden="true" /> Essential
                    </span>
                  ) : null}
                </div>
              ) : null}
              {activeCard.isEditorsPick ? (
                <div className="pick-ribbon" aria-label="Editor's pick">
                  <Sparkles size={11} aria-hidden="true" />
                  <span>Editor's pick</span>
                </div>
              ) : null}
              {heartKey ? (
                <div key={heartKey} className="dbl-heart" aria-hidden="true">
                  <Heart size={88} />
                </div>
              ) : null}
              {/* No plate band: it printed the story's bullets on the photograph a second time.
                  The photograph is the photograph; the words sit beside it once. */}
            </div>
            <div
              ref={copyRef}
              className={`card-copy${copyHasMore ? " has-more" : ""}`}
              onClick={onCopyTap}
              onScroll={checkCopyHasMore}
            >
              {/* Edition folio: the page number of the spread, an honest little instrument. */}
              {/* THE BYLINE IS THE FOLIO (2026-09-13, the simpler card). One line says the page
                  number, the section, whether it just landed, how long it takes and which round.
                  The meta row (JUST DROPPED, 49S READ, DISCUSSED) and the four-chip kicker row
                  under it said the same things twice in two more voices; both are gone. */}
              {(() => {
                const inRound = !showSavedOnly && filteredCards.length > 1;
                const fresh = isJustIn(activeCard);
                return (
                  <div
                    className={`folio-line${inRound ? "" : " folio-solo"}`}
                    aria-label={inRound ? `Story ${activeIndex + 1} of ${filteredCards.length}` : activeCard.category}
                  >
                    {/* The round count lives in the footer, once: "Round 1, story 1 of 10". */}
                    {activeCard.category ? (
                      <span
                        className="folio-cat"
                        title="Section"
                        style={{ "--chip-hue": `${tagHue(activeCard.category)}` } as React.CSSProperties}
                      >
                        {activeCard.category}
                      </span>
                    ) : null}
                    {fresh ? <span className="folio-fresh" title="Published in the last hour">Just in</span> : null}
                    <span className="folio-rule" aria-hidden="true" />
                    <span className="folio-read" title="Estimated read time">{shortRead(activeCard)}</span>
                  </div>
                );
              })()}
              {resumeCard ? (
                <button type="button" className="resume-line" onClick={() => { const id = resumeCard.id; setResumeAt(null); visitCard(id); }}>
                  <span>You stopped at story {resumeAt!.index + 1}: {tidyText(resumeCard.headline)}</span>
                  <em>Jump back</em>
                </button>
              ) : null}
              {/* Atelier plate caption: the image's own line, set like a gallery caption. */}
              {/* No caption line under the photograph. The stored text is card_assets.alt, i.e.
                  ALT TEXT for a screen reader, and it describes the pixels a sighted reader can
                  already see. displayCaption() is the single place that decides this. */}
              {displayCaption(activeCard.imageAlt) && !lowResHero[activeCard.id] && activeCard.contentType !== "brief" ? (
                <p className="plate-caption">{tidyText(displayCaption(activeCard.imageAlt))}</p>
              ) : null}
              {activeCard.id === todaysEssentialId ? (
                <div className="essential-ribbon" aria-label="Today's Essential">
                  <Star size={12} aria-hidden="true" />
                  <span>Today's Essential</span>
                </div>
              ) : null}
              {isNativeApp ? (
              <div className="meta-row">
                {/* Native app: the actual published DATE sits at the LEFT end of this row (a news
                    app must show real dates for App Store review; it used to sit on the image, where
                    the copy sheet's feathered edge sliced it). The web keeps its relative freshness
                    label instead, where the always-fresh framing is intentional. */}
                {!isNativeApp ? (
                  <time dateTime={activeCard.publishedAt}>{cardTimeLabel(activeCard)}</time>
                ) : activeCard.publishedAt && activeCard.contentType !== "knowledge" ? (
                  <time className="meta-date" dateTime={activeCard.publishedAt}>{formatDate(activeCard.publishedAt)}</time>
                ) : null}
                <div className="meta-pills">
                  {isJustIn(activeCard) ? (
                    <span className="pill just-in" title="Published in the last hour">
                      <span className="live-dot" aria-hidden="true" />
                      Just in
                    </span>
                  ) : null}
                  {activeCard.contentType && activeCard.contentType !== "news" ? (
                    <span className={`pill type-badge type-${activeCard.contentType}`} title="Beyond the news">
                      {activeCard.contentType === "brief"
                        ? "✦ The Brief"
                        : activeCard.contentType === "explainer"
                          ? "◈ Explained"
                          : activeCard.tags?.includes("On This Day")
                            ? "◈ On This Day"
                            : "◈ Knowledge"}
                    </span>
                  ) : null}
                  <span className="pill static" title="Estimated read time">
                    <Clock size={12} aria-hidden="true" />
                    {readingTime(activeCard)}
                  </span>
                  {/* v3 consolidation: the Sources pill folded into the Receipt line below.
                      One trust object per card; the meta row keeps only time and type. */}
                  {activeCard.discussion ? (
                    <span className="pill discussed-chip" title={`Actively discussed on ${activeCard.discussion.source}`}>
                      <MessageCircle size={12} aria-hidden="true" />
                      Discussed
                    </span>
                  ) : null}
                </div>
              </div>
              ) : null}
              {activeCard.id === sharedCardId ? (
                <div className="shared-ribbon">
                  {sharedVia === "favorites"
                    ? <><Bookmark size={13} aria-hidden="true" /> From your favourites</>
                    : <><Sparkles size={13} aria-hidden="true" /> Shared with you</>}
                </div>
              ) : null}
              {/* The tags read inside the dive now ("Filed under"); the folio carries the section. */}
              {/* The headline carries the "card-headline" transition name ONLY while the deep
                  dive is closed, the dive title takes it over while open (duplicates break VT). */}
              <h1 style={deepDiveOpen || deskOpen ? undefined : ({ viewTransitionName: "card-headline" } as React.CSSProperties)}>{keepCompounds(tidyText(activeCard.headline))}</h1>
              <p className="essence">{tidyText(activeCard.essence)}</p>
              {/* The publisher, on the face of the card: readers judge a summary by who stands
                  behind it before anything else. One tap opens Verify. */}
              {publisherLine ? (
                <button type="button" className="card-publisher" onClick={() => setSourcesOpen(true)} aria-label={`Sources: ${publisherLine}. Open Verify`}>
                  <ShieldCheck size={12} aria-hidden="true" />
                  <span>{publisherLine}</span>
                </button>
              ) : null}
              {/* Deep Dive: an invitation above the reading toggle. */}
              {(() => {
                const dd = deepDiveFor(activeCard);
                return dd && !phoneWeb ? (
                  <button type="button" className="deepdive-entry" data-tour="deepdive-entry" onClick={openDeepDive}>
                    <span className="deepdive-entry-icon" aria-hidden="true"><Compass size={16} /></span>
                    <span className="deepdive-entry-text">
                      <span className="deepdive-entry-label">Deep Dive</span>
                      <span className="deepdive-entry-sub">How it got here, in {dd.beats.length} steps</span>
                    </span>
                    <span className="deepdive-entry-go" aria-hidden="true"><ArrowRight size={16} /></span>
                  </button>
                ) : null;
              })()}
              {/* Reading depth toggle: Quick is the key-points scan; Deeper is the middle-tier
                  fuller-story read (deepDive.deeperRead paragraphs + a verbatim quote, grounded in
                  the source). The toggle only appears when a real deeperRead exists, so "Deeper" is
                  ALWAYS genuinely deeper than Quick and never just the same body restated; a card
                  with only a thin source shows the Quick key points with no misleading toggle. */}
              {readLevel === "quick" && cardPoints.length >= 2 ? (
                <ul className={bionicMode ? "body-points bionic" : "body-points"}>
                  {cardPoints.map((point, i) => <li key={i}>{bionicMode ? bionicNodes(point) : markFigures(point)}</li>)}
                </ul>
              ) : (
                (() => {
                  const dd = readLevel === "deeper" ? deepDiveFor(activeCard) : null;
                  const deeper = dd?.deeperRead;
                  return (
                <div className="full-read">
                  {activeCard.catchUp?.length ? (
                    <div className="catch-up" aria-label="Catch up">
                      <span className="catch-up-label">Catch up</span>
                      <ul>
                        {activeCard.catchUp.slice(0, 3).map((point, i) => <li key={i}>{point}</li>)}
                      </ul>
                    </div>
                  ) : null}
                  {deeper?.paragraphs?.length ? (
                    <div className="deeper-read" aria-label="The fuller story">
                      {deeper.paragraphs.map((para, i) => (
                        <p className={bionicMode ? "body-copy bionic" : "body-copy"} key={i}>
                          {bionicMode ? bionicNodes(para) : para}
                        </p>
                      ))}
                      {deeper.quote?.text ? (
                        <blockquote className="deeper-quote">
                          <p>{deeper.quote.text}</p>
                          {deeper.quote.speaker ? <cite>{deeper.quote.speaker}</cite> : null}
                        </blockquote>
                      ) : null}
                    </div>
                  ) : (
                    <p className={bionicMode ? "body-copy bionic" : "body-copy"}>
                      {bionicMode ? bionicNodes(activeCard.body) : activeCard.body}
                    </p>
                  )}
                  {activeCard.notes?.length ? (
                    <div className="notes-box" aria-label="Jargon, explained">
                      <span className="notes-label">Good to know</span>
                      <dl>
                        {activeCard.notes.slice(0, 4).map((n, i) => (
                          <div className="note-row" key={i}>
                            <dt>{n.term}</dt>
                            <dd>{n.note}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                  {/* Same rule as the dive panel and the plate: a placeholder label never prints,
                      and sentences are set as lines rather than as figures. */}
                  {(() => {
                    const shown = displayFacts(dd?.keyFacts);
                    if (!shown.facts.length) return null;
                    const heading = shown.mode === "lines" ? "What was reported" : "Key facts";
                    return (
                      <div className="depth-deeper" aria-label={heading}>
                        <span className="depth-eli5-lead">{heading}</span>
                        <div className={`depth-facts${shown.mode === "lines" ? " is-lines" : ""}`}>
                          {shown.facts.slice(0, 5).map((f, i) => (
                            <div className={`df${shown.mode === "lines" ? " is-line" : ""}`} key={i}>
                              <span className="df-val">{f.value}</span>
                              {f.label ? <span className="df-cap">{f.label}</span> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                  );
                })()
              )}
              {/* Why THIS card is in the round, with the dials to steer tomorrow right beside
                  the reason. More/Less reuse the drawer's tuning handlers on the card's
                  category; the drawer keeps its fuller controls. Absent whyShown = no row. */}
              {/* READ THE FULL STORY. One quiet line under the pointers opens, in place: why it
                  matters, the story (the fuller read when the pipeline authored one, else the
                  body), its quote, the key facts and the Australian angle. Everything the Deeper
                  tab, the why-box and the Australia box used to spread across the card face,
                  behind one word instead of three instruments. Members still have the dive. */}
              {cardPoints.length >= 2 && !phoneWeb && activeCard.contentType !== "brief" && activeCard.contentType !== "quiz" ? (() => {
                const dd = deepDiveFor(activeCard);
                const deeper = dd?.deeperRead;
                const paragraphs = deeper?.paragraphs?.length ? deeper.paragraphs : bodyParagraphs(activeCard.body);
                const facts = storyOpen ? displayFacts(dd?.keyFacts) : null;
                const au = activeCard.payload?.auImpact;
                return (
                  <>
                    <button
                      type="button"
                      className="story-more"
                      aria-expanded={storyOpen}
                      onClick={(event) => {
                        event.stopPropagation();
                        setStoryOpen((open) => !open);
                        if (!storyOpen) logEngagement("expand", activeCard.id);
                        window.setTimeout(checkCopyHasMore, 380);
                      }}
                    >
                      <span>{storyOpen ? "Fold the story away" : "Read the full story"}</span>
                      <ChevronDown size={15} aria-hidden="true" />
                    </button>
                    {storyOpen ? (
                      <div className="story-full">
                        {activeCard.whyItMatters ? (
                          <div className="sf-block">
                            <span className="sf-label">Why it matters</span>
                            <p className="sf-why">{activeCard.whyItMatters}</p>
                          </div>
                        ) : null}
                        {paragraphs.length ? (
                          <div className="sf-block">
                            <span className="sf-label">{deeper?.paragraphs?.length ? "The fuller story" : "The story"}</span>
                            <div className="sf-story">
                              {paragraphs.map((para, i) => (
                                <p className={bionicMode ? "bionic" : undefined} key={i}>{bionicMode ? bionicNodes(para) : para}</p>
                              ))}
                            </div>
                            {deeper?.quote?.text ? (
                              <blockquote className="sf-quote">
                                <p>{deeper.quote.text}</p>
                                {deeper.quote.speaker ? <cite>{deeper.quote.speaker}</cite> : null}
                              </blockquote>
                            ) : null}
                          </div>
                        ) : null}
                        {facts && facts.facts.length ? (
                          <div className="sf-block">
                            <span className="sf-label">{facts.mode === "lines" ? "What was reported" : "Key facts"}</span>
                            <div className={`depth-facts${facts.mode === "lines" ? " is-lines" : ""}`}>
                              {facts.facts.slice(0, 5).map((f, i) => (
                                <div className={`df${facts.mode === "lines" ? " is-line" : ""}`} key={i}>
                                  <span className="df-val">{f.value}</span>
                                  {f.label ? <span className="df-cap">{f.label}</span> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {typeof au === "string" && au.trim() ? (
                          <div className="sf-block sf-au">
                            <span className="sf-label">Australia</span>
                            <p>{au}</p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                );
              })() : null}
              {/* Kindle's footer line (2026-09-13): how long the round has left, how far in. On a
                  phone the copy is the page, so the line is its last line; on a desktop the copy
                  is a scroll box and the line is pinned under it instead (see the actions zone). */}
              {/* Said once, in words: the round and the story, no minutes-left, no percentage. */}
              {phoneWeb && !showSavedOnly && filteredCards.length > 1 ? (
                <div className="read-left" aria-label="Your place in this round">
                  <span>{serverDrivenRef.current ? `Round ${dayRound}, story ${activeIndex + 1} of ${filteredCards.length}` : `Story ${activeIndex + 1} of ${filteredCards.length}`}</span>
                </div>
              ) : null}
              {(() => {
                const why = whyShownLabel(activeCard.whyShown);
                return why ? (
                  <div className="why-shown">
                    <span className="why-shown-line">{why}</span>
                    {activeCard.category ? (
                      <span className="why-shown-tune">
                        <button
                          type="button"
                          className={topicState(topicSignals, activeCard.category) === "more" ? "why-tune-btn active" : "why-tune-btn"}
                          onClick={() => tuneTopic(activeCard.category, 1, activeCard.id)}
                          aria-pressed={topicState(topicSignals, activeCard.category) === "more"}
                          title={`More ${activeCard.category} in your rounds`}
                        >
                          More
                        </button>
                        <button
                          type="button"
                          className={topicState(topicSignals, activeCard.category) === "less" ? "why-tune-btn active" : "why-tune-btn"}
                          onClick={() => tuneTopic(activeCard.category, -1, activeCard.id)}
                          aria-pressed={topicState(topicSignals, activeCard.category) === "less"}
                          title={`Less ${activeCard.category} in your rounds`}
                        >
                          Less
                        </button>
                      </span>
                    ) : null}
                  </div>
                ) : null;
              })()}
              {/* The snip sweep used to ride the why-box; the box is inside the disclosure now,
                  so the sweep rides the copy column on its own. */}
              {snipCardId === activeCard.id ? (
                <span key={snipKey} className="snip-sweep" aria-hidden="true" />
              ) : null}
              {/* The Australian angle, the why-box's sibling. The pipeline writes it honestly,
                  so "No direct Australian effect yet" renders like any other value: absence of
                  impact is information, absence of the FIELD is the only thing that hides it. */}
              {/* v3 Receipts: the card's ONE trust object (signal + sources + strength),
                  replacing the photo chip and the action-row Verify buttons. */}
              <ReceiptStrip
                card={activeCard}
                signal={computeSignalScore(activeCard)}
                onVerify={() => setSourcesOpen(true)}
              />
              {/* v3 "Ask the story": grounded follow-up chips, instant and free. */}
              {phoneWeb ? null : <FollowUpChips
                key={`fu-${activeCard.id}`}
                card={activeCard}
                onAskMore={sessionApiEnabled() ? openAsk : null}
              />}
              {/* Per-card feedback, directly under Ask the story: the reader has just finished the
                  card and, if something in it was wrong, this is the moment they know it. It sits
                  below Ask deliberately, so the offer to learn more comes before the offer to
                  complain. Briefs and quizzes are not single claims, so they do not carry it. */}
              {activeCard.contentType !== "brief" && activeCard.contentType !== "quiz" ? (
                <button
                  type="button"
                  className="card-feedback-btn"
                  onClick={(event) => { event.stopPropagation(); openFeedback({ kind: "card", cardId: activeCard.id }); }}
                  aria-label="Send feedback on this card"
                >
                  <MessageCircle size={12} aria-hidden="true" />
                  <span>Feedback on this card</span>
                </button>
              ) : null}
              {/* Scroll cue: shown only when the body sits below the fold, so the reader knows
                  there is more. Tapping nudges the copy down; it hides once scrolled to the end. */}
              {/* On a phone the pill sat on top of "Read the full story" and the last bullet;
                  there, the text toggle is the one affordance. */}
              {phoneWeb ? null : <div className={`copy-more${copyHasMore ? " show" : ""}`} aria-hidden={!copyHasMore}>
                <button
                  type="button"
                  className="copy-more-pill"
                  tabIndex={copyHasMore ? 0 : -1}
                  onClick={(event) => {
                    event.stopPropagation();
                    const el = copyRef.current;
                    if (el) el.scrollBy({ top: Math.round(el.clientHeight * 0.72), behavior: "smooth" });
                  }}
                  aria-label="Scroll to read the full story"
                >
                  <span>Keep reading</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </button>
              </div>}
            </div>
            <div className="card-actions-zone">
                {!phoneWeb && !showSavedOnly && filteredCards.length > 1 ? (
                  <div className="read-left read-left-pin" aria-label="Your place in this round">
                    <span>{serverDrivenRef.current ? `Round ${dayRound}, story ${activeIndex + 1} of ${filteredCards.length}` : `Story ${activeIndex + 1} of ${filteredCards.length}`}</span>
                  </div>
                ) : null}
                <div className="action-row" data-tour="actions">
                  <div className="react-verify-row">
                  <div className={`reaction-row${reactOpen ? " open" : ""}`} data-tour="react" aria-label="React to this card">
                  {/* One Like on the row; the five expressive reactions open behind one "React"
                      control, so a card is not rated three ways at once. A death story takes
                      no flame or sparkle: the row narrows to the two reactions that read as
                      respect (Insightful, Pondering). */}
                  <button
                    type="button"
                    className={`reaction react-toggle${reactOpen ? " active" : ""}`}
                    onClick={(event) => { event.stopPropagation(); setReactOpen((o) => !o); }}
                    aria-expanded={reactOpen}
                    aria-label={reactOpen ? "Hide reactions" : "React to this story"}
                    title="React"
                    data-label="React"
                  >
                    <Sparkles size={16} aria-hidden="true" />
                  </button>
                  {reactOpen ? REACTIONS.filter((reaction) => reaction.id !== "like")
                    .filter((reaction) => !isGraveStory(activeCard) || reaction.id === "insight" || reaction.id === "pondering")
                    .map((reaction) => {
                    const Icon = reaction.Icon;
                    const count = cardReactions.filter((id) => id === reaction.id).length;
                    const active = count > 0;
                    return (
                      <button
                        key={reaction.id}
                        type="button"
                        data-rid={reaction.id}
                        className={active ? "reaction active" : "reaction"}
                        onClick={(event) => react(activeCard.id, reaction.id, event)}
                        aria-label={`React: ${reaction.label}`}
                        title={reaction.label}
                        data-label={reaction.label}
                        style={{ "--tint": reaction.tint } as React.CSSProperties}
                      >
                        <Icon size={16} aria-hidden="true" />
                        <span className="burst-glow" aria-hidden="true" />
                        <span className="burst-emoji" aria-hidden="true">{reaction.emoji}</span>
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        <span className="burst-particle" aria-hidden="true" />
                        {count ? <em className="reaction-count">{count}</em> : null}
                      </button>
                    );
                  }) : null}
                  {/* Like lives INSIDE the reaction pill, pinned to the right end as the primary
                      positive tap; the five expressive emojis sit to its left, set off by a
                      hairline. It obeys the grave-story guard too: "liking" a death is not a
                      tap this product offers. */}
                  {isGraveStory(activeCard) ? null : (() => {
                    const likeCount = cardReactions.filter((id) => id === "like").length;
                    return (
                      <button
                        type="button"
                        data-rid="like"
                        className={`reaction like-standalone${likeCount > 0 ? " active" : ""}`}
                        onClick={(event) => react(activeCard.id, "like", event)}
                        aria-label="Like this card"
                        aria-pressed={likeCount > 0}
                        title="Like"
                        data-label="Like"
                        style={{ "--tint": "#5fd08a" } as React.CSSProperties}
                      >
                        <ThumbsUp size={16} aria-hidden="true" />
                        {likeCount ? <em className="reaction-count">{likeCount}</em> : null}
                      </button>
                    );
                  })()}
                  </div>
                  </div>
                  <div className="util-actions">
                    {/* VERIFY, restored to the verbs, and deliberately not a second room.
                        v4 folded it into the Receipt line above on the reasoning that one card
                        deserves one trust object. The line is good and it stays, but it is a
                        score ("Signal 7.8, 1 source, Single source"), and a score does not tell
                        a first-time reader that they can go and check. The landing page sells
                        this product on "Tap Verify"; the reader has to contain the verb it
                        promises. Both doors open the same drawer, so nothing is duplicated
                        except the invitation. */}
                    <button
                      type="button"
                      className="util-btn util-verify"
                      data-tour="verify-btn"
                      onClick={() => setSourcesOpen(true)}
                      title="Verify: the sources, credibility and gate-check behind this snip"
                      aria-label="Verify this story: sources, credibility and perspectives"
                    >
                      <ShieldCheck size={17} aria-hidden="true" />
                      <span>Verify</span>
                    </button>
                    <button
                      type="button"
                      className="util-btn util-share"
                      onClick={() => setShareCard(activeCard)}
                      title="Share this story"
                      aria-label="Share this story"
                    >
                      <Share2 size={17} aria-hidden="true" />
                      <span>Share</span>
                    </button>
                    {sessionApiEnabled() && !phoneWeb ? (
                      <button
                        type="button"
                        className="util-btn util-ask"
                        onClick={openAsk}
                        title="Ask a question about this story"
                        aria-label="Ask about this story"
                      >
                        <MessageCircle size={17} aria-hidden="true" />
                        <span>Ask</span>
                      </button>
                    ) : null}
                    {speechSupported ? (
                      <button
                        type="button"
                        className={`util-btn util-listen${briefingSession ? " active" : ""}`}
                        onClick={toggleBriefing}
                        title="Play the drop: listen hands-free, auto-advancing"
                        aria-label={briefingSession ? "Pause listening" : "Listen to the drop"}
                      >
                        {briefingSession ? <Pause size={17} aria-hidden="true" /> : <Volume2 size={17} aria-hidden="true" />}
                        <span>Listen</span>
                      </button>
                    ) : null}
                  </div>
                </div>
                {/* The per-card "Call it: Signal / Noise" commitment row was removed from the
                    reader. It sat under the action row on every card and cost the fold a band it
                    did not earn, and it duplicated the promise of the Signal or Noise daily game,
                    which keeps its own home in the rail. The stance plumbing (worker /stance, the
                    stances state, verdict delivery) is left intact so calls already made still
                    resolve; nothing new can be called from the card. */}
                {/* What they turned down at the door, one true line at a time, INSIDE the card
                    so it scrolls away with the story instead of joining the fixed chrome.
                    Only ever rendered for a phone reader who declined the app. */}
                {appNudge ? <AppNudge platform={storePlatform} onClick={takeApp} /> : null}
              </div>
            <div className="card-grain" aria-hidden="true" />
          </article>
          <button
            type="button"
            className="nav-arrow nav-next"
            data-tour="edge-next"
            onClick={() => move(1)}
            aria-label="Next card"
            title="Next (→)"
            disabled={!serverDrivenRef.current && activeIndex >= filteredCards.length - 1}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>

          {hookLine && !showSavedOnly && !bodyExpanded ? (
            <p className="reader-hook" aria-hidden="true"><span>{hookLine}</span></p>
          ) : null}

          <div className="reader-toolbar">
            <div className="toolbar-info">
              {showSavedOnly ? (
                <>
                  <strong className="tb-round">Saved</strong>
                  <em className="tb-count">{filteredCards.length ? `${activeIndex + 1} of ${filteredCards.length}` : "Your reading list"}</em>
                </>
              ) : serverDrivenRef.current ? (
                <>
                  <strong className="tb-round">Round {dayRound}</strong>
                  <em className="tb-count">{(() => {
                    // Said once: the story and the round, in words, with commas.
                    // Under a topic the count is the topic's, and the round is not over just
                    // because the topic is.
                    if (activeTag !== "All") return `${activeTag}, story ${activeIndex + 1} of ${filteredCards.length}`;
                    const left = Math.max(0, filteredCards.length - (activeIndex + 1));
                    if (!hasMoreCards) return left > 0 ? `story ${activeIndex + 1} of ${filteredCards.length}, ${left} left today` : "You're up to date";
                    if (left === 0) return `Round ${dayRound + 1} unlocked`;
                    return `story ${activeIndex + 1} of ${filteredCards.length}, ${left} more to Round ${dayRound + 1}`;
                  })()}</em>
                </>
              ) : (
                <>
                  {totalRounds > 1 ? <strong className="tb-round">Round {roundNumber}/{totalRounds}</strong> : null}
                  <em className="tb-count">{activeIndex + 1} of {filteredCards.length}</em>
                </>
              )}
            </div>
            {!showSavedOnly && filteredCards.length > 1 ? (
              /* The round as a filmstrip: each story's own photograph is its marker.
                 On wide screens the cells grow into thumbnails (tap = jump to that
                 story); below that the same cells render as the classic dots. */
              <nav className="round-dots" aria-label="Stories in this round">
                {filteredCards.slice(0, 12).map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`rdot${i === activeIndex ? " now" : i < activeIndex ? " done" : ""}`}
                    aria-label={`Story ${i + 1} of ${Math.min(filteredCards.length, 12)}: ${c.headline}`}
                    aria-current={i === activeIndex ? "true" : undefined}
                    title={c.headline}
                    onClick={() => visitCard(c.id)}
                  >
                    {c.imageUrl ? (
                      <img
                        src={resolveImageUrl(c.imageUrl)}
                        alt=""
                        /* The first cells paint with the page: seven grey boxes sitting in
                           the strip seconds after load was the audit's 1440px shabbiness. */
                        loading={i < 4 ? "eager" : "lazy"}
                        /* A dead image collapses to the classic dot instead of shipping a
                           blank white tile (one broken thumb glowed in all four themes). */
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : null}
                  </button>
                ))}
              </nav>
            ) : null}
            <div className="nav-buttons" data-tour="nav-buttons">
              {showSavedOnly ? (
                <button type="button" className="icon-button toolbar-home" onClick={goHome} aria-label="Back to your feed" title="Back to your feed">
                  <Home size={16} aria-hidden="true" />
                  <span>Home</span>
                </button>
              ) : (
                <>
                  <button type="button" className="icon-button toolbar-nav-prev" aria-label="Previous card (←)" onClick={() => move(-1)} disabled={activeIndex <= 0}><ChevronLeft size={18} /></button>
                  <button
                    type="button"
                    className={`icon-button toolbar-play${slideshowMode !== "off" ? " playing" : ""}`}
                    aria-label={slideshowMode !== "off" ? "Pause slideshow" : "Start slideshow"}
                    title={slideshowMode !== "off" ? "Pause slideshow" : "Play slideshow"}
                    onClick={toggleSlideshow}
                  >
                    {slideshowMode !== "off" ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button type="button" className="icon-button toolbar-nav-next" aria-label="Next card (→)" onClick={() => move(1)} disabled={(!serverDrivenRef.current || showSavedOnly) && activeIndex >= filteredCards.length - 1}><ChevronRight size={18} /></button>
                  {/* The shortcuts card is genuinely good and completely undiscoverable
                      (the audit's power reader never found j/k/d/g). One quiet door. */}
                  <button type="button" className="icon-button kbd-hint-btn" onClick={() => setShortcutsOpen(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)"><span aria-hidden="true">?</span></button>
                </>
              )}
            </div>
            {/* No second thumbs here: Like lives on the card's action row, and "Less like this"
                in the card menu covers the other direction. One way to rate a card. */}
          </div>
        </section>

        <aside className="right-rail" aria-label="Up next">
          <button type="button" className="goodnews-tile" onClick={() => setGoodNewsOpen(true)}>
            <span className="goodnews-tile-sun" aria-hidden="true"><Sun size={18} /></span>
            <span className="goodnews-tile-copy">
              <span className="goodnews-tile-eyebrow">The bright side</span>
              <strong className="goodnews-tile-title">Good News</strong>
              <span className="goodnews-tile-sub">
                {!goodNewsCards.length
                  ? "The day's good side"
                  : goodNewsToday
                    ? `${goodNewsToday} good ${goodNewsToday === 1 ? "story" : "stories"} today`
                    : `${goodNewsCards.length} good ${goodNewsCards.length === 1 ? "story" : "stories"} waiting`}
              </span>
            </span>
            <ChevronRight size={16} aria-hidden="true" className="goodnews-tile-caret" />
          </button>
          <span className="rail-heading">Up next</span>
          <div className="card-list" role="list">
            {filteredCards.map((card) => {
              const isActive = card.id === activeCard.id;
              const isViewed = viewedIds.includes(card.id);
              return (
                <button key={card.id} type="button" role="listitem" className={["list-card", isActive ? "active" : "", !isActive && isViewed ? "viewed" : ""].join(" ").trim()} onClick={() => visitCard(card.id)}>
                  {card.imageUrl ? (
                    <span className="list-thumb" style={{ backgroundImage: `url(${card.imageUrl})` }} aria-hidden="true" />
                  ) : null}
                  <div className="list-card-top">
                    <span style={{ "--chip-hue": `${tagHue(card.category)}` } as React.CSSProperties}>{card.category}</span>
                    <div className="list-card-icons">
                      {card.isEditorsPick ? <Sparkles size={12} aria-label="Editor's pick" /> : null}
                      {savedIds.includes(card.id) ? <Bookmark size={12} aria-label="Saved" /> : null}
                    </div>
                  </div>
                  <strong>{card.headline}</strong>
                  <small>{cardMeta(card)}</small>
                </button>
              );
            })}
          </div>
        </aside>

        {/* The QR "get the app" note is gone from the reader: at wide viewports it sat
            on top of the story copy (the Edition spread grows with the window, so there
            is no safe gutter at any width). The app stays reachable from the desk footer
            and the landing page. */}

        {/* THE WIRE: the news desk column that fills the wide-screen margin with the
            furniture every real front page has (Up next, the freshest drops, sections).
            Visible >=1500px only (CSS); everything on it is live data and every line is
            a control. Never in the native app shell, never in focus mode. */}
        {!isNativeApp && !showSavedOnly && filteredCards.length > 0 ? (() => {
          const wireUpNext = filteredCards.slice(activeIndex + 1, activeIndex + 4);
          // Just in never repeats what the reader is looking at or about to read:
          // each module carries its own information or it is furniture.
          const wireSeen = new Set([activeCard.id, ...wireUpNext.map((c) => c.id)]);
          const wireJustIn = [...cards]
            .filter((c) => c.contentType !== "knowledge" && c.publishedAt && !wireSeen.has(c.id))
            .sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""))
            .slice(0, 3);
          const sectionCounts = new Map<string, number>();
          for (const c of filteredCards) {
            if (c.category) sectionCounts.set(c.category, (sectionCounts.get(c.category) ?? 0) + 1);
          }
          const wireSections = [...sectionCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 7);
          const openWireCard = openAnyCard;
          const wireSaved = [...savedIds]
            .reverse()
            .map((id) => cards.find((c) => c.id === id) ?? savedCardCache[id])
            .filter((c): c is InSnipCard => Boolean(c))
            .slice(0, 2);
          return (
            <div className="wire-col">
            <aside className="news-wire" aria-label="Today's news desk">
              <div className="nw-mast">
                <span className="nw-live" aria-hidden="true" />
                <span className="nw-title">The Wire</span>
                <button
                  type="button"
                  className="nw-digest"
                  onClick={() => setDigestOpen(true)}
                  title="Every story on one sheet"
                >
                  The {Math.max(2, Math.round((filteredCards.length * 25) / 60))} minute day
                </button>
              </div>
              {wireUpNext.length ? (
                <section className="nw-mod" aria-label="Up next">
                  <h3 className="nw-h">Up next</h3>
                  {wireUpNext.map((c, wi) => (
                    <button key={c.id} type="button" className="nw-story" onClick={() => openWireCard(c)} title={c.headline}>
                      {c.imageUrl ? <img src={resolveImageUrl(c.imageUrl)} alt="" loading="lazy" /> : <span className="nw-thumb-x" aria-hidden="true" />}
                      <span className="nw-story-copy">
                        <em className="nw-kicker">
                          <b className="nw-no">{String(activeIndex + 2 + wi).padStart(2, "0")}</b>
                          {c.category}
                        </em>
                        <span className="nw-line">{c.headline}</span>
                      </span>
                    </button>
                  ))}
                </section>
              ) : null}
              {wireJustIn.length ? (
                <section className="nw-mod" aria-label="Fresh off the wire">
                  <h3 className="nw-h nw-h-live">Just in</h3>
                  {wireJustIn.map((c) => (
                    <button key={c.id} type="button" className="nw-brief" onClick={() => openWireCard(c)} title={`${cardTimeLabel(c) || "Latest"}: ${c.headline}`}>
                      <span className="nw-line">{c.headline}</span>
                    </button>
                  ))}
                </section>
              ) : null}
              {wireSections.length > 1 ? (
                <section className="nw-mod" aria-label="Sections">
                  <h3 className="nw-h">Sections</h3>
                  <div className="nw-chips">
                    {wireSections.map(([cat, n]) => {
                      const followed = topicPrefs[cat] === "follow";
                      return (
                        <span key={cat} className={`nw-chip${followed ? " followed" : ""}`}>
                          <button
                            type="button"
                            className="nw-chip-open"
                            onClick={() => openSearchFor(cat)}
                            title={`Open ${cat}`}
                          >
                            {trending.includes(cat) ? <span className="nw-flame" aria-hidden="true" /> : null}
                            {cat}
                            <em>{n}</em>
                          </button>
                          <button
                            type="button"
                            className="nw-follow"
                            aria-pressed={followed}
                            aria-label={followed ? `Unfollow ${cat}` : `Follow ${cat}`}
                            title={followed ? `Following ${cat}. Its stories lead your For you lens` : `Follow ${cat} to lead your For you lens`}
                            onClick={() => toggleTopicPref(cat, "follow")}
                          >
                            <Star size={11} aria-hidden="true" fill={followed ? "currentColor" : "none"} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              {wireSaved.length ? (
                <section className="nw-mod" aria-label="Saved for later">
                  <h3 className="nw-h">Saved for later</h3>
                  {wireSaved.map((c) => (
                    <button key={c.id} type="button" className="nw-brief" onClick={() => openWireCard(c)} title={c.headline}>
                      <span className="nw-line">{c.headline}</span>
                    </button>
                  ))}
                  <button type="button" className="nw-more" onClick={() => setLibraryOpen(true)}>
                    Open your library ({savedIds.length})
                  </button>
                </section>
              ) : null}
            </aside>

            {/* The app, its own object: a standalone plate under the wire, where a
                QR actually works (a desktop screen a phone can scan). Bigger and
                centred so it reads as the page's one invitation. */}
            <aside className="app-plate" aria-label="Get the InSnip app">
              <span className="ap-head">
                <AppIcon size={34} />
                <span>
                  <em className="ap-eyebrow">On your phone</em>
                  <strong className="ap-line">Take the day with you</strong>
                </span>
              </span>
              <a
                className="ap-qr"
                href={GET_URL}
                target="_blank"
                rel="noopener noreferrer"
                title="insnip.com/get"
                onClick={() => logEngagement("app_get_click", "wire_qr")}
              >
                <img src={QR_GET_DATAURI} alt="QR code that opens insnip.com/get" width={118} height={118} />
              </a>
              <span className="ap-sub">Scan for the free app</span>
              <div className="ap-badges">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  onClick={() => logEngagement("app_get_click", "wire_ios")}
                >
                  <img src="/app-store-badge.svg" alt="Download on the App Store" width={104} height={31} />
                </a>
                <a
                  href={PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  onClick={() => logEngagement("app_get_click", "wire_android")}
                >
                  <img src="/google-play-badge.png" alt="Get it on Google Play" width={104} height={31} />
                </a>
              </div>
            </aside>
            </div>
          );
        })() : null}
      </section>

      {/* THE DIGEST: the whole front page on one sheet. Skim every story's essence in
          order, tap any line to read it in full; the ledger and share loop close it.
          Built entirely from content already on the page. */}
      {digestOpen ? (() => {
        const digestMin = Math.max(2, Math.round((filteredCards.length * 25) / 60));
        return (
          <section className="digest-overlay" role="dialog" aria-modal="true" aria-label="The whole front page" onClick={() => setDigestOpen(false)}>
            <div className="digest-sheet" onClick={(e) => e.stopPropagation()}>
              <header className="digest-mast">
                <span className="digest-eyebrow">The whole front page</span>
                <h2 className="digest-title">Today, in {filteredCards.length} stories</h2>
                <p className="digest-sub">About {digestMin} minutes. Every story source-checked.</p>
                <button type="button" className="digest-close" aria-label="Close the digest" onClick={() => setDigestOpen(false)}>
                  <X size={16} aria-hidden="true" />
                </button>
              </header>
              <ol className="digest-list">
                {filteredCards.map((c, i) => {
                  const read = viewedIds.includes(c.id) || c.id === activeId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={`digest-item${read ? " read" : ""}`}
                        onClick={() => { setDigestOpen(false); visitCard(c.id); }}
                      >
                        <span className="di-no">{String(i + 1).padStart(2, "0")}</span>
                        <span className="di-copy">
                          <em className="di-kicker">{c.category ?? "Today"}</em>
                          <strong className="di-h">{c.headline}</strong>
                          <span className="di-essence">{c.essence}</span>
                        </span>
                        <span className="di-state" aria-hidden="true">{read ? "✓" : ""}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <footer className="digest-foot">
                <button
                  type="button"
                  className="round-go"
                  onClick={() => {
                    setDigestOpen(false);
                    const next = filteredCards.find((c) => !viewedIds.includes(c.id) && c.id !== activeId);
                    if (next) visitCard(next.id);
                  }}
                >
                  Read the next story
                </button>
                <button type="button" className="ghost-action" onClick={() => void shareEdition()}>
                  <Share2 size={14} aria-hidden="true" /> Share this front page
                </button>
              </footer>
            </div>
          </section>
        );
      })() : null}

      {paletteOpen ? (() => {
        const commands: { id: string; label: string; hint: string; run: () => void }[] = [
          { id: "next", label: "Next story", hint: "j", run: () => move(1) },
          { id: "prev", label: "Previous story", hint: "k", run: () => move(-1) },
          { id: "search", label: "Search every story", hint: "/", run: openSearch },
          { id: "brief", label: "Read the Brief", hint: "the day in a minute", run: openBrief },
          { id: "good", label: "Open Good News", hint: "the bright side", run: () => setGoodNewsOpen(true) },
          { id: "edition", label: "Open today's edition", hint: "the front page", run: () => setDeskOpen(true) },
          { id: "jump", label: "Jump to a story", hint: "feed overview", run: () => setOverviewOpen(true) },
          { id: "game", label: "Play Signal or Noise", hint: "daily game", run: () => setGameOpen(true) },
          { id: "saved", label: showSavedOnly ? "Back to the feed" : "Open saved stories", hint: "your library", run: toggleSavedView },
          { id: "theme", label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme", hint: "appearance", run: toggleTheme },
          { id: "trust", label: "How every story is checked", hint: "sourcing", run: () => setTrustOpen(true) },
          { id: "keys", label: "Keyboard shortcuts", hint: "help", run: () => setShortcutsOpen(true) },
        ];
        const q = paletteQuery.trim().toLowerCase();
        const shown = !q
          ? commands
          : commands
              .map((c) => {
                const label = c.label.toLowerCase();
                let score = -1;
                if (label.startsWith(q)) score = 0;
                else if (label.includes(` ${q}`)) score = 1;
                else if (label.includes(q)) score = 2;
                else if (c.hint.toLowerCase().includes(q)) score = 3;
                return { c, score };
              })
              .filter((s) => s.score >= 0)
              .sort((a, b) => a.score - b.score)
              .map((s) => s.c);
        return (
          <section className="palette-overlay" role="dialog" aria-modal="true" aria-label="Commands" onClick={() => setPaletteOpen(false)}>
            <div className="palette-box" onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                className="palette-input"
                value={paletteQuery}
                placeholder="Type a command…"
                aria-label="Type a command"
                onChange={(e) => setPaletteQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  if (shown[0]) { setPaletteOpen(false); shown[0].run(); }
                  else if (paletteHits[0]) { setPaletteOpen(false); openPaletteCard(paletteHits[0]); }
                }}
              />
              <ul className="palette-list">
                {shown.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => { setPaletteOpen(false); c.run(); }}>
                      <strong>{c.label}</strong>
                      <em>{c.hint}</em>
                    </button>
                  </li>
                ))}
                {/* Live story hits: the palette doubles as search (members, best-effort). */}
                {paletteHits.map((card) => (
                  <li key={`hit-${card.id}`}>
                    <button type="button" onClick={() => { setPaletteOpen(false); openPaletteCard(card); }}>
                      <strong>{`Read: ${card.headline.length > 60 ? `${card.headline.slice(0, 59).trimEnd()}…` : card.headline}`}</strong>
                      <em>{card.category || "story"}</em>
                    </button>
                  </li>
                ))}
                {shown.length === 0 && paletteHits.length === 0 ? <li className="palette-empty">No command matches that.</li> : null}
              </ul>
              <footer className="palette-foot">
                <span><kbd>Enter</kbd> run</span>
                <span><kbd>Esc</kbd> close</span>
                <span><kbd>Ctrl</kbd><kbd>K</kbd> toggle</span>
              </footer>
            </div>
          </section>
        );
      })() : null}

      {overviewOpen ? (
        <section className="overview-overlay" role="dialog" aria-modal="true" aria-label="Jump to a story" onClick={() => setOverviewOpen(false)}>
          <div className="overview-modal" onClick={(e) => e.stopPropagation()}>
            <header className="overview-head">
              <div>
                <span className="overview-eyebrow"><Layers size={12} aria-hidden="true" /> Your feed</span>
                <h2 className="overview-title">Jump to a story</h2>
                <span className="overview-sub">{filteredCards.length} {filteredCards.length === 1 ? "story" : "stories"} in your feed · tap any to read</span>
              </div>
              <button type="button" className="brief-close" onClick={() => setOverviewOpen(false)} aria-label="Close"><X size={18} /></button>
            </header>
            {(() => {
              const deepCount = filteredCards.filter((c) => hasDeepDiveFor(c)).length;
              return deepCount ? (
                <div className="overview-legend">
                  <span className="overview-legend-chip"><Compass size={12} aria-hidden="true" /> Deep Dive</span>
                  <span className="overview-legend-count">{deepCount} of {filteredCards.length} stories flow deeper</span>
                </div>
              ) : null;
            })()}
            <div className="overview-grid">
              {filteredCards.map((card) => {
                const isViewed = viewedIds.includes(card.id);
                const hasDeep = hasDeepDiveFor(card);
                return (
                  <button
                    key={card.id}
                    type="button"
                    className={`overview-card${isViewed ? " viewed" : ""}${hasDeep ? " has-deepdive" : ""}`}
                    onClick={() => { setOverviewOpen(false); setBodyExpanded(false); visitCard(card.id); }}
                  >
                    <span className="overview-thumb" style={card.imageUrl ? { backgroundImage: `url(${card.imageUrl})` } : undefined} aria-hidden="true">
                      {hasDeep ? <span className="overview-dd" title="Deep Dive available"><Compass size={12} aria-hidden="true" /></span> : null}
                      {savedIds.includes(card.id) ? <span className="overview-saved"><Bookmark size={11} aria-hidden="true" /></span> : null}
                    </span>
                    <span className="overview-cat" style={{ "--chip-hue": `${tagHue(card.category)}` } as React.CSSProperties}>{card.category}</span>
                    <strong className="overview-headline">{tidyText(card.headline)}</strong>
                    <small className="overview-meta">
                      {cardMeta(card)}
                      {hasDeep ? <span className="overview-meta-dd"><Compass size={10} aria-hidden="true" /> Deep Dive</span> : null}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* THE SECOND LAYER. Every member surface refuses to render without an account, so a
          door that forgets to ask (or a state route nobody thought of) cannot leave one open.
          The check is `member`, which is false while auth is still resolving. */}
      {member && deepDiveOpen && deepDiveFor(activeCard) ? (
        <DeepDive
          title={activeCard.headline}
          category={activeCard.category}
          deepDive={deepDiveFor(activeCard)!}
          sources={sourceCount(activeCard)}
          wholePicture={activeWholePicture}
          // The card front handed its chapters to the dive (2026-09-13): why it matters, the
          // story with its quote, the tags, the feedback line.
          whyItMatters={activeCard.whyItMatters}
          story={deepDiveFor(activeCard)?.deeperRead?.paragraphs?.length
            ? deepDiveFor(activeCard)!.deeperRead!.paragraphs
            : bodyParagraphs(activeCard.body)}
          storyQuote={deepDiveFor(activeCard)?.deeperRead?.quote ?? null}
          tags={activeCard.tags.filter((tag) => tag.trim().toLowerCase() !== "good news")}
          onFeedback={activeCard.contentType !== "brief" && activeCard.contentType !== "quiz"
            ? () => openFeedback({ kind: "card", cardId: activeCard.id })
            : undefined}
          onClose={closeDeepDive}
          onAsk={(q) => askFromDeepDive(q)}
          onShare={() => void shareDeepDive(activeCard)}
        />
      ) : null}

      {goodNewsOpen ? (
        <section className="goodnews-overlay goodnews-sunrise" role="dialog" aria-modal="true" aria-label="Good News" onClick={() => setGoodNewsOpen(false)}>
          <div className="goodnews-modal" onClick={(e) => e.stopPropagation()}>
            <header className="goodnews-head">
              <div className="goodnews-head-copy">
                <span className="goodnews-eyebrow"><Sun size={13} aria-hidden="true" /> The bright side</span>
                <h2 className="goodnews-title">Good News</h2>
                <span className="goodnews-sub">{goodNewsLabel}</span>
              </div>
              <button type="button" className="brief-close" onClick={() => setGoodNewsOpen(false)} aria-label="Close Good News"><X size={18} /></button>
            </header>
            {goodNewsCards.length ? (
              <div className="goodnews-grid">
                {goodNewsCards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    className="goodnews-card"
                    onClick={() => openGoodNewsCard(card)}
                  >
                    <span className="goodnews-thumb" style={card.imageUrl ? { backgroundImage: `url(${card.imageUrl})` } : undefined} aria-hidden="true">
                      <span className="goodnews-sunbadge"><Sun size={11} aria-hidden="true" /></span>
                    </span>
                    <strong className="goodnews-card-headline">{tidyText(card.headline)}</strong>
                    <small className="goodnews-card-meta">{cardMeta(card)}</small>
                  </button>
                ))}
              </div>
            ) : (
              <div className="goodnews-empty">
                <span className="goodnews-empty-sun" aria-hidden="true"><Sun size={26} /></span>
                <strong>More good news on the way</strong>
                <p>This is where the day's genuinely uplifting stories land, rescues, recoveries, breakthroughs, and quiet wins. Check back as today's stories come in.</p>
              </div>
            )}
          </div>
        </section>
      ) : null}

      {member && briefOpen && briefs[briefIndex] ? (
        <section className="brief-overlay" role="dialog" aria-modal="true" aria-label="The Brief" onClick={() => { stopSpeech(); setBriefOpen(false); }}>
          <div className="brief-modal" onClick={(e) => e.stopPropagation()}>
            {/* The day and its prev/next stepper live once, in the header + the "Pick a day"
                bar below. A separate cover strip on top just repeated both, so it's gone. */}
            <header className="brief-modal-head">
              <div className="brief-modal-title">
                <span className="brief-eyebrow">✦ Daily ritual</span>
                <h2 className="brief-ritual-title">The Brief</h2>
                <span className="brief-ritual-date">{briefDayLabel(briefs[briefIndex])}</span>
              </div>
              <button type="button" className="brief-close" onClick={() => { stopSpeech(); setBriefOpen(false); }} aria-label="Close the brief"><X size={18} /></button>
            </header>
            {briefs.length > 1 ? (
              <div className="brief-daybar">
                <span className="brief-daybar-label">Pick a day</span>
                <div className="brief-daybar-controls">
                  <button type="button" className="brief-day-nav" disabled={briefIndex >= briefs.length - 1} onClick={() => setBriefIndex((i) => Math.min(briefs.length - 1, i + 1))} aria-label="Previous day"><ChevronLeft size={16} /></button>
                  <select className="brief-day-select" aria-label="Pick a day" value={briefIndex} onChange={(e) => setBriefIndex(Number(e.target.value))}>
                    {briefs.map((b, i) => <option key={b.id} value={i}>{briefDayLabel(b)}</option>)}
                  </select>
                  <button type="button" className="brief-day-nav" disabled={briefIndex <= 0} onClick={() => setBriefIndex((i) => Math.max(0, i - 1))} aria-label="Next day"><ChevronRight size={16} /></button>
                </div>
              </div>
            ) : null}
            {briefWeather ? (
              <div className="brief-weather">
                <span className="brief-weather-emoji" aria-hidden="true">{briefWeather.emoji}</span>
                <div className="brief-weather-text">
                  <strong>{briefWeather.tMax}{briefWeather.unit}<span className="brief-weather-low"> / {briefWeather.tMin}{briefWeather.unit}</span></strong>
                  <span>{briefWeather.label}{briefWeather.city ? ` · ${briefWeather.city}` : ""}</span>
                </div>
                <span className="brief-weather-tag">Your day</span>
              </div>
            ) : null}
            <p className="brief-lead">{briefs[briefIndex].essence}</p>
            <ol className="brief-points">
              {briefs[briefIndex].body.split("\n").map((l) => l.replace(/^[•\-•\s]+/, "").trim()).filter(Boolean).map((line, i) => (
                <li key={i}><span className="brief-num">{i + 1}</span><span>{line}</span></li>
              ))}
            </ol>
            <div className="brief-throughline">
              <span>The through-line</span>
              <p>{briefs[briefIndex].whyItMatters}</p>
            </div>
            <div className="brief-actions">
              <button type="button" className={`brief-listen-btn${speechState === "speaking" ? " playing" : ""}`} onClick={() => toggleBriefAudio(briefs[briefIndex])} aria-label={speechState === "speaking" ? "Pause the Brief" : "Play the Brief"}>
                {speechState === "speaking" ? <Pause size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
                <span>{speechState === "speaking" ? "Pause" : "Play the Brief"}</span>
              </button>
              <button type="button" className="brief-share-btn" onClick={() => void shareBrief(briefs[briefIndex])}>
                <Share2 size={15} aria-hidden="true" />
                <span>{briefCopied ? "Copied" : "Share the Brief"}</span>
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {trustOpen ? (
        <section className="trust-overlay" role="dialog" aria-modal="true" aria-label="Sourced and gate-checked" onClick={() => setTrustOpen(false)}>
          <div className="trust-pop" onClick={(e) => e.stopPropagation()}>
            <header className="trust-pop-head">
              <div>
                <span className="trust-pop-eyebrow"><ShieldCheck size={13} aria-hidden="true" /> Our promise</span>
                <h2>Sourced and gate-checked</h2>
              </div>
              <button type="button" className="brief-close" onClick={() => setTrustOpen(false)} aria-label="Close"><X size={18} /></button>
            </header>
            <p className="trust-pop-body">
              Every InSnip card is built from real, cited sources and passes one strict quality check
              before it publishes. No AI slop, no unsourced claims, no filler. Pure signal, no noise.
            </p>
            <ul className="trust-pop-list">
              <li><Check size={14} aria-hidden="true" /> Real sources, verified</li>
              <li><Check size={14} aria-hidden="true" /> One strict quality gate</li>
              <li><Check size={14} aria-hidden="true" /> No AI slop or filler</li>
            </ul>
            {activeCard ? (
              <button type="button" className="trust-pop-more" onClick={() => { setTrustOpen(false); setSourcesOpen(true); }}>
                See how this snip was built →
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* A signed-out reader can still reach ONE thing in here: a single card opened from
          "More like this" in the Verify drawer, which is a story, not the library. The search
          field and its results live in the else branch, which only a member ever reaches. */}
      {searchOpen && (member || searchDetail) ? (
        <section className="search-overlay" role="dialog" aria-modal="true" aria-label="Search snippets" onClick={closeSearch}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            {searchDetail ? (
              <div className="search-detail">
                {/* "Back to results" is a promise only a member can be given: a guest who
                    arrived here from a related story has no results behind this card. */}
                <button type="button" className="search-back" onClick={() => (member ? setSearchDetail(null) : closeSearch())}>
                  <ChevronLeft size={16} aria-hidden="true" /> {member ? "Back to results" : "Close"}
                </button>
                {searchDetail.imageUrl ? (
                  <div className="search-detail-img">
                    <img src={resolveImageUrl(searchDetail.imageUrl)} alt={searchDetail.imageAlt || searchDetail.headline} loading="lazy" />
                  </div>
                ) : null}
                <span className="search-detail-cat" style={{ "--chip-hue": `${tagHue(searchDetail.category)}` } as React.CSSProperties}>{searchDetail.category}</span>
                <h2>{searchDetail.headline}</h2>
                <p className="search-detail-essence">{searchDetail.essence}</p>
                <p className="search-detail-text">{searchDetail.body}</p>
                {searchDetail.whyItMatters ? (
                  <div className="search-detail-why"><span>Why it matters</span><p>{searchDetail.whyItMatters}</p></div>
                ) : null}
                {/* Full card actions for the searched card. These act ONLY on searchDetail (via the
                    searchDetail ?? activeCard target), so they never touch the user's current deck.
                    Latest/source tags were removed: the Intelligence button covers the source trail. */}
                <div className="command-bar search-detail-actions" data-tour="actions">
                  <button
                    type="button"
                    className="sources-btn"
                    onClick={() => setSourcesOpen(true)}
                    title="Intelligence: the source trail and related stories"
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    <span>Intelligence</span>
                  </button>
                  <button
                    type="button"
                    className="share-btn"
                    onClick={() => void shareCardAsImage(searchDetail)}
                    disabled={imageShareState === "busy"}
                    aria-label="Share this card as an image"
                  >
                    {imageShareState === "busy" ? <ImageDown size={15} aria-hidden="true" /> : <Share2 size={15} aria-hidden="true" />}
                    <span>{imageShareState === "busy" ? "Creating…" : imageShareState === "done" ? "Shared" : "Share"}</span>
                  </button>
                  {sessionApiEnabled() ? (
                    <button
                      type="button"
                      className="ask-btn"
                      onClick={openAsk}
                      title="Ask a question about this story"
                      aria-label="Ask about this story"
                    >
                      <MessageCircle size={18} aria-hidden="true" />
                      <span>Ask</span>
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <div className="search-modal-head">
                  <Search size={18} aria-hidden="true" />
                  <input
                    type="text"
                    autoFocus={!tourOpen}
                    aria-label="Search snippets"
                    placeholder="Search every snippet, past or present…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button type="button" className="search-modal-close" onClick={closeSearch} aria-label="Close search"><X size={18} /></button>
                </div>
                <div className="search-results-list">
                  {searchQuery.trim().length < 2 ? (
                    <div className="search-empty">
                      <Search size={26} aria-hidden="true" />
                      <p>Find any snippet by topic, person or idea.</p>
                      <span>Try “climate”, “AI”, or a country. It searches the whole library, not just your deck.</span>
                    </div>
                  ) : searching && !searchResults ? (
                    <div className="search-empty"><p>Searching…</p></div>
                  ) : searchResults && searchResults.length ? (
                    <>
                      {searchWeak ? <div className="search-weak-note">Closest matches</div> : null}
                      {searchResults.slice(0, 10).map((card) => (
                        <button key={card.id} type="button" className="search-result" onClick={() => setSearchDetail(card)}>
                          {card.imageUrl ? (
                            <img className="search-result-img" src={resolveImageUrl(card.imageUrl)} alt="" loading="lazy" />
                          ) : (
                            <span className="search-result-img placeholder" aria-hidden="true" />
                          )}
                          <span className="search-result-text">
                            <span className="search-result-cat" style={{ "--chip-hue": `${tagHue(card.category)}` } as React.CSSProperties}>{card.category}</span>
                            <strong>{card.headline}</strong>
                            <small>{cardMeta(card)}</small>
                          </span>
                          <ChevronRight size={16} aria-hidden="true" className="search-result-arrow" />
                        </button>
                      ))}
                    </>
                  ) : (
                    <div className="search-empty"><p>No snippets matched “{searchQuery.trim()}”.</p></div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      ) : null}

      {/* The keyboard hint is desktop advice ("← → to swipe") and a phone has no arrow keys,
          so a phone browser never gets it. The class lets phoneread.css say that once. */}
      {showHint && !isNativeApp ? (
        <div className="toast toast-kbd-hint" role="status">
          <Keyboard size={14} aria-hidden="true" />
          <span>Tip: <kbd>←</kbd> <kbd>→</kbd> to swipe, <kbd>?</kbd> for all shortcuts.</span>
        </div>
      ) : null}

      {milestone ? (
        <div className="toast toast-celebrate" role="status">
          <span>{milestone}</span>
        </div>
      ) : null}

      {insightOfDay && !isNativeApp && !tourOpen && !onboardingOpen ? (
        <section
          className="iotd-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Insight of the day"
          onClick={dismissInsightOfDay}
        >
          <div className="iotd-card" onClick={(e) => e.stopPropagation()}>
            <div className="iotd-glow" aria-hidden="true" />
            <span className="iotd-eyebrow">✦ Insight of the day · {insightOfDay.eyebrow}</span>
            <p className="iotd-text">{insightOfDay.text}</p>
            <p className="iotd-detail">{insightOfDay.detail}</p>
            <button type="button" className="iotd-begin" onClick={dismissInsightOfDay}>
              Begin reading →
            </button>
          </div>
        </section>
      ) : null}

      {reward ? (
        <section className="insight-modal" role="dialog" aria-modal="true" aria-label="Reading reward" onClick={() => setReward(null)}>
          <div className={`insight-card reward-card${reward.big ? " reward-big" : ""}`} onClick={(e) => e.stopPropagation()}>
            {reward.big ? <div className="reward-rays" aria-hidden="true" /> : null}
            <div className="insight-burst" aria-hidden="true">{reward.emoji}</div>
            <span>{reward.big ? "Big reward" : "Depth unlocked"}</span>
            <h2>{reward.title}</h2>
            <p>{reward.body}</p>
            <div className="insight-actions">
              <button type="button" onClick={() => setReward(null)}>Keep reading</button>
            </div>
          </div>
        </section>
      ) : null}

      {referralWelcome ? (
        <div className="referral-welcome" role="dialog" aria-label="Welcome to InSnip">
          <div className="rw-copy">
            <strong><Sparkles size={14} aria-hidden="true" /> A friend shared this with you</strong>
            <span>InSnip is the news you can finish, a clean daily drop in a swipe.</span>
          </div>
          <div className="rw-actions">
            <button
              type="button"
              className="rw-cta"
              onClick={() => { setReferralWelcome(false); setOnboardingOpen(true); }}
            >
              Start your daily drop
            </button>
            <button type="button" className="rw-dismiss" onClick={() => setReferralWelcome(false)}>
              Maybe later
            </button>
          </div>
        </div>
      ) : null}

      {curtain ? (
        <div className="edition-curtain" aria-hidden="true">
          <div className="ec-inner">
            <span className="ec-mast"><span className="ec-in">In</span><span className="ec-snip">Snip</span></span>
            <span className="ec-rule" />
            <span className="ec-date">
              {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="ec-line">Today's round · about four minutes</span>
          </div>
        </div>
      ) : null}

      {(() => {
        // "Get the native app" banner: iOS goes to the App Store, Android to Google Play, and
        // desktop is sent to /get, which shows a QR to scan. Both stores are live. Web-only,
        // shown after a little reading, dismissible.
        //
        // AND IT YIELDS. The audit caught it photobombing three surfaces at once: covering
        // the Ask chips mid-read, overlapping the keyboard-shortcuts card, and crashing the
        // round-end ceremony. A promo that interrupts the product's best moments sells the
        // opposite of what it thinks it is selling, so it stands down whenever any overlay
        // or the ceremony is up, and returns when the reader is simply reading.
        // `authWall` belongs on this list for the same reason everything else is on it: the
        // sign-in invitation now lands at three stories and the banner at three cards, so
        // without it the reader's one decision moment gets an install ad underneath it.
        const overlayUp =
          showInsight || shortcutsOpen || sourcesOpen || askOpen || briefOpen || digestOpen ||
          deepDiveOpen || gameOpen || overviewOpen || goodNewsOpen || insightsOpen || searchOpen ||
          recallOpen || libraryOpen || proOpen || trustOpen || deskOpen || quizOpen || authWall;
        // Never beside the interlude, and three cards later when the interlude has had its
        // turn: two asks on one screen read as nagging, not as an offer.
        // On phone web the door's standing strip is the one ask on screen; this banner would be
        // a second, on top of Listen and Share.
        const appPromoVisible = !isNativeApp && !isStandalone() && !phoneWeb && !appPromoGone && !interlude && viewedIds.length >= (interludeShown.current ? 6 : 3) && !overlayUp;
        if (!appPromoVisible) return null;
        const wp = webPlatform();
        const closeAppPromo = () => { dismissAppPromo(); setAppPromoGone(true); };
        return (
          <div className="install-banner app-get-banner" role="dialog" aria-label="Get the InSnip app">
            <AppIcon size={40} className="app-get-mark" />
            <div className="install-banner-text">
              {wp === "android" ? (
                <>
                  <strong>Get the InSnip app</strong>
                  <span>Free on Google Play. The whole feed, in your pocket.</span>
                </>
              ) : wp === "ios" ? (
                <>
                  <strong>Get the InSnip app for iPhone</strong>
                  <span>Free on the App Store. The whole feed, in your pocket.</span>
                </>
              ) : (
                <>
                  {/* NO "scan the code" HERE. The QR lives in the .app-plate rail card, which
                      is display:none below 1400px landscape, so on a 1366 laptop this line
                      used to point at a code that was not on the screen. The copy has to be
                      true at every width the banner can appear at, and the CTA below is the
                      thing that actually carries the reader to /get. */}
                  <strong>Get InSnip on your phone</strong>
                  <span>Free on the App Store and Google Play. Open insnip.com/get to send it across.</span>
                </>
              )}
            </div>
            {wp === "android" ? (
              <a className="install-banner-cta" href={PLAY_URL} target="_blank" rel="noopener noreferrer" onClick={() => logAppGetClick("android")}>Get it</a>
            ) : wp === "ios" ? (
              <a className="install-banner-cta" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => logAppGetClick("ios")}>Get it</a>
            ) : wp === "desktop" ? (
              <a className="install-banner-cta" href={GET_PATH} target="_blank" rel="noopener noreferrer" onClick={() => logAppGetClick("desktop")}>Get the app</a>
            ) : null}
            <button type="button" className="install-banner-dismiss" onClick={closeAppPromo} aria-label="Dismiss app prompt">
              <X size={14} />
            </button>
          </div>
        );
      })()}

      {!isStandalone() && !installDismissed && installReady && viewedIds.length >= 4 && (isNativeApp || isStandalone() || appPromoGone) ? (
        <div className="install-banner" role="dialog" aria-label="Install InSnip">
          <div className="install-banner-text">
            <strong>Add InSnip to your Home Screen</strong>
            <span>{isIOS() ? "Tap Share, then “Add to Home Screen”." : "One-tap, full-screen, offline-ready."}</span>
          </div>
          {isIOS() ? null : (
            <button type="button" className="install-banner-cta" disabled={installing} onClick={handleInstall}>
              {installing ? "…" : "Install"}
            </button>
          )}
          <button type="button" className="install-banner-dismiss" onClick={dismissInstall} aria-label="Dismiss install prompt">
            <X size={14} />
          </button>
        </div>
      ) : null}

      {topicsToast ? (
        <div className="toast" role="status">
          <SlidersHorizontal size={14} aria-hidden="true" />
          <span>{topicsToast}</span>
        </div>
      ) : null}


      {focusMode ? (
        <button type="button" className="focus-exit" onClick={() => setFocusMode(false)} aria-label="Exit focus mode">
          <Focus size={14} /> Focus mode (press Esc to exit)
        </button>
      ) : null}

      {sourcesOpen ? (
        <div className={`drawer-scrim${searchDetail ? " over-search" : ""}`} onClick={() => setSourcesOpen(false)} role="dialog" aria-modal="true" aria-label="Intelligence">
          <div className="sources-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-handle" aria-hidden="true" />
            <header>
              <div><span>Verify</span><h2>{wholePicture ? "The whole picture" : "Who reports this"}</h2></div>
              <div className="drawer-head-actions">
                {intelCard ? <ProofShareButton card={intelCard} /> : null}
                <button type="button" className="icon-button ghost" onClick={() => setSourcesOpen(false)} aria-label="Close sources"><X size={18} /></button>
              </div>
            </header>
            {fullPublishedLabel(intelCard) ? (
              <p className="drawer-published">
                <Clock size={12} aria-hidden="true" />
                {cardTimeLabel(intelCard) ? <span className="dp-fresh">{cardTimeLabel(intelCard)}</span> : null}
                <span className="dp-date">Published {fullPublishedLabel(intelCard)}</span>
              </p>
            ) : null}
            {intelCheckedLine ? (
              <p className="drawer-published">
                <Check size={12} aria-hidden="true" />
                <span className="dp-date">{intelCheckedLine}</span>
              </p>
            ) : null}
            {/* Evidence first: the outlets, then one sentence a first-time reader can use. The
                formula sits behind a fold below. */}
            <p className="verify-lede">
              {intelSourcing.independentDomains === 1
                ? "One outlet reports this so far; a single-outlet story is marked developing until others corroborate."
                : `${intelSourcing.independentDomains} outlets report this; a single-outlet story would be marked developing.`}
            </p>
            <ul className="source-list">
              {intelSources.map((source) => {
                const tier = tierForDomain(domainOf(source.url));
                // The receipt's own tier fills the gap when the local domain map has nothing:
                // known tier ids reuse their label + colour, an unknown id shows as sent.
                const rawTier = tierHasChip(tier)
                  ? null
                  : intelReceipt?.sources.find((s) => s.domain.replace(/^www\./i, "").toLowerCase() === domainOf(source.url).toLowerCase())?.tier ?? null;
                const rawTierId = rawTier?.toLowerCase() ?? "";
                // Only whitelisted tier ids render; an unknown id from the wire shows nothing
                // rather than unvetted text in an unstyled chip.
                const receiptTier =
                  rawTier && ["wire", "major", "regional", "specialist", "state"].includes(rawTierId)
                    ? { cls: rawTierId, label: tierLabel(rawTierId as SourceTier) }
                    : null;
                return (
                  <li key={source.url}>
                    <a href={httpHref(source.url) ?? undefined} target="_blank" rel="noreferrer" onClick={() => logEngagement("source_click", intelCard.id)}>
                      <img className="source-favicon-lg" src={faviconFor(source.url)} alt="" width={22} height={22} loading="lazy" />
                      <div className="source-name">
                        <strong>{source.name}</strong>
                        {("title" in source && source.title) ? (
                          <span className="source-angle">"{source.title}"</span>
                        ) : null}
                        <small>{domainOf(source.url)}</small>
                      </div>
                      <div className="source-meta">
                        {tierHasChip(tier) ? (
                          <span className={`source-tier tier-${tier}`}>{tierLabel(tier)}</span>
                        ) : receiptTier ? (
                          <span className={`source-tier tier-${receiptTier.cls}`}>{receiptTier.label}</span>
                        ) : null}
                        {source.primary ? <span className="primary-pill">Primary</span> : null}
                        <ExternalLink size={14} aria-hidden="true" />
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
            {wholePicture ? (
              <section className="whole-picture" aria-label="The whole picture">
                {wholePicture.settled.length ? (
                  <div className="wp-block">
                    <span className="wp-label wp-settled">What's settled</span>
                    <ul>{wholePicture.settled.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                ) : null}
                {wholePicture.disputed.length ? (
                  <div className="wp-block">
                    <span className="wp-label wp-disputed">What's disputed</span>
                    <ul>{wholePicture.disputed.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                ) : null}
                {wholePicture.perspectives.length ? (
                  <div className="wp-block">
                    <span className="wp-label wp-views-label"><Scale size={12} aria-hidden="true" /> The perspectives</span>
                    <div className="wp-views">
                      {wholePicture.perspectives.map((p, i) => (
                        <div key={i} className="wp-view">
                          <strong>{p.label}</strong>
                          <span>{p.point}</span>
                          {p.sourceName ? <em>{p.sourceName}</em> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}
            <details className="verify-how">
            <summary>How this was checked and scored</summary>
            <span className="intel-section-label">{wholePicture ? "How solid is this" : "Synthesis"}</span>
            <div className="intel-strip" role="group" aria-label="Synthesis signals">
              <div className="intel-stat">
                <strong>{intelSourcing.independentDomains}</strong>
                <span>independent {intelSourcing.independentDomains === 1 ? "source" : "sources"}</span>
              </div>
              <div className="intel-stat">
                <strong>{intelOriginalPct}%</strong>
                <span>original synthesis</span>
              </div>
            </div>
            {intelSourcing.singleSource ? (
              <p className="intel-warning">Rests on a single source, so treat it as developing until others corroborate.</p>
            ) : null}
            {/* Claims check: what the pipeline's fact pass found, said plainly. A corrected card
                says so (the flags were fixed before publish); an amber block means the flags
                shipped and the reader deserves to see them. Absent entirely on unchecked cards. */}
            {intelReceipt?.claims?.checked ? (
              intelReceipt.claims.resolvedByEdit ? (
                <p className="claims-check clean">Claims check: corrected before publish</p>
              ) : claimStatus(intelReceipt) === "flagged" ? (
                <div className="claims-check flagged" role="note" aria-label="Flagged claims">
                  <span className="claims-check-label">Claims check: {intelReceipt.claims.flagged.length} flagged</span>
                  {intelReceipt.claims.flagged.slice(0, 3).map((f, i) => (
                    <p key={i} className="claims-check-item">
                      <strong>{f.claim}</strong>
                      <span>{f.why}</span>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="claims-check clean">Claims check: clean against the source</p>
              )
            ) : null}
            {/* The story moved: the cross-day chain, oldest first, this card marked. Only a
                story that genuinely appeared more than once earns the timeline. */}
            {storyChain.length > 1 ? (
              <div className="story-moved">
                <span className="sm-label">This story has moved</span>
                <ol>
                  {storyChain.map((st) => {
                    const here = st.id === intelCard?.id;
                    // Concrete dates, matching the drawer's idiom (the card face keeps the
                    // calm non-temporal label; the drawer is where precision lives).
                    const when = st.at
                      ? new Date(st.at * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short" })
                      : "";
                    const meta = [sourceDisplayName(st.source), when].filter(Boolean).join(", ");
                    return (
                      <li key={st.id} className={here ? "sm-now" : undefined}>
                        {here ? (
                          <span className="sm-h">{st.headline}</span>
                        ) : (
                          <button type="button" className="sm-link" onClick={() => { setSourcesOpen(false); visitCard(st.id); }}>
                            <span className="sm-h">{st.headline}</span>
                          </button>
                        )}
                        <span className="sm-m">{meta}{here ? `${meta ? ", " : ""}this update` : ""}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : null}
            {/* The Signal dial sits BELOW the source list now: evidence first, heuristic second.
                The receipt is the record of what backs the story; the score is our reading of it. */}
            {intelCard && intelCard.contentType !== "brief" && intelCard.contentType !== "quiz" ? (() => {
              const sig = explainSignalScore(intelCard);
              return (
                <section className="signal-explain" aria-label={`Signal score ${sig.score.toFixed(1)} of 10`}>
                  <div className="signal-explain-head">
                    <div className="signal-explain-dial">
                      <b>{sig.score.toFixed(1)}</b>
                      <small>/ 10</small>
                    </div>
                    <div className="signal-explain-lede">
                      <span className="signal-explain-band">{sig.band}</span>
                      <p>How much this story matters, scored from its magnitude, corroboration and depth. Not an opinion, a transparent formula.</p>
                    </div>
                  </div>
                  <ul className="signal-explain-factors">
                    <li className="sef-base">
                      <span className="sef-label">Baseline</span>
                      <span className="sef-detail">Every story starts here.</span>
                      <span className="sef-points">{sig.base.toFixed(1)}</span>
                    </li>
                    {sig.factors.map((f, i) => (
                      <li key={i} className={f.points < 0 ? "sef-neg" : "sef-pos"}>
                        <span className="sef-label">{f.label}</span>
                        <span className="sef-detail">{f.detail}</span>
                        <span className="sef-points">{f.points >= 0 ? "+" : ""}{f.points.toFixed(2)}</span>
                      </li>
                    ))}
                    <li className="sef-total">
                      <span className="sef-label">Signal score</span>
                      <span className="sef-detail">Clamped to a calm 3.6 to 9.6 range.</span>
                      <span className="sef-points">{sig.score.toFixed(1)}</span>
                    </li>
                  </ul>
                </section>
              );
            })() : null}
            </details>
            {relatedCards.length ? (
              <div className="related-section">
                <span className="related-label">More like this</span>
                <div className="related-list">
                  {relatedCards.map((rc) => (
                    <button
                      key={rc.id}
                      type="button"
                      className="related-item"
                      onClick={() => { setSourcesOpen(false); setSearchDetail(rc); setSearchOpen(true); }}
                    >
                      {rc.imageUrl ? (
                        <img className="related-thumb" src={resolveImageUrl(rc.imageUrl)} alt="" loading="lazy" />
                      ) : null}
                      <span className="related-text">
                        <small>{rc.category}</small>
                        <strong>{tidyText(rc.headline)}</strong>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {!searchDetail && activeCard?.category ? (
              <div className="feed-tune">
                <span className="feed-tune-label">Tune your feed · {activeCard.category}</span>
                <div className="feed-tune-actions">
                  <button
                    type="button"
                    className={topicState(topicSignals, activeCard.category) === "more" ? "tune-btn active" : "tune-btn"}
                    onClick={() => tuneTopic(activeCard.category, 1, activeCard.id)}
                    aria-pressed={topicState(topicSignals, activeCard.category) === "more"}
                  >
                    <ThumbsUp size={13} aria-hidden="true" /> More like this
                  </button>
                  <button
                    type="button"
                    className={topicState(topicSignals, activeCard.category) === "less" ? "tune-btn active less" : "tune-btn"}
                    onClick={() => tuneTopic(activeCard.category, -1, activeCard.id)}
                    aria-pressed={topicState(topicSignals, activeCard.category) === "less"}
                  >
                    <ThumbsDown size={13} aria-hidden="true" /> Less like this
                  </button>
                  <button
                    type="button"
                    className="tune-btn hide"
                    onClick={() => hideCard(activeCard.id)}
                    title="Hide this card and stop showing similar ones"
                  >
                    <EyeOff size={13} aria-hidden="true" /> Not interested
                  </button>
                </div>
              </div>
            ) : null}
            <footer>
              <p>Synthesised from {intelIndependentDomains} independent {intelIndependentDomains === 1 ? "domain" : "domains"} · {intelOriginalPct}% original · {intelVerbatimPct}% verbatim overlap. We show the full source trail.</p>
              <p className="drawer-legal"><a href="/sources" rel="nofollow">Sources</a> · <a href="/contact" rel="nofollow">Contact</a> · <a href="/privacy" rel="nofollow">Privacy &amp; content policy</a></p>
            </footer>
          </div>
        </div>
      ) : null}

      {/* Feedback popup: per-card (kind=card) or product-wide (kind=general). Category chips plus
          an optional note, posted to the same worker endpoint and the same admin view the app has
          been filing into, so the two surfaces land in one inbox. */}
      {feedbackFor ? (() => {
        const isCard = feedbackFor.kind === "card";
        const cats = isCard ? FEEDBACK_CATEGORIES_CARD : FEEDBACK_CATEGORIES_GENERAL;
        const words = fbMessage.trim() ? fbMessage.trim().split(/\s+/).length : 0;
        const overLimit = words > FEEDBACK_WORD_LIMIT;
        const canSend = fbStatus !== "sending" && !overLimit && (fbCategory !== "" || fbMessage.trim() !== "");
        const cardTitle = isCard ? (cards.find((c) => c.id === feedbackFor.cardId)?.headline ?? "") : "";
        return (
          <div className="drawer-scrim center feedback-scrim" onClick={() => setFeedbackFor(null)} role="dialog" aria-modal="true" aria-label="Send feedback">
            <div className="shortcuts-card feedback-card" onClick={(e) => e.stopPropagation()}>
              <header>
                <h2>{isCard ? "Feedback on this card" : "Send feedback"}</h2>
                <button type="button" className="icon-button ghost" onClick={() => setFeedbackFor(null)} aria-label="Close feedback"><X size={18} /></button>
              </header>
              {fbStatus === "sent" ? (
                <div className="feedback-done">
                  <span className="feedback-done-check"><Check size={20} aria-hidden="true" /></span>
                  <strong>Thank you.</strong>
                  <span>Your feedback goes straight to the team.</span>
                </div>
              ) : (
                <>
                  {isCard && cardTitle ? (
                    <p className="feedback-context">On <em>{tidyText(cardTitle)}</em></p>
                  ) : (
                    <p className="feedback-context">Tell us what's working and what isn't. We read every note.</p>
                  )}
                  <div className="feedback-cats" role="group" aria-label="What is this about">
                    {cats.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`feedback-cat${fbCategory === c ? " active" : ""}`}
                        aria-pressed={fbCategory === c}
                        onClick={() => setFbCategory((cur) => (cur === c ? "" : c))}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="feedback-text"
                    placeholder={isCard ? "Add detail (optional), up to 100 words" : "Tell us more (optional), up to 100 words"}
                    value={fbMessage}
                    maxLength={800}
                    rows={4}
                    onChange={(e) => setFbMessage(e.target.value)}
                  />
                  <div className="feedback-foot">
                    <span className={`feedback-count${overLimit ? " over" : ""}`}>{words}/{FEEDBACK_WORD_LIMIT} words</span>
                    <button type="button" className="feedback-send" disabled={!canSend} onClick={() => void submitFeedback()}>
                      {fbStatus === "sending" ? "Sending..." : "Send feedback"}
                    </button>
                  </div>
                  {fbStatus === "error" ? <p className="feedback-error">Couldn't send just now. Please try again.</p> : null}
                </>
              )}
            </div>
          </div>
        );
      })() : null}

      {shortcutsOpen ? (
        <div className="drawer-scrim center" onClick={() => setShortcutsOpen(false)} role="dialog" aria-modal="true" aria-label="Keyboard shortcuts">
          <div className="shortcuts-card" onClick={(e) => e.stopPropagation()}>
            <header><h2>Keyboard shortcuts</h2><button type="button" className="icon-button ghost" onClick={() => setShortcutsOpen(false)} aria-label="Close shortcuts"><X size={18} /></button></header>
            <dl>
              <div><dt><kbd>←</kbd> <kbd>→</kbd></dt><dd>Previous / next card</dd></div>
              <div><dt><kbd>J</kbd> <kbd>K</kbd></dt><dd>Next / previous card</dd></div>
              <div><dt><kbd>S</kbd></dt><dd>Save current card</dd></div>
              <div><dt><kbd>D</kbd></dt><dd>Open the deep dive</dd></div>
              <div><dt><kbd>G</kbd></dt><dd>Play Signal or Noise</dd></div>
              <div><dt><kbd>E</kbd></dt><dd>Open today's edition</dd></div>
              <div><dt><kbd>R</kbd></dt><dd>Open sources</dd></div>
              <div><dt><kbd>L</kbd></dt><dd>Listen to card</dd></div>
              <div><dt><kbd>F</kbd></dt><dd>Focus mode</dd></div>
              <div><dt><kbd>T</kbd></dt><dd>Toggle theme</dd></div>
              <div><dt><kbd>/</kbd></dt><dd>Focus search</dd></div>
              <div><dt><kbd>?</kbd></dt><dd>Toggle this list</dd></div>
              <div><dt><kbd>Esc</kbd></dt><dd>Close any overlay</dd></div>
            </dl>
          </div>
        </div>
      ) : null}

      {topicsOpen ? (
        <div className="drawer-scrim center" onClick={() => setTopicsOpen(false)} role="dialog" aria-modal="true" aria-label="Make it yours">
          <div className="topics-card" onClick={(e) => e.stopPropagation()}>
            <header>
              <div>
                <span>Make it yours</span>
                <h2>Shape your feed</h2>
              </div>
              <button type="button" className="icon-button ghost" onClick={() => setTopicsOpen(false)} aria-label="Close topic preferences"><X size={18} /></button>
            </header>
            <p className="topics-intro">Follow topics to see more of them, or mute the ones you'd rather skip. We always keep a mix so your feed never becomes one-note.</p>
            <ul className="topics-list">
              {ANCHOR_CATEGORIES.map(({ id, emoji }) => {
                const pref = topicPrefs[id];
                return (
                  <li key={id} className={`topics-row${pref ? ` is-${pref}` : ""}`}>
                    <span className="topics-name"><span className="topics-emoji" aria-hidden="true">{emoji}</span>{id}</span>
                    <span className="topics-toggles">
                      <button
                        type="button"
                        className={`topic-toggle follow${pref === "follow" ? " active" : ""}`}
                        onClick={() => toggleTopicPref(id, "follow")}
                        aria-pressed={pref === "follow"}
                        aria-label={`Follow ${id}`}
                        title={`Follow ${id}`}
                      >
                        <Star size={14} aria-hidden="true" /> Follow
                      </button>
                      <button
                        type="button"
                        className={`topic-toggle mute${pref === "mute" ? " active" : ""}`}
                        onClick={() => toggleTopicPref(id, "mute")}
                        aria-pressed={pref === "mute"}
                        aria-label={`Mute ${id}`}
                        title={`Mute ${id}`}
                      >
                        <ThumbsDown size={14} aria-hidden="true" /> Mute
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="topics-actions">
              {Object.keys(topicPrefs).length ? (
                <button type="button" className="topics-clear" onClick={() => setTopicPrefs({})}>Clear all</button>
              ) : <span />}
              <button type="button" className="topics-save" onClick={applyTopicPrefs}>Save &amp; refresh</button>
            </div>
          </div>
        </div>
      ) : null}

      {subscribeOpen ? (
        <div className="drawer-scrim center" onClick={() => setSubscribeOpen(false)} role="dialog" aria-modal="true" aria-label="Subscribe">
          <div className="subscribe-modal" onClick={(e) => e.stopPropagation()}>
            <header>
              <div><span>Free · No spam</span><h2>Join InSnip</h2></div>
              <button type="button" className="icon-button ghost" onClick={() => setSubscribeOpen(false)} aria-label="Close subscribe"><X size={18} /></button>
            </header>
            {subscribeStatus === "saved" ? (
              <div className="subscribe-success">
                <Check size={28} aria-hidden="true" />
                <h3>Tomorrow's edition, 7am.</h3>
                <p>You're in. One calm email a day, unsubscribe any time from its footer.</p>
                <button type="button" onClick={() => setSubscribeOpen(false)}>Back to reading</button>
              </div>
            ) : (
              <form onSubmit={submitSubscribe}>
                <p>Three to seven snippets a day, source-backed, ready in a minute.</p>
                <label htmlFor="subscribe-email">Email address</label>
                <input id="subscribe-email" ref={emailInputRef} type="email" inputMode="email" autoComplete="email" required placeholder="you@example.com" value={emailDraft} onChange={(e) => { setEmailDraft(e.target.value); if (subscribeStatus === "invalid" || subscribeStatus === "error") setSubscribeStatus("idle"); }} aria-invalid={subscribeStatus === "invalid"} />
                {subscribeStatus === "invalid" ? <small className="form-error">Please enter a valid email.</small> : null}
                {subscribeStatus === "error" ? <small className="form-error">Couldn't save that just now. Try again in a moment.</small> : null}
                <button type="submit" disabled={subscribeStatus === "saving"}>{subscribeStatus === "saving" ? "Sending…" : "Subscribe"}</button>
                <small className="form-hint">Straight from us, no third party. Unsubscribe any time.</small>
              </form>
            )}
          </div>
        </div>
      ) : null}

      {mobileMenuOpen ? (
        <div className="drawer-scrim mobile-menu-scrim" onClick={() => setMobileMenuOpen(false)} role="dialog" aria-modal="true" aria-label="Mobile menu">
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <header>
              <div>
                <span>Today's drop</span>
                <h2>{dailyStats.stories} stories · {dailyStats.minutes}m · {dailyStats.sources} sources</h2>
              </div>
              <div className="mobile-menu-header-actions">
                <button
                  type="button"
                  className="icon-button ghost"
                  onClick={toggleTheme}
                  aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
                  title="Theme"
                >
                  {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                </button>
                <button
                  type="button"
                  className="icon-button ghost"
                  onClick={() => { setFocusMode((c) => !c); setMobileMenuOpen(false); }}
                  aria-label="Focus mode"
                  title="Focus mode"
                >
                  {focusMode ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
                <button
                  type="button"
                  className="icon-button ghost"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={18} />
                </button>
              </div>
            </header>

            <div className="sheet-profile">
              <span className={`sheet-avatar${streak.count > 0 ? " ring" : ""}`} aria-hidden="true">
                <span className="sheet-avatar-inner">
                  {account?.signedIn && (account.name || account.email)
                    ? (account.name || account.email || "").trim().charAt(0).toUpperCase()
                    : <Sparkles size={18} />}
                </span>
              </span>
              <div className="sheet-profile-meta">
                <strong>{account?.signedIn ? (account.name || account.email || "You") : "Your InSnip"}</strong>
                <span>{streak.count > 0 ? `${streak.count} day streak` : account?.signedIn ? "No streak yet" : "Not signed in"}{account?.cardsRead ? `, ${account.cardsRead} read` : ""}</span>
              </div>
            </div>

            <div className="freeze-panel">
              <span className="freeze-panel-head">
                <Snowflake size={14} aria-hidden="true" />
                <strong>{perks.freezeTokens}</strong> streak freeze{perks.freezeTokens === 1 ? "" : "s"}
              </span>
              <p>Freezes protect your streak if you miss a day. Earn them from streak milestones and from inviting friends.</p>
            </div>

            {/* The daily rituals (Brief, game, quiz, Good News, Recall, recap) live in the
                Insights tab now. This sheet is identity + settings only: no duplicates. */}

            {/* The mm-* classes below are hooks for phoneread.css, which hides several of these
                sections on a phone browser. They carry no styling of their own: naming the
                sections is the only way to prune them without nth-child selectors that break
                the moment a section is added or made conditional. */}
            {serverDrivenRef.current ? (
              <section className="mobile-menu-section mm-feed">
                <div className="section-label">Feed</div>
                <div className="feed-mode-toggle" role="group" aria-label="Feed ordering">
                  <button
                    type="button"
                    className={feedMode === "latest" ? "feed-mode-btn active" : "feed-mode-btn"}
                    aria-pressed={feedMode === "latest"}
                    onClick={() => switchFeedMode("latest")}
                  >
                    <Zap size={14} aria-hidden="true" />
                    <span>Latest</span>
                  </button>
                  <button
                    type="button"
                    className={feedMode === "trending" ? "feed-mode-btn active" : "feed-mode-btn"}
                    aria-pressed={feedMode === "trending"}
                    onClick={() => switchFeedMode("trending")}
                  >
                    <Flame size={14} aria-hidden="true" />
                    <span>Trending</span>
                  </button>
                </div>
              </section>
            ) : null}

            <section className="mobile-menu-section mm-explore">
              <label className="section-label">Explore</label>
              <button type="button" className="search-trigger" onClick={() => { setMobileMenuOpen(false); openSearch(); }}>
                <Search size={15} aria-hidden="true" />
                <span>Find any snippet…</span>
              </button>
              <button type="button" className="search-trigger library-trigger" onClick={() => { setMobileMenuOpen(false); setLibraryOpen(true); }}>
                <Bookmark size={15} aria-hidden="true" />
                <span>Library{savedIds.length ? ` · ${savedIds.length} saved` : ""}</span>
              </button>
            </section>

            {serverDrivenRef.current ? (
              <section className="mobile-menu-section mm-topics">
                <div className="section-label">Your topics</div>
                <button
                  type="button"
                  className="saved-toggle"
                  onClick={() => { setMobileMenuOpen(false); setTopicsOpen(true); }}
                >
                  <SlidersHorizontal size={14} aria-hidden="true" />
                  {Object.keys(topicPrefs).length ? "Topics tuned · edit" : "Make it yours"}
                  {Object.keys(topicPrefs).length ? <span className="count">{Object.keys(topicPrefs).length}</span> : null}
                </button>
              </section>
            ) : (
              <section className="mobile-menu-section mm-topics">
                <div className="section-label">Categories</div>
                <div className="tag-list">
                  {categories.map((tag) => {
                    const hue = tag === "All" ? null : tagHue(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        className={tag === activeTag ? "tag-chip active" : "tag-chip"}
                        onClick={() => { changeTag(tag); setMobileMenuOpen(false); }}
                        style={hue != null ? ({ "--chip-hue": `${hue}` } as React.CSSProperties) : undefined}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="mobile-menu-section mm-comfort">
              <div className="section-label">Reading comfort</div>
              <div className="comfort-row">
                {THEME_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`comfort-swatch theme-${opt.id}${theme === opt.id ? " active" : ""}`}
                    onClick={() => setTheme(opt.id)}
                    aria-pressed={theme === opt.id}
                    title={`${opt.label} theme`}
                  >
                    <span className="comfort-swatch-dot" aria-hidden="true" />
                    {opt.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="comfort-display"
                onClick={() => { setMobileMenuOpen(false); setAaOpen(true); }}
              >
                <span className="cd-aa" aria-hidden="true">Aa</span>
                Display settings
                <span className="cd-hint">{FACES.find((f) => f.id === display.face)?.label ?? "Literata"} · size {display.size}</span>
              </button>
            </section>

            <section className="mobile-menu-section mm-yours">
              {!isNativeApp ? (
                <a className="saved-toggle mm-more" href="/">
                  <Gauge size={14} aria-hidden="true" />
                  Home: around you, and your numbers
                </a>
              ) : null}
              <button
                type="button"
                className={showSavedOnly ? "saved-toggle mm-saved active" : "saved-toggle mm-saved"}
                onClick={() => { toggleSavedView(); setMobileMenuOpen(false); }}
                aria-pressed={showSavedOnly}
              >
                <Bookmark size={14} aria-hidden="true" />
                {showSavedOnly ? "Showing saved" : "Show saved"}
                <span className="count">{savedIds.length}</span>
              </button>
              <button
                type="button"
                className="saved-toggle mm-library"
                onClick={() => { setMobileMenuOpen(false); setLibraryOpen(true); }}
              >
                <Layers size={14} aria-hidden="true" />
                Your library
                {savedIds.length ? <span className="count">{savedIds.length}</span> : null}
              </button>
              <button
                type="button"
                className="saved-toggle mm-personalise"
                onClick={() => { setOnboardingOpen(true); setMobileMenuOpen(false); }}
              >
                <Sparkles size={14} aria-hidden="true" />
                {interests.length ? "Tuned for you · edit" : "Personalise feed"}
                {interests.length ? <span className="count">{interests.length}</span> : null}
              </button>
              <button
                type="button"
                className="saved-toggle mm-birthday"
                onClick={() => { setMobileMenuOpen(false); setBirthdayCelebration(false); setBirthdayOpen(true); }}
              >
                <Sparkles size={14} aria-hidden="true" />
                On your day
              </button>
              <button
                type="button"
                className="saved-toggle mm-week"
                onClick={() => { setMobileMenuOpen(false); setWeekRecapOpen(true); }}
              >
                <Flame size={14} aria-hidden="true" />
                Your week
              </button>
              <button
                type="button"
                className="saved-toggle"
                onClick={() => { setMobileMenuOpen(false); setTourOpen(true); }}
              >
                <Sparkles size={14} aria-hidden="true" />
                How to use InSnip
              </button>
              <button
                type="button"
                className="saved-toggle pro-entry"
                onClick={() => { setMobileMenuOpen(false); setProOpen(true); }}
              >
                <Star size={14} aria-hidden="true" />
                InSnip Pro
              </button>
            </section>

            <section className="mobile-menu-section mobile-menu-list">
              <div className="section-label">Today's snippets</div>
              <div className="card-list" role="list">
                {filteredCards.map((card) => {
                  const isActive = card.id === activeCard.id;
                  const isViewed = viewedIds.includes(card.id);
                  return (
                    <button
                      key={card.id}
                      type="button"
                      role="listitem"
                      className={["list-card", isActive ? "active" : "", !isActive && isViewed ? "viewed" : ""].join(" ").trim()}
                      onClick={() => { visitCard(card.id); setMobileMenuOpen(false); }}
                    >
                      <div className="list-card-top">
                        <span style={{ "--chip-hue": `${tagHue(card.category)}` } as React.CSSProperties}>{card.category}</span>
                        <div className="list-card-icons">
                          {card.isEditorsPick ? <Sparkles size={12} aria-label="Editor's pick" /> : null}
                          {savedIds.includes(card.id) ? <Bookmark size={12} aria-label="Saved" /> : null}
                        </div>
                      </div>
                      <strong>{card.headline}</strong>
                      <small>{cardMeta(card)}</small>
                    </button>
                  );
                })}
              </div>
            </section>

            {sessionApiEnabled() ? (
              <section className="mobile-menu-section account-section">
                <label className="section-label">Account</label>
                {accountCard}
              </section>
            ) : null}

            <section className="mobile-menu-section mobile-subscribe-section">
              <button
                type="button"
                className="mobile-subscribe-cta"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSubscribeStatus("idle");
                  setSubscribeOpen(true);
                  window.setTimeout(() => emailInputRef.current?.focus(), 60);
                }}
              >
                <Mail size={16} aria-hidden="true" />
                <span>Subscribe to the daily drop</span>
              </button>
            </section>
          </div>
        </div>
      ) : null}

      <BottomNav
        mode={barMode}
        active={overviewOpen ? "stories" : insightsOpen ? "insights" : (mobileMenuOpen || proOpen || recallOpen || libraryOpen || showSavedOnly) ? "you" : "feed"}
        onOpenMenu={() => setMenuOpen(true)}
        onCloseMenu={() => setMenuOpen(false)}
        onSelect={selectNavTab}
        onPrev={() => move(-1)}
        onNext={() => move(1)}
        roundLabel={showSavedOnly ? "Saved" : serverDrivenRef.current ? `Round ${dayRound}` : totalRounds > 1 ? `Round ${roundNumber}/${totalRounds}` : "Today"}
        roundNote={(() => {
          if (showSavedOnly) return filteredCards.length ? `${activeIndex + 1} of ${filteredCards.length}` : "Your reading list";
          // While the lesson stands, the count is the one a stranger understands.
          if (lesson >= 0 && lesson < 3) return `Story ${activeIndex + 1} of ${filteredCards.length}`;
          if (activeTag !== "All") return `${activeTag}, ${activeIndex + 1} of ${filteredCards.length}`;
          if (serverDrivenRef.current) {
            const left = Math.max(0, filteredCards.length - (activeIndex + 1));
            if (!hasMoreCards) return left > 0 ? `${left} left today` : "You're up to date";
            if (left === 0) return `Round ${dayRound + 1} unlocked`;
            if (left === 1) return `1 more to Round ${dayRound + 1}`;
            return `${left} more to Round ${dayRound + 1}`;
          }
          return `${activeIndex + 1} of ${filteredCards.length}`;
        })()}
        hook={(() => {
          // The footer names what a swipe gives you: the next headline. The one swipe teacher
          // is the caption on the photograph; this line never repeats it.
          if (showSavedOnly) return undefined;
          const next = filteredCards[activeIndex + 1];
          if (next?.headline) return `Next: ${tidyText(next.headline)}`;
          return hookLine || undefined;
        })()}
        streak={streak.count}
        savedCount={savedIds.length}
        freshCount={freshCount}
        signedIn={!!account?.signedIn}
        accountName={account?.name ?? account?.email ?? undefined}
      />

      {phoneDoorOpen ? (
        <PhoneDoor
          platform={storePlatform}
          cards={filteredCards}
          onSeen={() => sendEvent({ type: "app_door_seen", tag: storePlatform })}
          onTake={takeApp}
          onDecline={declineApp}
        />
      ) : null}

      {roundPromptOpen ? (
        <RoundPrompt
          round={promptedRound}
          storiesRead={currentRoundTarget || READ_TARGET}
          cards={filteredCards}
          platform={storePlatform}
          onSeen={() => sendEvent({ type: "app_round_prompt", tag: `round${promptedRound}` })}
          onTake={(surface) => { setRoundPromptOpen(false); takeApp(surface); }}
          onDismiss={() => setRoundPromptOpen(false)}
        />
      ) : null}

      {/* THE TOUR WAITS FOR THE DOOR.
          Two full-screen overlays cannot both be the first thing a visitor meets, and the
          order matters: the door is a question with two answers, the tour is a lesson about
          a screen. Teaching someone the reader while a modal is still asking whether they
          want the reader at all is how "Welcome to InSnip" ended up floating over a blurred
          card that the reader had not been given yet. */}
      {tourOpen && !tourBlocked ? (
        <FeatureTour
          onDone={finishTour}
          controls={tourControls}
          signedIn={member}
        />
      ) : null}

      {briefingSession ? (
        <AudioBriefing
          title={activeCard.headline}
          folio={(() => {
            const i = filteredCards.findIndex((c) => c.id === activeCard.id);
            return i >= 0 ? `Story ${String(i + 1).padStart(2, "0")} of ${String(filteredCards.length).padStart(2, "0")}` : undefined;
          })()}
          speaking={briefingActive}
          rate={briefingRate}
          onPlayPause={toggleBriefingPlay}
          onPrev={() => move(-1)}
          onNext={() => move(1)}
          onCycleRate={cycleBriefingRate}
          onClose={closeBriefing}
        />
      ) : null}

      {weekRecapOpen ? (
        (() => {
          // v3: the earned reader archetype rides the recap as the second shareable.
          const totals = readTotals();
          const archetype = computeArchetype({
            categoryCounts: categoryReads(),
            cardsRead: Math.max(totals.reads, readStored<string[]>("insnip.seen", []).length),
            streak: streak.count,
            savedCount: savedIds.length,
            reactionsCount: Object.values(reactions).flat().length,
            divesOpened: divesOpened(),
            goodNewsReads: totals.goodNews,
          });
          const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
          return (
            <WeekRecap
              cardsThisWeek={weeklyReadCount()}
              streak={streak.count}
              totalRead={readStored<string[]>("insnip.seen", []).length}
              saved={savedIds.length}
              topics={topTopics(readStored<Record<string, number>>("insnip.topicSignals", {}))}
              archetype={archetype}
              onShare={shareWeek}
              onShareArchetype={
                archetype ? () => void shareArchetypeImage(archetype, monthLabel, referralId() ?? "") : undefined
              }
              onClose={() => setWeekRecapOpen(false)}
            />
          );
        })()
      ) : null}

      {insightsOpen ? (
        <div className="drawer-scrim insights-scrim" onClick={() => setInsightsOpen(false)} role="dialog" aria-modal="true" aria-label="Your insights">
          <section className="insights-panel" onClick={(e) => e.stopPropagation()}>
            <header className="insights-head">
              <div>
                <span className="insights-eyebrow">
                  {(() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; })()}
                  {account?.signedIn && account.name ? `, ${account.name.split(" ")[0]}` : ""}
                </span>
                <h2>Insights</h2>
              </div>
              <button type="button" className="icon-button ghost insights-close" onClick={() => setInsightsOpen(false)} aria-label="Close insights">
                <X size={18} aria-hidden="true" />
              </button>
            </header>

            {/* Good News, promoted. It used to be a tile wedged into the reader's Up next
                strip, where it competed for a row that is only ever about what to read next.
                Insights is where a reader comes for the shape of their day, so the bright
                side opens it. Same overlay, same handler, nothing new behind it. */}
            <div className="insights-good">
              <span className="insights-label">The bright side</span>
              <button
                type="button"
                className="goodnews-tile insights-goodnews"
                onClick={() => { setInsightsOpen(false); setGoodNewsOpen(true); }}
              >
                <span className="goodnews-tile-sun" aria-hidden="true"><Sun size={22} /></span>
                <span className="goodnews-tile-copy">
                  <strong className="goodnews-tile-title">Good News</strong>
                  <span className="goodnews-tile-sub">
                    {goodNewsCards.length
                      ? `${goodNewsToday ? `${goodNewsToday} good ${goodNewsToday === 1 ? "story" : "stories"} today` : `${goodNewsCards.length} good ${goodNewsCards.length === 1 ? "story" : "stories"} waiting`}${goodNewsCards[0] ? `, starting with "${goodNewsCards[0].headline}"` : ""}`
                      : "The day's brighter stories, gathered in one place."}
                  </span>
                </span>
                <ChevronRight size={18} aria-hidden="true" className="goodnews-tile-caret" />
              </button>
            </div>

            <div className="insights-hero" aria-label="Today at a glance">
              {(() => {
                const total = Math.max(1, filteredCards.length || dailyStats.stories);
                const frac = Math.min(1, readToday / total);
                return (
                  <div className="ins-ring" role="img" aria-label={`${readToday} of ${total} stories read today`}>
                    <svg viewBox="0 0 84 84" aria-hidden="true">
                      <circle className="ins-ring-track" cx="42" cy="42" r="36" />
                      <circle className="ins-ring-fill" cx="42" cy="42" r="36" strokeDasharray={`${(frac * 226.2).toFixed(1)} 226.2`} />
                    </svg>
                    <span className="ins-ring-center">
                      <strong><span className="num-roll">{animReadToday}</span></strong>
                      <em>of {total}</em>
                    </span>
                  </div>
                );
              })()}
              <div className="ins-stats">
                <div className="ins-stat">
                  <strong>{dailyStats.minutes}<em>m</em></strong>
                  <span>of pure signal</span>
                </div>
                <div className="ins-stat">
                  <strong>{dailyStats.sources}</strong>
                  <span>sources checked</span>
                </div>
                <div className="ins-stat flame">
                  <strong><Flame size={16} aria-hidden="true" />{streak.count}</strong>
                  <span>day streak</span>
                </div>
              </div>
            </div>

            {/* Your recap: the cumulative story (all the stories you have finished, the
                reading time you have won back, your lean) folded in from the old standalone
                recap modal, so Insights holds today AND the bigger picture in one place. */}
            {(() => {
              const recap = computeRecap({
                seenIds: seenRef.current,
                cards,
                streak: streak.count,
                reactions,
                savedCount: savedIds.length,
              });
              const shareRecap = () => {
                const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
                const lines = [
                  "📊 My InSnip recap",
                  `🔥 ${recap.streak}-day streak`,
                  `📖 ${recap.cardsRead} cards read, ~${recap.minutesSaved} min saved vs the full articles`,
                  recap.topCategory ? `📚 Top topic: ${recap.topCategory}` : "",
                  `The news you can finish. ${origin}`,
                ].filter(Boolean).join("\n");
                if (typeof navigator !== "undefined" && navigator.share) {
                  void navigator.share({ title: "My InSnip recap", text: lines }).catch(() => {});
                } else {
                  void navigator.clipboard?.writeText(lines);
                }
                sendEvent({ type: "share", tag: "recap" });
              };
              return (
                <div className="insights-recap">
                  <div className="insights-recap-head">
                    <span className="insights-label">Your recap</span>
                    <button type="button" className="recap-share-btn" onClick={shareRecap} aria-label="Share your recap">
                      <Share2 size={13} aria-hidden="true" /> Share
                    </button>
                  </div>
                  <p className="recap-lede">
                    {recap.cardsRead > 0 ? (
                      <>You have finished <strong>{recap.cardsRead}</strong> stories, about <strong>~{recap.minutesSaved} min</strong> shorter than the full articles{recap.topCategory ? (<>, leaning into <strong>{recap.topCategory}</strong></>) : null}.</>
                    ) : (
                      <>Read your first story and your recap starts filling in.</>
                    )}
                  </p>
                  {/* Every number says its WINDOW and its BASIS. The ring above counts today;
                      these count all time; "time saved" is vs reading the full articles. The
                      audit caught "1 of 10" and "0 stories read" on one panel, same words,
                      different windows, reading as a contradiction. */}
                  <div className="recap-metrics">
                    <div className="recap-metric"><strong>{recap.cardsRead}</strong><small>stories, all time</small></div>
                    <div className="recap-metric"><strong>~{recap.minutesSaved}<em>m</em></strong><small>vs full articles</small></div>
                    <div className="recap-metric"><strong>{recap.reactions}</strong><small>reactions</small></div>
                    <div className="recap-metric"><strong>{recap.saved}</strong><small>saved</small></div>
                  </div>
                </div>
              );
            })()}

            <div className="insights-dna">
              <span className="insights-label">Your reading DNA</span>
              {readingDna.length ? (
                <div className="dna-rows">
                  {readingDna.map((d) => (
                    <div key={d.category} className="dna-row" style={{ ["--hue"]: `${tagHue(d.category)}` } as React.CSSProperties}>
                      <span className="dna-cat">{d.category}</span>
                      <span className="dna-track"><span className="dna-fill" style={{ width: `${d.pct}%` }} /></span>
                      <span className="dna-n">{d.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="dna-empty">Read your first story and the shape of your day appears here.</p>
              )}
            </div>

            {/* v3: personalization the reader can SEE (the invisible affinity re-rank
                stays, but the hub now says what it has learned and offers the shortcut). */}
            {(() => {
              const top = readingDna[0];
              if (!top || top.count < 3) return null;
              return (
                <div className="insights-beat">
                  <span className="insights-label">Your beat</span>
                  <p className="beat-line">
                    You keep coming back to <strong>{top.category}</strong>, {top.count} stories and counting.
                  </p>
                  <button
                    type="button"
                    className="beat-pin"
                    onClick={() => { setInsightsOpen(false); changeTag(top.category); }}
                  >
                    Read today's {top.category} first <ArrowRight size={12} aria-hidden="true" />
                  </button>
                </div>
              );
            })()}

            {/* The ritual, drawn: a 14-day reading calendar (from the local readDays log) and
                the reader's best hour (from the readHours histogram). Both are quiet facts. */}
            {(() => {
              const daySet = new Set(readStored<string[]>("insnip.readDays", []));
              const dots = Array.from({ length: 14 }, (_, i) => {
                const d = new Date(Date.now() - (13 - i) * 86_400_000);
                const key = d.toLocaleDateString("en-CA");
                return { key, on: daySet.has(key), today: i === 13 };
              });
              const litDays = dots.filter((d) => d.on).length;
              const hours = readStored<number[]>("insnip.readHours", []);
              let bestHour = -1;
              let bestN = 0;
              hours.slice(0, 24).forEach((n, h) => {
                const v = Number(n) || 0;
                if (v > bestN) { bestN = v; bestHour = h; }
              });
              const hourLabel = (h: number) =>
                h === 0 ? "midnight" : h === 12 ? "midday" : h < 12 ? `${h}am` : `${h - 12}pm`;
              return (
                <div className="insights-ritual">
                  <span className="insights-label">Your ritual</span>
                  <div
                    className="ritual-cal"
                    role="img"
                    aria-label={`Reading days, last two weeks: ${litDays} of 14`}
                  >
                    {dots.map((d) => (
                      <i key={d.key} data-on={d.on ? "1" : "0"} data-today={d.today ? "1" : "0"} title={d.key} />
                    ))}
                  </div>
                  {bestHour >= 0 && bestN > 0 ? (
                    <p className="best-hour">You read best around {hourLabel(bestHour)}.</p>
                  ) : null}
                </div>
              );
            })()}

            <div className="insights-doors">
              <span className="insights-label">Today's rituals</span>
              <div className="doors-grid">
                {briefs.length ? (
                  <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); openBrief(); }}>
                    <span className="door-ico brief"><Sparkles size={16} aria-hidden="true" /></span>
                    <span className="door-copy"><strong>The Brief</strong><small>The day in a minute</small></span>
                  </button>
                ) : null}
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); setGameOpen(true); }}>
                  <span className="door-ico game"><Zap size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Signal or Noise</strong><small>Can you tell what's real?</small></span>
                </button>
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); void openQuiz(); }}>
                  <span className="door-ico quiz"><Brain size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Today's Quiz</strong><small>Five questions on the day</small></span>
                </button>
                {/* Good News is no longer one door among the rest: it leads this panel now. */}
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); openCalm(); }}>
                  <span className="door-ico calm"><Coffee size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Catch me up</strong><small>Five calm stories, then done</small></span>
                </button>
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); setDietOpen(true); }}>
                  <span className="door-ico diet"><PieChart size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Signal diet</strong><small>Where your attention goes</small></span>
                </button>
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); setRecallOpen(true); }}>
                  <span className="door-ico recall"><Lightbulb size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Recall</strong><small>Keep what you learned</small></span>
                </button>
                <button type="button" className="ins-door" onClick={() => { setInsightsOpen(false); setWeekRecapOpen(true); }}>
                  <span className="door-ico week"><Layers size={16} aria-hidden="true" /></span>
                  <span className="door-copy"><strong>Week recap</strong><small>Your seven day signal</small></span>
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {birthdayOpen && !tourOpen && !onboardingOpen ? (
        <BirthdayPanel
          dob={dob}
          name={readStored<{ firstName?: string }>("insnip.profile", {}).firstName || undefined}
          celebration={birthdayCelebration}
          onClose={() => setBirthdayOpen(false)}
          onAddBirthday={() => { setBirthdayOpen(false); setOnboardEntry("profile"); setOnboardingOpen(true); }}
        />
      ) : null}

      {onboardingOpen ? (
        <Onboarding
          options={ONBOARD_OPTIONS}
          initialPicks={interests}
          entry={onboardEntry}
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      ) : null}

      {/* v3: the signal-diet wheel and the calm catch-up ritual (Insights doors). */}
      {dietOpen ? (
        <SignalDietPanel
          counts={categoryReads()}
          onClose={() => setDietOpen(false)}
          onReadBlindspot={(category) => {
            const target =
              cards.find((c) => String(c.category) === category && !seenRef.current.has(c.id)) ??
              cards.find((c) => String(c.category) === category);
            if (!target) return false;
            setDietOpen(false);
            visitCard(target.id);
            return true;
          }}
        />
      ) : null}
      {calmOpen ? (
        <CatchMeUp
          cards={calmCards}
          onClose={() => setCalmOpen(false)}
          onOpenFull={(id) => {
            setCalmOpen(false);
            visitCard(id);
          }}
        />
      ) : null}

      {/* THE PHONE DOCK (PhoneDock.tsx): the home's bar, on the deck and on the Edition too.
          News and Edition are served in place; Home and the tool are plain links. */}
      {phoneWeb ? (
        <PhoneDock
          here={deskOpen ? "edition" : "news"}
          favs={readFavs()}
          onTab={(room) => {
            if (room === "edition") { setDeskOpen(true); return true; }
            if (room === "news") { if (deskOpen) closeDesk(); else if (showSavedOnly) goHome(); return true; }
            return false;
          }}
          onMore={() => setToolsMenuOpen(true)}
        />
      ) : null}
      {phoneWeb && toolsMenuOpen ? (
        <Suspense fallback={null}><ReaderDrawer onClose={() => setToolsMenuOpen(false)} /></Suspense>
      ) : null}

      {/* The Desk: today's edition as a designed front page (auto-opens once per day;
          the game and Good News overlays stack above it, so their cells open in place). */}
      {deskOpen && !onboardingOpen && !tourOpen && filteredCards.length > 0 ? (
        <EditionDesk
          cards={filteredCards}
          paper={deskPaper}
          paperState={deskPaperState}
          readIds={deskReadIds}
          goodNewsCount={goodNewsCards.length}
          gamePlayed={!!gameResult}
          gameAvailable={!!gameToday}
          quizAvailable={filteredCards.filter((c) => (deepDiveFor(c)?.keyFacts?.length ?? 0) > 0).length >= 3}
          joined={subscribeStatus === "saved"}
          onOpenStory={(id, chain) => { closeDesk(); openFromPaper(id, chain, false); }}
          fresh={deskFresh}
          onRefresh={() => void setPaper("again")}
          pushState={pushState}
          onEnablePush={() => void enablePush()}
          frontPageFirst={frontPageFirst}
          onToggleFrontPageFirst={toggleFrontPageFirst}
          onShareImage={(paperCards, title) => shareFrontPageImage(paperCards, paperStats(paperCards), title)}
          onPlayGame={() => setGameOpen(true)}
          onPlayQuiz={() => { closeDesk(); void openQuiz(); }}
          onListen={(id, chain) => {
            // "The paper, read to you": the briefing starts from the first unread story on
            // the page and turns the pages (the push-tap door starts it once the story leads).
            closeDesk();
            openFromPaper(id, chain, true);
          }}
          onGoodNews={() => setGoodNewsOpen(true)}
          onJoin={deskJoin}
          onShare={(paperCards, title) => void shareEdition(paperCards, title)}
          onClose={closeDesk}
        />
      ) : null}

      {showInsight ? (() => {
        // The round report: what you read, what it was made of, what it earned you.
        const byId = new Map(cards.map((c) => [c.id, c]));
        const fieldCounts = new Map<string, number>();
        for (const id of roundViewedIds) {
          const cat = byId.get(id)?.category;
          if (cat) fieldCounts.set(cat, (fieldCounts.get(cat) ?? 0) + 1);
        }
        const roundFields = [...fieldCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
        // The ceremony tells the truth in receipts: how many SOURCES stood behind what was
        // just read. "~30 min saved" from ten sub-minute reads was the audit's inflated
        // claim sitting beside honest numbers; sources-read-for-you is bigger, true, and
        // exactly the brand.
        // Distinct publications behind the round, not a tally of every citation.
        const roundSources = new Set(
          filteredCards.filter((c) => viewedIds.includes(c.id)).flatMap((c) => [domainOf(c.primarySourceUrl), ...(c.supportingSources ?? []).map((s) => domainOf(s.url))]).filter(Boolean),
        ).size;
        const roundRead = Math.min(progress, READ_TARGET);
        const reactionCount = Object.values(reactions).flat().length;
        const clientNextStart = serverDrivenRef.current
          ? null
          : nextRoundStartIndex(activeIndex, filteredCards.length, READ_TARGET);
        const upNext = clientNextStart != null ? filteredCards.slice(clientNextStart, clientNextStart + 2) : [];
        return (
        <section className="insight-modal" role="dialog" aria-modal="true" aria-label="Reading insight">
          <div className="round-shimmer" aria-hidden="true" />
          <div className="insight-card round-report">
            <span>{serverDrivenRef.current ? `Round ${dayRound} read` : "Ten stories read"}</span>
            <h2>{`${roundRead} ${roundRead === 1 ? "story" : "stories"} from ${roundSources} ${roundSources === 1 ? "source" : "sources"}, ${minutesWord(roundRead)}.`}</h2>
            {/* The reader's archetype, as one quiet line under the facts. */}
            <p className="insight-pattern"><strong>{pattern.title}.</strong> {pattern.body}</p>
            <div className="insight-stats round-ledger">
              <div><strong>{progress}</strong><small>cards read</small></div>
              <div><strong>{roundSources}</strong><small>sources behind them</small></div>
              <div><strong>{streak.count}</strong><small>day streak</small></div>
              <div><strong>{fieldCounts.size}</strong><small>fields</small></div>
            </div>
            {roundFields.length ? (
              <div className="insight-fields" aria-label="Fields this round">
                {roundFields.map(([cat, n]) => (
                  <span key={cat} className="insight-field" style={{ "--chip-hue": `${tagHue(cat)}` } as React.CSSProperties}>
                    {cat}
                    {n > 1 ? <em>×{n}</em> : null}
                  </span>
                ))}
              </div>
            ) : null}
            {reactionCount > 0 || savedIds.length > 0 ? (
              <p className="insight-trace">
                {reactionCount > 0 ? `${reactionCount} reaction${reactionCount === 1 ? "" : "s"}` : null}
                {reactionCount > 0 && savedIds.length > 0 ? ", " : null}
                {savedIds.length > 0 ? `${savedIds.length} saved for later` : null}
              </p>
            ) : null}
            {(() => {
              // The open loop that brings the reader back tomorrow: their unresolved calls.
              const calls = viewedIds.filter((id) => stances[id]?.mine && !stances[id]?.verdict).length;
              return calls > 0 ? (
                <p className="insight-trace call-tease">
                  {calls} call{calls === 1 ? "" : "s"} in. Verdict{calls === 1 ? "" : "s"} tomorrow morning.
                </p>
              ) : null;
            })()}
            {serverDrivenRef.current ? (
              dayRound * READ_TARGET >= dailyGoal ? (
                <div className="daily-goal met">Daily goal met: {dayRound * READ_TARGET} stories today</div>
              ) : (
                <div className="daily-goal">
                  <span className="daily-goal-track">
                    <span
                      className="daily-goal-fill"
                      style={{ width: `${Math.min(100, ((dayRound * READ_TARGET) / dailyGoal) * 100)}%` }}
                    />
                  </span>
                  {dayRound * READ_TARGET} of {dailyGoal} stories today
                </div>
              )
            ) : null}
            {upNext.length ? (
              <div className="insight-next" aria-label="Up next">
                <span className="insight-next-label">Up next</span>
                {upNext.map((c) => (
                  <span key={c.id} className="insight-next-line">{c.headline}</span>
                ))}
              </div>
            ) : null}
            <div className="insight-actions">
              {serverDrivenRef.current ? (
                hasMoreCards
                  ? <button type="button" className="round-go" onClick={startNextRound}>Keep going, Round {dayRound + 1}</button>
                  : <button type="button" className="round-go" onClick={() => void showCaughtUp()}>See your day</button>
              ) : nextRoundStartIndex(activeIndex, filteredCards.length, READ_TARGET) != null ? (
                <button type="button" className="round-go" onClick={startNextRound}>Next 10 cards</button>
              ) : (
                <button type="button" className="round-go" onClick={() => { setShowInsight(false); setRecapOpen(true); }}>
                  See your recap
                </button>
              )}
              {pushState === "off" || pushState === "busy" ? (
                <button
                  type="button"
                  className="ghost-action"
                  disabled={pushState === "busy"}
                  onClick={enablePush}
                  title="Get a nudge when the next drop lands. Keep your streak alive"
                >
                  <Bell size={14} aria-hidden="true" /> {pushState === "busy" ? "Turning on…" : "Keep my streak"}
                </button>
              ) : (
                /* One secondary action: the streak nudge where it is still to be turned on,
                   else the share. Email and the rest live in the You menu. */
                <button type="button" className="ghost-action" onClick={() => void shareEdition()} title="Share today's front page as text">
                  <Share2 size={14} aria-hidden="true" /> Share today's front page
                </button>
              )}
            </div>
          </div>
        </section>
        );
      })() : null}

      {recapOpen ? (() => {
        const recap = computeRecap({
          seenIds: seenRef.current,
          cards,
          streak: streak.count,
          reactions,
          savedCount: savedIds.length,
        });
        const topReaction = REACTIONS.find((r) => r.id === recap.topReaction);
        return (
          <section className="insight-modal" role="dialog" aria-modal="true" aria-label="Your InSnip recap" onClick={() => setRecapOpen(false)}>
            <div className="insight-card recap-card" onClick={(e) => e.stopPropagation()}>
              <div className="insight-burst" aria-hidden="true">📊</div>
              <span>Your InSnip recap</span>
              <h2>{recap.cardsRead} cards, ~{recap.minutesSaved} min saved vs the full articles</h2>
              <p>You're on a {recap.streak}-day streak{recap.topCategory ? ` and you lean into ${recap.topCategory}` : ""}.</p>
              <div className="insight-stats">
                <div><strong>{recap.cardsRead}</strong><small>cards read</small></div>
                <div><strong>{recap.reactions}</strong><small>reactions</small></div>
                <div><strong>{recap.saved}</strong><small>saved</small></div>
              </div>
              <div className="recap-rows">
                <div className="recap-row"><span>🔥 Streak</span><strong>{recap.streak} day{recap.streak === 1 ? "" : "s"}</strong></div>
                {recap.topCategory ? <div className="recap-row"><span>📚 Top topic</span><strong>{recap.topCategory}</strong></div> : null}
                {topReaction ? <div className="recap-row"><span>{topReaction.emoji} Go-to reaction</span><strong>{topReaction.label}</strong></div> : null}
                <div className="recap-row"><span>⏱️ Vs the full articles</span><strong>~{recap.minutesSaved} min</strong></div>
              </div>
              <div className="insight-actions">
                <button type="button" onClick={() => setRecapOpen(false)}>Keep reading</button>
                {pushState === "off" || pushState === "busy" ? (
                  <button
                    type="button"
                    className="ghost-action"
                    disabled={pushState === "busy"}
                    onClick={enablePush}
                    title="Get a nudge when the next drop lands. Keep your streak alive"
                  >
                    <Bell size={14} aria-hidden="true" /> {pushState === "busy" ? "Turning on…" : "Keep my streak"}
                  </button>
                ) : pushState === "on" ? (
                  <button type="button" className="ghost-action" disabled>
                    <Bell size={14} aria-hidden="true" /> Daily drop on
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ghost-action"
                  onClick={() => {
                    const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
                    const lines = [
                      "📊 My InSnip recap",
                      `🔥 ${recap.streak}-day streak`,
                      `📖 ${recap.cardsRead} cards read · ~${recap.minutesSaved} min saved vs the full articles`,
                      recap.topCategory ? `📚 Top topic: ${recap.topCategory}` : "",
                      `The news you can finish. ${origin}`,
                    ].filter(Boolean).join("\n");
                    if (navigator.share) void navigator.share({ title: "My InSnip recap", text: lines }).catch(() => {});
                    else void navigator.clipboard?.writeText(lines);
                  }}
                >
                  <Share2 size={14} aria-hidden="true" /> Share recap
                </button>
              </div>
            </div>
          </section>
        );
      })() : null}

      {caughtUp ? (() => {
        const recap = computeRecap({
          seenIds: seenRef.current,
          cards,
          streak: streak.count,
          reactions,
          savedCount: savedIds.length,
        });
        return (
          <section className="insight-modal" role="dialog" aria-modal="true" aria-label="All caught up">
            <div className="insight-card recap-card">
              <div className="insight-burst" aria-hidden="true">🎉</div>
              <span>That's the drop</span>
              <h2>You're all caught up</h2>
              <p>You read <strong>{insights?.cardsRead ?? recap.cardsRead}</strong> cards across <strong>{dayRound}</strong> round{dayRound === 1 ? "" : "s"} today. Fresh stories land through the day, so check back soon.</p>
              <div className="recap-rows">
                <div className="recap-row"><span>📅 Rounds today</span><strong>{dayRound}</strong></div>
                <div className="recap-row"><span>📖 Cards read</span><strong>{insights?.cardsRead ?? recap.cardsRead}</strong></div>
                {(insights?.topCategory ?? recap.topCategory) ? <div className="recap-row"><span>📚 Most read</span><strong>{insights?.topCategory ?? recap.topCategory}</strong></div> : null}
                <div className="recap-row"><span>⏱️ Time saved</span><strong>~{(insights?.cardsRead ?? recap.cardsRead) * 3} min</strong></div>
              </div>
              {/* The satisfied moment asks: one field, first party, right where the day ends. */}
              {subscribeStatus === "saved" ? (
                <p className="caughtup-join-done"><Check size={13} aria-hidden="true" /> Tomorrow's edition, 7am.</p>
              ) : (
                <form className="caughtup-join" onSubmit={submitSubscribe}>
                  <p>Done for today. Get tomorrow's edition by email.</p>
                  <div className="caughtup-join-row">
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      aria-label="Email address"
                      aria-invalid={subscribeStatus === "invalid"}
                      value={emailDraft}
                      onChange={(e) => { setEmailDraft(e.target.value); if (subscribeStatus === "invalid" || subscribeStatus === "error") setSubscribeStatus("idle"); }}
                    />
                    <button type="submit" disabled={subscribeStatus === "saving"}>{subscribeStatus === "saving" ? "Sending…" : "Send it"}</button>
                  </div>
                  {subscribeStatus === "invalid" ? <small className="form-error">Please enter a valid email.</small> : null}
                  {subscribeStatus === "error" ? <small className="form-error">Couldn't save that just now. Try again in a moment.</small> : null}
                </form>
              )}
              <div className="insight-actions">
                <button type="button" className="share-day-btn" onClick={() => void shareDay()}>
                  {imageShareState === "busy"
                    ? "Preparing…"
                    : imageShareState === "done"
                      ? "Shared ✓"
                      : "📸 Share my day"}
                </button>
                <button type="button" onClick={() => { setCaughtUp(false); void openQuiz(); }}>🧠 Take today's quiz</button>
                {phoneWeb ? null : <button type="button" onClick={() => { setCaughtUp(false); openSearch(); }}>Search older stories</button>}
                <button type="button" className="ghost-action" onClick={() => setCaughtUp(false)}>Done for now</button>
              </div>
            </div>
          </section>
        );
      })() : null}

      {quizOpen && quizCard ? (
        <QuizModal quiz={quizCard} onClose={() => setQuizOpen(false)} onShare={shareQuizScore} />
      ) : null}

      {gameOpen ? (
        <SignalOrNoiseGame
          open
          onClose={() => setGameOpen(false)}
          today={gameToday}
          onComplete={(r) => {
            setGameResult(r);
            try { localStorage.setItem(`insnip.son.${r.day}`, JSON.stringify(r)); } catch { /* ignore */ }
          }}
        />
      ) : null}

      {shareCard ? (
        <ShareSheet
          card={shareCard}
          onClose={() => setShareCard(null)}
          onShareImage={() => { const c = shareCard; setShareCard(null); void shareCardAsImage(c); }}
          onShared={() => logEngagement("share", shareCard.id)}
        />
      ) : null}

      {recallOpen ? (
        <RecallModal
          deck={recallDeck}
          onClose={() => setRecallOpen(false)}
          onOpenCard={(rc) => { setRecallOpen(false); setSearchDetail(rc); setSearchOpen(true); }}
        />
      ) : null}

      {libraryOpen ? (
        <LibraryModal
          cards={cards}
          savedIds={savedIds}
          onOpenCard={(c) => { setLibraryOpen(false); setSearchDetail(c); setSearchOpen(true); }}
          onStartRecall={() => { setLibraryOpen(false); setRecallOpen(true); }}
          onUnsave={(id) => toggleSave(id)}
          onClose={() => setLibraryOpen(false)}
        />
      ) : null}

      {proOpen ? (
        <ProModal
          onInterest={() => { try { localStorage.setItem("insnip.proInterest", "1"); } catch { /* ignore */ } }}
          onClose={() => setProOpen(false)}
        />
      ) : null}

      {tipOpen ? (() => {
        const tip = tipForHour();
        return (
          <section className="insight-modal" role="dialog" aria-modal="true" aria-label="Reading tip" onClick={() => setTipOpen(false)}>
            <div className="tip-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tip-modal-icon" aria-hidden="true">{tip.icon}</div>
              <span className="tip-modal-label">Tip of the hour</span>
              <p>{tip.text}</p>
              <button type="button" onClick={() => setTipOpen(false)}>Got it</button>
            </div>
          </section>
        );
      })() : null}

      {member && askOpen && intelCard ? (
        <section className={`insight-modal${searchDetail ? " over-search" : ""}`} role="dialog" aria-modal="true" aria-label="Ask about this card" onClick={closeAsk}>
          <div className="ask-modal" onClick={(e) => e.stopPropagation()}>
            <header className="ask-header">
              <div className="ask-head-main">
                {intelCard.imageUrl ? (
                  <img className="ask-thumb" src={resolveImageUrl(intelCard.imageUrl)} alt="" aria-hidden="true" loading="lazy" />
                ) : null}
                <div className="ask-head-text">
                  <span className="ask-eyebrow"><Sparkles size={12} aria-hidden="true" /> Ask about this</span>
                  <h2 className="ask-headline">{intelCard.headline}</h2>
                </div>
              </div>
              <button type="button" className="icon-button ghost" onClick={closeAsk} aria-label="Close ask"><X size={18} /></button>
            </header>

            <div className="ask-thread" role="log" aria-live="polite">
              {askThread.length === 0 && !askBusy ? (
                <div className="ask-empty">
                  <div className="ask-empty-orb" aria-hidden="true"><MessageCircle size={22} /></div>
                  <p className="ask-empty-title">Curious about this story?</p>
                  <p className="ask-hint">Tap a question below or ask your own, answers use only this card's sources.</p>
                </div>
              ) : null}
              {askThread.map((entry, i) => (
                <div key={i} className="ask-qa">
                  <p className="ask-q"><MessageCircle size={13} aria-hidden="true" /> {entry.q}</p>
                  <p className="ask-a">{entry.a}</p>
                  {/* The answer names its evidence. Old workers send no sources; those answers
                      render exactly as before, chips only ever add. */}
                  {entry.sources?.length ? (
                    <span className="ask-grounded">
                      {entry.sources.slice(0, 4).map((s, j) => {
                        const href = httpHref(s.url);
                        return href ? (
                          <a key={j} className="ask-grounded-chip" href={href} target="_blank" rel="noopener noreferrer">
                            Grounded on: {s.name}
                          </a>
                        ) : (
                          <span key={j} className="ask-grounded-chip">Grounded on: {s.name}</span>
                        );
                      })}
                    </span>
                  ) : null}
                </div>
              ))}
              {askBusy ? (
                <div className="ask-loading" aria-label="Thinking">
                  <span /><span /><span />
                </div>
              ) : null}
              {askError ? <p className="ask-error">{askError}</p> : null}
            </div>

            <div className="ask-chips">
              {[
                "Explain it simply",
                "Why does this matter?",
                "What's the background?",
                "What happens next?",
                "Key facts & numbers",
                "Who's affected?",
              ].map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className="ask-chip"
                  onClick={() => void askQuestion(chip)}
                  disabled={askBusy}
                >
                  {chip}
                </button>
              ))}
            </div>

            <form
              className="ask-form"
              onSubmit={(e) => { e.preventDefault(); void askQuestion(askInput); }}
            >
              <input
                type="text"
                className="ask-input"
                placeholder="Ask your own question…"
                value={askInput}
                maxLength={300}
                onChange={(e) => setAskInput(e.target.value)}
                disabled={askBusy}
                aria-label="Your question about this card"
              />
              <button type="submit" className="ask-send" disabled={askBusy || !askInput.trim()} aria-label="Send question">
                <Sparkles size={16} aria-hidden="true" />
              </button>
            </form>
          </div>
        </section>
      ) : null}

      {/* Quote share pill: floats above a text selection in the card copy. JS owns the
          fixed position (clamped to the viewport); onMouseDown preventDefault keeps the
          selection alive through the click. */}
      {quotePill && activeCard ? (
        <div
          className="quote-pill"
          style={{ position: "fixed", left: quotePill.x, top: quotePill.y, transform: "translate(-50%, -100%)", zIndex: 80 }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const text = quotePill.text;
              setQuotePill(null);
              try { window.getSelection?.()?.removeAllRanges(); } catch { /* ignore */ }
              haptic(8);
              void shareQuoteImage(activeCard, text);
              sendEvent({ type: "share", cardId: activeCard.id, tag: "quote" });
            }}
          >
            <Quote size={13} aria-hidden="true" /> Share quote
          </button>
        </div>
      ) : null}
    </main>
  );
}
